import { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'es' | 'de' | 'fr' | 'zh' | 'ja' | 'ar' | 'vi' | 'ko' | 'pt' | 'fa' | 'tr' | 'he';

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Common
    bookSlot: "Book Slot",
    cancel: "Cancel",
    confirmBooking: "Confirm Booking",
    yourName: "Your Name",
    email: "Email",
    phone: "Phone",
    signUp: "Sign Up",
    full: "Full",
    teacher: "Teacher",
    location: "Location",
    backToSchool: "Back to School Conferences",
    required: "(Required)",
    optional: "(Optional)",
    notifications: "Notifications",
    
    // School Page
    selectTeachers: "Select Teachers",
    searchTeachers: "Search teachers...",
    sortBy: "Sort by",
    lastName: "Last Name",
    grade: "Grade",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Teacher Selected",
    teachersSelected: "Teachers Selected",
    findTimes: "Find Times",
    clear: "Clear",
    select: "Select",
    selected: "Selected",
    add: "Add",
    added: "Added",
    availableSlots: "available slots",
    teamOf: "Team of",
    selectAllTeam: "Select all teachers in this team?",
    yes: "Yes",
    no: "No",
    allTeachers: "All Teachers",
    showAllTeachers: "Show all teachers",
    chooseTimes: "Choose Times",
    pickAvailableSlots: "Pick available slots",
    confirm: "Confirm",
    confirmMeetings: "Confirm Meetings",
    getConfirmation: "Get confirmation",
    
    // Booking Limit
    bookingLimitTitle: "Booking Limit",
    maxSlotsAllowed: "Maximum",
    slotsPerParent: "conferences per parent for this event.",
    enterEmailToSeeCount: "Enter your email during registration to see your current bookings.",
    youHaveBooked: "You have booked",
    of: "of",
    allowedSlots: "allowed conference slots",
    toBookMore: "To book more, please cancel an existing appointment first.",
    youCanBook: "You can book",
    moreSlots: "more slot(s)",
    viewMySchedule: "View & Manage My Schedule →",
    
    // Plan Page
    planYourSchedule: "Plan Your Schedule",
    selectTimeSlots: "Select time slots for each teacher",
    autoSuggest: "Auto-Suggest",
    autoSuggestDesc: "Let us find the best times for you",
    confirmAppointments: "Confirm Appointments",
    appointment: "Appointment",
    appointments: "Appointments",
    noTimeSelected: "No time selected",
    childsName: "Child's Name",
    enterChildName: "Enter your child's name",
    yourDetails: "Your Details",
    parentName: "Parent Name",
    yourFullName: "Your full name",
    yourEmail: "your.email@example.com",
    yourPhone: "Your phone number",
    review: "Review",
    back: "Back",
    nextTeacher: "Next Teacher",
    yourSchedule: "Your Schedule",
    tapToChange: "Tap to change",
    confirmNow: "Confirm Now",
    confirming: "Confirming...",
    
    // Booking Form
    bookingFor: "Booking for",
    selectSlot: "Select a time slot",
    joinWaitlist: "Join Waitlist",
    onWaitlist: "On Waitlist",
    booked: "Booked",
    waitlisted: "Waitlisted",
    notifyMe: "Notify me by:",
    remindMeVia: "Remind me via",
    emailNotify: "Email",
    smsNotify: "SMS",
    bothNotify: "Both",
    
    // Confirmation
    bookingConfirmed: "Booking Confirmed!",
    addedToWaitlist: "Added to Waitlist",
    confirmationSent: "A confirmation has been sent to your email.",
    waitlistInfo: "We'll notify you if a spot opens up.",
    viewSchedule: "View My Schedule",
    bookAnother: "Book Another",
    
    // Schedule Lookup
    mySchedule: "My Schedule",
    enterEmail: "Enter your email to view your appointments",
    lookupSchedule: "Look Up Schedule",
    noAppointments: "No appointments found",
    tryDifferentEmail: "Try a different email address",
    
    // Dates & Times
    today: "Today",
    tomorrow: "Tomorrow",
    am: "AM",
    pm: "PM",
    
    // Errors
    errorOccurred: "An error occurred",
    pleaseRetry: "Please try again",
    requiredField: "This field is required",
    invalidEmail: "Please enter a valid email",
    
    // Registration locked
    registrationOpens: "Registration Opens",
    registrationClosed: "Registration has closed",
    comingSoon: "Coming Soon",
    
    // Additional UI elements
    teacherPortal: "Teacher Portal",
    schoolConferences: "School Conferences",
    needToMakeChanges: "Need to make changes to your schedule?",
    nameAZ: "Name (A-Z)",
    nameZA: "Name (Z-A)",
    gradeAsc: "Grade (Asc)",
    gradeDesc: "Grade (Desc)",
    recommendedOrder: "Recommended",
    teamsFilterLabel: "Teams",
    otherTeachersLabel: "Other teachers",
    noConferencesFound: "No conferences found matching",
    generalEvents: "General Events",
    generalEvent: "General Event",
    general: "General",
    signup: "Sign-up",
    appointmentsLabel: "Appointments",
    virtual: "Virtual",
    groupedTeachersTooltip: "Grouped teachers - selecting one selects all in group",
    teachersAvailable: "teachers available",
    clickHere: "Click Here",
    inCategory: "in",
    noTeachersInCategory: "No teachers found in",
    
    // Plan page additions
    bookingConfirmedTitle: "Booking Confirmed!",
    successfullyScheduled: "You have successfully scheduled appointments with",
    teachers: "teachers",
    nextBest: "Next Best",
    confirmationEmailSent: "A confirmation email has been sent to",
    addToCalendar: "Add to Calendar",
    done: "Done",
    selectedTeachers: "Selected Teachers",
    dragToReorder: "Drag to reorder",
    noTeachersSelected: "No teachers selected.",
    goBackToAdd: "Go back to add",
    scheduleYourConferences: "Schedule Your Conferences",
    youHaveTeachersToMeet: "You have {count} teachers to meet with.",
    youHaveMeetingsToSchedule: "You have {count} {meeting} to schedule.",
    meeting: "meeting",
    meetings: "meetings",
    continue: "Continue",
    continueButton: "continue →",
    yourTeachers: "Your Teachers",
    selectTimesManually: "Select Times Manually",
    suggestedSchedule: "Suggested Schedule",
    starts: "Starts:",
    totalTime: "Total time:",
    tapContinue: "Tap \"Continue\"",
    desiredStartTime: "Desired Start Time",
    autoSuggestSchedule: "Auto-Suggest Schedule",
    bestForBackToBack: "Best for back-to-back slots",
    or: "Or",
    selectManually: "Select Manually",
    stepOf: "Step {current} of {total}",
    percentComplete: "{percent}% Complete",
    availableTimes: "Available Times",
    noAvailableTimes: "No available times for this teacher.",
    reviewAndConfirm: "Review & Confirm",
    time: "Time",
    howToUsePlanner: "How to Use the Schedule Planner",
    howToUse: "How To Use",
    tipClickSlots: "Click time slots to select appointments for each teacher. Selected slots turn blue.",
    tipAutoSuggest: "Auto-Suggest (top right) finds the best consecutive schedule automatically.",
    tipDragTeachers: "Drag teachers in the sidebar to reorder them. This affects the suggested schedule order.",
    tipGrayedSlots: "Grayed-out slots are blocked because you've selected that time for another teacher.",
    tipEnterStudentNames: "Enter student names above each teacher column to specify which child each meeting is for.",
    foundOptimalSlots: "Found {count} optimal time slots!",
    suggestionsOnly: "These are suggestions only. Review the schedule below and click \"Confirm\" when ready to book.",
    couldNotFindSchedule: "Could not find a valid schedule. Try adjusting your start time or selecting times manually.",
    pleaseFillIn: "Please fill in the following:",
    validEmailRequired: "Please enter a valid email address.",
    
    // Booking page
    registrationOpensSoon: "Registration Opens Soon",
    opensIn: "Opens in:",
    unitDays: "Days",
    unitHours: "Hours",
    unitMinutes: "Minutes",
    unitSeconds: "Seconds",
    pageUnlocksAutomatically: "This page will automatically unlock when registration opens.",
    eventInformation: "Event Information",
    dateTBA: "Date TBA",
    virtualMeeting: "Virtual Meeting",
    selectTimeSlot: "Select a Time Slot",
    choosePreferredSlot: "Choose your preferred time slot for this event.",
    noSlotsAvailable: "No slots available.",
    selectedTime: "Selected:",
    waitlistOnly: "(Waitlist Only)",
    completeFormBelow: "Complete form below.",
    slotJustTaken: "Oops! That slot was just taken",
    slotTakenMessage: "Another parent booked this time just before you. The times below are up to date — pick another one.",
    slotTakenNextTimes: "No problem — {teacher} still has these times open. Pick one and everything you typed below stays filled in.",
    slotTakenNoTimes: "{teacher} has no other open times right now. You can join the waitlist for this time below, or look at other teachers.",
    slotTakenOrWaitlist: "Or join the waitlist for this time using the button below.",
    seeAllTimes: "See all times",
    refreshAvailability: "Refresh Availability",
    registerForEvent: "Register for this Event",
    selectSlotFirst: "Please select a time slot above first.",
    proceedToRegistration: "Click continue to complete your registration.",
    changeTime: "Change Time",
    yourAppointment: "Your Appointment",
    completeFormToConfirm: "Complete the form to confirm your booking.",
    fullName: "Full Name",
    parentNamePlaceholder: "Parent Name",
    emailAddress: "Email Address",
    emailPlaceholder: "email@example.com",
    emailOptionalNote: "Optional - provide to receive email reminders",
    studentName: "Student Name",
    studentNamePlaceholder: "Student Name",
    phoneNumber: "Phone Number",
    phonePlaceholder: "(555) 123-4567",
    phoneOptionalNote: "Optional - provide to receive SMS reminders",
    commentsOptional: "Comments or Special Requests (Optional)",
    commentsPlaceholder: "Any special needs, questions, or information you'd like to share with the teacher...",
    needInterpreter: "I need an interpreter",
    interpreterLanguagePlaceholder: "Language (e.g., Spanish, ASL)",
    reminderNotifications: "Reminder Notifications (Optional)",
    reminderNotificationsDesc: "Choose how you'd like to receive reminders about your appointment. You must provide the contact information for the methods you select.",
    emailReminders: "Email reminders",
    emailRemindersDesc: "Receive email reminders about your appointment. Requires email address above.",
    smsReminders: "SMS reminders",
    smsRemindersDesc: "Receive text message reminders. Requires phone number above. Standard rates may apply.",
    registering: "Registering...",
    confirmRegistration: "Confirm Registration",
    poweredBy: "Powered by",
    on: "on",
    
    // Multi-Child Support
    parentInformation: "Parent Information",
    enterYourInfoAndChildren: "Enter your contact information and add your children to schedule conferences.",
    children: "Children",
    addChild: "Add Child",
    addUpToTwoChildren: "You can schedule conferences for up to 2 children at once.",
    addFirstChild: "Add Your First Child",
    childName: "Child Name",
    continueToSelectTeachers: "Continue to Select Teachers",
    forChild: "For:",
    selectChild: "Select child...",
    for: "For",
    pleaseEnterName: "Please enter your name",
    pleaseEnterValidEmail: "Please enter a valid email address",
    pleaseEnterValidPhone: "Please enter a valid phone number",
    pleaseAddAtLeastOneChild: "Please add at least one child",
    pleaseEnterChildNames: "Please enter names for all children",
    pleaseAssignChildToAllTeachers: "Please assign a child to all selected teachers",
    enterYourName: "Enter your full name",
    enterYourEmail: "Enter your email address",
    childOne: "Child 1",
    childTwo: "Child 2",
    
    // Waitlist & Interpreter (schoolId page)
    conferenceNotAvailable: "Conference Not Available",
    startFresh: "Start Fresh",
    yourPrivacyMatters: "Your Privacy Matters",
    privacyInfoUsage: "We use your information solely to schedule your conference appointments.",
    privacyNoSelling: "We never sell your data.",
    viewPrivacyPolicy: "View Privacy Policy",
    autoSuggestWarning: "Auto-Suggest will replace your current time selections. Do you want to continue? This will clear your current selections.",
    interpreterNeeded: "I need an interpreter",
    selectInterpreterLanguage: "Select language",
    allSlotsFull: "All slots full",
    joinedWaitlistSuccess: "You've been added to the waitlist! We'll notify you if a spot opens up.",
    stepYourInfo: "Your<br />Info",
    stepSelectTeachers: "Select<br />Teachers",
    stepChooseTimes: "Choose<br />Times",
    stepConfirmMeetings: "Confirm<br />Meetings",
    draftModeMessage: "This conference is currently in draft mode and is not yet available for public registration. Please check back later or contact the school for more information.",
    draftModeAdminNote: "The conference will be visible once it's published by the school administrator.",
    tryAutoSuggest: "Try Auto-Suggest!",
    autoSuggestDescription: "Finds the best back-to-back schedule instantly.",
    autoSuggestedHint: "We picked back-to-back times for you — tap any slot to change them.",
    dismiss: "Dismiss",
    manageSchedule: "Manage Schedule",
    findYourSchedule: "Find Your Schedule",
    enterEmailToFindSchedule: "Enter the email address you used to book your appointments.",
    emailAddressLabel: "Email Address",
    sendVerificationCode: "Send Verification Code",
    checkYourEmail: "Check Your Email",
    weSentCodeTo: "We sent a 6-digit code to",
    verificationCode: "Verification Code",
    verifyAndViewSchedule: "Verify & View Schedule",
    useDifferentEmail: "Use a different email",
    viewingScheduleFor: "Viewing schedule for",
    noUpcomingAppointments: "No upcoming appointments found.",
    bookAnAppointment: "Book an Appointment",
    studentLabel: "Student:",
    rescheduleAppointment: "Reschedule Appointment",
    cancelAppointment: "Cancel Appointment",
    downloadCalendar: "Download Calendar (.ics)",
    bookMoreAppointments: "+ Book More Appointments",
    reschedule: "Reschedule",
    currentAppointment: "Current Appointment",
    selectNewTime: "Select a New Time",
    loadingAvailableTimes: "Loading available times...",
    noOtherTimesAvailable: "No other times available.",
    allSlotsFullOrPast: "All slots are full or in the past.",
    confirmReschedule: "Confirm Reschedule",
    cancelConfirmation: "Are you sure you want to cancel this appointment?",
  },
  
  es: {
    onWaitlist: "En lista de espera",
    bookingConfirmedTitle: "¡Reserva confirmada!",
    successfullyScheduled: "Ha programado correctamente citas con",
    teachers: "maestros",
    confirmationEmailSent: "Se ha enviado un correo de confirmación a",
    addToCalendar: "Agregar al calendario",
    done: "Listo",
    selectedTeachers: "Maestros seleccionados",
    dragToReorder: "Arrastre para reordenar",
    noTeachersSelected: "No hay maestros seleccionados.",
    goBackToAdd: "Regrese para agregar",
    youHaveTeachersToMeet: "Tiene {count} maestros con quienes reunirse.",
    desiredStartTime: "Hora de inicio deseada",
    autoSuggestSchedule: "Sugerir horario automáticamente",
    bestForBackToBack: "Ideal para citas consecutivas",
    or: "O",
    selectManually: "Seleccionar manualmente",
    stepOf: "Paso {current} de {total}",
    percentComplete: "{percent}% completado",
    availableTimes: "Horarios disponibles",
    noAvailableTimes: "No hay horarios disponibles para este maestro.",
    time: "Hora",
    howToUse: "Cómo usar",
    tipClickSlots: "Haga clic en los horarios para seleccionar citas con cada maestro. Los horarios seleccionados se resaltan.",
    tipAutoSuggest: "Sugerir automáticamente (arriba a la derecha) encuentra el mejor horario consecutivo.",
    tipDragTeachers: "Arrastre los maestros en la barra lateral para reordenarlos. Esto afecta el orden sugerido.",
    tipGrayedSlots: "Los horarios en gris están bloqueados porque ya eligió esa hora con otro maestro.",
    tipEnterStudentNames: "Escriba el nombre del estudiante sobre la columna de cada maestro para indicar de qué hijo es cada reunión.",
    foundOptimalSlots: "¡Se encontraron {count} horarios óptimos!",
    suggestionsOnly: "Estas son solo sugerencias. Revise el horario a continuación y haga clic en \"Confirmar\" cuando esté listo para reservar.",
    couldNotFindSchedule: "No se pudo encontrar un horario válido. Intente ajustar la hora de inicio o seleccionar menos maestros.",
    pleaseFillIn: "Complete lo siguiente:",
    validEmailRequired: "Ingrese un correo electrónico válido.",
    parentInformation: "Información del padre o tutor",
    enterYourInfoAndChildren: "Ingrese su información de contacto y agregue a sus hijos para programar las reuniones.",
    children: "Hijos",
    addChild: "Agregar hijo",
    addUpToTwoChildren: "Puede programar reuniones para hasta 2 hijos a la vez.",
    addFirstChild: "Agregue a su primer hijo",
    childName: "Nombre del hijo",
    continueToSelectTeachers: "Continuar para seleccionar maestros",
    forChild: "Para:",
    selectChild: "Seleccione un hijo...",
    for: "Para",
    pleaseEnterName: "Ingrese su nombre",
    pleaseEnterValidEmail: "Ingrese un correo electrónico válido",
    pleaseEnterValidPhone: "Ingrese un número de teléfono válido",
    pleaseAddAtLeastOneChild: "Agregue al menos un hijo",
    pleaseEnterChildNames: "Ingrese los nombres de todos los hijos",
    pleaseAssignChildToAllTeachers: "Asigne un hijo a todos los maestros seleccionados",
    enterYourName: "Ingrese su nombre completo",
    enterYourEmail: "Ingrese su correo electrónico",
    childOne: "Hijo 1",
    childTwo: "Hijo 2",
    // Common
    bookSlot: "Reservar",
    cancel: "Cancelar",
    confirmBooking: "Confirmar Reserva",
    yourName: "Su Nombre",
    email: "Correo Electrónico",
    phone: "Teléfono",
    signUp: "Inscribirse",
    full: "Lleno",
    teacher: "Maestro/a",
    location: "Ubicación",
    backToSchool: "Volver a las Conferencias",
    required: "(Requerido)",
    optional: "(Opcional)",
    notifications: "Notificaciones",
    
    // School Page
    selectTeachers: "Seleccionar Maestros",
    searchTeachers: "Buscar maestros...",
    sortBy: "Ordenar por",
    lastName: "Apellido",
    grade: "Grado",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Maestro Seleccionado",
    teachersSelected: "Maestros Seleccionados",
    findTimes: "Buscar Horarios",
    clear: "Limpiar",
    select: "Seleccionar",
    selected: "Seleccionado",
    add: "Agregar",
    added: "Agregado",
    availableSlots: "espacios disponibles",
    teamOf: "Equipo de",
    selectAllTeam: "¿Seleccionar todos los maestros de este equipo?",
    yes: "Sí",
    no: "No",
    allTeachers: "Todos los Maestros",
    showAllTeachers: "Mostrar todos los maestros",
    chooseTimes: "Elegir Horarios",
    pickAvailableSlots: "Seleccione espacios disponibles",
    confirm: "Confirmar",
    confirmMeetings: "Confirmar Reuniones",
    getConfirmation: "Recibir confirmación",
    
    // Booking Limit
    bookingLimitTitle: "Límite de Reservas",
    maxSlotsAllowed: "Máximo",
    slotsPerParent: "conferencias por padre para este evento.",
    enterEmailToSeeCount: "Ingrese su correo durante el registro para ver sus reservas actuales.",
    youHaveBooked: "Ha reservado",
    of: "de",
    allowedSlots: "espacios de conferencia permitidos",
    toBookMore: "Para reservar más, primero cancele una cita existente.",
    youCanBook: "Puede reservar",
    moreSlots: "espacio(s) más",
    viewMySchedule: "Ver y Gestionar Mi Horario →",
    
    // Plan Page
    planYourSchedule: "Planifique Su Horario",
    selectTimeSlots: "Seleccione horarios para cada maestro",
    autoSuggest: "Auto-Sugerir",
    autoSuggestDesc: "Permítanos encontrar los mejores horarios",
    confirmAppointments: "Confirmar Citas",
    appointment: "Cita",
    appointments: "Citas",
    noTimeSelected: "Sin horario seleccionado",
    childsName: "Nombre del Niño/a",
    enterChildName: "Ingrese el nombre de su hijo/a",
    yourDetails: "Sus Datos",
    parentName: "Nombre del Padre/Madre",
    yourFullName: "Su nombre completo",
    yourEmail: "su.correo@ejemplo.com",
    yourPhone: "Su número de teléfono",
    review: "Revisar",
    back: "Atrás",
    nextTeacher: "Siguiente Maestro",
    yourSchedule: "Su Horario",
    tapToChange: "Toque para cambiar",
    confirmNow: "Confirmar Ahora",
    confirming: "Confirmando...",
    
    // Booking Form
    bookingFor: "Reservando para",
    selectSlot: "Seleccione un horario",
    joinWaitlist: "Unirse a Lista de Espera",
    booked: "Reservado",
    waitlisted: "En Lista de Espera",
    notifyMe: "Notificarme por:",
    remindMeVia: "Recordarme por",
    emailNotify: "Correo",
    smsNotify: "SMS",
    bothNotify: "Ambos",
    
    // Confirmation
    bookingConfirmed: "¡Reserva Confirmada!",
    addedToWaitlist: "Agregado a Lista de Espera",
    confirmationSent: "Se ha enviado una confirmación a su correo.",
    waitlistInfo: "Le notificaremos si hay disponibilidad.",
    viewSchedule: "Ver Mi Horario",
    bookAnother: "Reservar Otro",
    
    // Schedule Lookup
    mySchedule: "Mi Horario",
    enterEmail: "Ingrese su correo para ver sus citas",
    lookupSchedule: "Buscar Horario",
    noAppointments: "No se encontraron citas",
    tryDifferentEmail: "Intente con otro correo",
    
    // Dates & Times
    today: "Hoy",
    tomorrow: "Mañana",
    am: "AM",
    pm: "PM",
    
    // Errors
    errorOccurred: "Ocurrió un error",
    pleaseRetry: "Por favor intente de nuevo",
    requiredField: "Este campo es requerido",
    invalidEmail: "Ingrese un correo válido",
    
    // Registration locked
    registrationOpens: "El Registro Abre",
    registrationClosed: "El registro ha cerrado",
    comingSoon: "Próximamente",
    
    // Additional UI elements
    teacherPortal: "Portal del Maestro",
    schoolConferences: "Conferencias Escolares",
    needToMakeChanges: "¿Necesita hacer cambios en su horario?",
    nameAZ: "Nombre (A-Z)",
    nameZA: "Nombre (Z-A)",
    gradeAsc: "Grado (Asc)",
    gradeDesc: "Grado (Desc)",
    noConferencesFound: "No se encontraron conferencias",
    generalEvents: "Eventos Generales",
    generalEvent: "Evento General",
    general: "General",
    signup: "Inscripción",
    appointmentsLabel: "Citas",
    virtual: "Virtual",
    groupedTeachersTooltip: "Maestros agrupados - al seleccionar uno se seleccionan todos",
    teachersAvailable: "maestros disponibles",
    clickHere: "Haga clic aquí",
    inCategory: "en",
    noTeachersInCategory: "No se encontraron maestros en",
    
    // Booking page
    registrationOpensSoon: "El registro abre pronto",
    opensIn: "Abre en:",
    unitDays: "Días",
    unitHours: "Horas",
    unitMinutes: "Minutos",
    unitSeconds: "Segundos",
    pageUnlocksAutomatically: "Esta página se desbloqueará automáticamente cuando abra el registro.",
    eventInformation: "Información del Evento",
    dateTBA: "Fecha por confirmar",
    virtualMeeting: "Reunión Virtual",
    selectTimeSlot: "Seleccione un Horario",
    choosePreferredSlot: "Elija su horario preferido para este evento.",
    noSlotsAvailable: "No hay espacios disponibles.",
    selectedTime: "Seleccionado:",
    waitlistOnly: "(Solo lista de espera)",
    completeFormBelow: "Complete el formulario abajo.",
    slotJustTaken: "¡Ups! Ese espacio acaba de ser ocupado",
    slotTakenNextTimes: "No hay problema: {teacher} todavía tiene estos horarios disponibles. Elija uno y todo lo que escribió abajo se mantiene.",
    slotTakenNoTimes: "{teacher} no tiene otros horarios disponibles ahora. Puede unirse a la lista de espera para este horario abajo, o ver otros maestros.",
    slotTakenOrWaitlist: "O únase a la lista de espera para este horario con el botón de abajo.",
    seeAllTimes: "Ver todos los horarios",
    slotTakenMessage: "Otro padre reservó este horario justo antes que usted. Los horarios de abajo están actualizados: elija otro.",
    refreshAvailability: "Actualizar Disponibilidad",
    registerForEvent: "Registrarse para este Evento",
    selectSlotFirst: "Por favor seleccione un horario arriba primero.",
    proceedToRegistration: "Haga clic en continuar para completar su registro.",
    changeTime: "Cambiar Hora",
    yourAppointment: "Su Cita",
    completeFormToConfirm: "Complete el formulario para confirmar su reserva.",
    fullName: "Nombre Completo",
    parentNamePlaceholder: "Nombre del Padre/Madre",
    emailAddress: "Correo Electrónico",
    emailPlaceholder: "correo@ejemplo.com",
    emailOptionalNote: "Opcional - proporcione para recibir recordatorios por correo",
    studentName: "Nombre del Estudiante",
    studentNamePlaceholder: "Nombre del Estudiante",
    phoneNumber: "Número de Teléfono",
    phonePlaceholder: "(555) 123-4567",
    phoneOptionalNote: "Opcional - proporcione para recibir recordatorios por SMS",
    commentsOptional: "Comentarios o Solicitudes Especiales (Opcional)",
    commentsPlaceholder: "Cualquier necesidad especial, pregunta o información que desee compartir con el maestro...",
    needInterpreter: "Necesito un intérprete",
    interpreterLanguagePlaceholder: "Idioma (ej., Español, LSA)",
    reminderNotifications: "Recordatorios (Opcional)",
    reminderNotificationsDesc: "Elija cómo desea recibir recordatorios. Debe proporcionar la información de contacto para los métodos que seleccione.",
    emailReminders: "Recordatorios por correo",
    emailRemindersDesc: "Reciba recordatorios por correo electrónico. Requiere dirección de correo arriba.",
    smsReminders: "Recordatorios por SMS",
    smsRemindersDesc: "Reciba recordatorios por mensaje de texto. Requiere número de teléfono arriba. Pueden aplicar tarifas estándar.",
    registering: "Registrando...",
    confirmRegistration: "Confirmar Registro",
    
    // Plan page additions
    scheduleYourConferences: "Programe Sus Conferencias",
    youHaveMeetingsToSchedule: "Tiene {count} {meeting} para programar.",
    meeting: "reunión",
    meetings: "reuniones",
    continue: "Continuar",
    continueButton: "continuar →",
    yourTeachers: "Sus Maestros",
    selectTimesManually: "Seleccionar Horarios Manualmente",
    suggestedSchedule: "Horario Sugerido",
    starts: "Comienza:",
    totalTime: "Tiempo total:",
    tapContinue: "Toque \"Continuar\"",
    howToUsePlanner: "Cómo Usar el Planificador de Horarios",
    reviewAndConfirm: "Revisar y Confirmar",
    nextBest: "Siguiente Mejor",
    
    poweredBy: "Desarrollado por",
    on: "el",
    conferenceNotAvailable: "Conferencia No Disponible",
    startFresh: "Empezar de Nuevo",
    yourPrivacyMatters: "Su Privacidad es Importante",
    privacyInfoUsage: "Usamos su información únicamente para programar sus citas de conferencia.",
    privacyNoSelling: "Nunca vendemos sus datos.",
    viewPrivacyPolicy: "Ver Política de Privacidad",
    autoSuggestWarning: "Auto-Sugerir reemplazará sus selecciones de horarios actuales. ¿Desea continuar? Esto borrará sus selecciones actuales.",
    interpreterNeeded: "Necesito un intérprete",
    selectInterpreterLanguage: "Seleccionar idioma",
    allSlotsFull: "Todos los horarios llenos",
    joinedWaitlistSuccess: "¡Se ha agregado a la lista de espera! Le notificaremos si se abre un lugar.",
    stepYourInfo: "Su<br />Info",
    stepSelectTeachers: "Seleccionar<br />Maestros",
    stepChooseTimes: "Elegir<br />Horarios",
    stepConfirmMeetings: "Confirmar<br />Reuniones",
    draftModeMessage: "Esta conferencia está actualmente en modo borrador y no está disponible para registro público. Por favor, vuelva más tarde o contacte a la escuela para más información.",
    draftModeAdminNote: "La conferencia será visible una vez que el administrador de la escuela la publique.",
    tryAutoSuggest: "¡Probar Auto-Sugerencia!",
    autoSuggestDescription: "Encuentra el mejor horario consecutivo al instante.",
    dismiss: "Descartar",
    manageSchedule: "Administrar Horario",
    findYourSchedule: "Encuentra Tu Horario",
    enterEmailToFindSchedule: "Ingresa el correo electrónico que usaste para reservar tus citas.",
    emailAddressLabel: "Correo Electrónico",
    sendVerificationCode: "Enviar Código de Verificación",
    checkYourEmail: "Revisa Tu Correo",
    weSentCodeTo: "Enviamos un código de 6 dígitos a",
    verificationCode: "Código de Verificación",
    verifyAndViewSchedule: "Verificar y Ver Horario",
    useDifferentEmail: "Usar otro correo",
    viewingScheduleFor: "Viendo horario de",
    noUpcomingAppointments: "No se encontraron citas próximas.",
    bookAnAppointment: "Reservar una Cita",
    studentLabel: "Estudiante:",
    rescheduleAppointment: "Reprogramar Cita",
    cancelAppointment: "Cancelar Cita",
    downloadCalendar: "Descargar Calendario (.ics)",
    bookMoreAppointments: "+ Reservar Más Citas",
    reschedule: "Reprogramar",
    currentAppointment: "Cita Actual",
    selectNewTime: "Seleccionar Nuevo Horario",
    loadingAvailableTimes: "Cargando horarios disponibles...",
    noOtherTimesAvailable: "No hay otros horarios disponibles.",
    allSlotsFullOrPast: "Todos los horarios están llenos o ya pasaron.",
    confirmReschedule: "Confirmar Reprogramación",
    cancelConfirmation: "¿Estás seguro de que quieres cancelar esta cita?",
  },
  
  fr: {
    bookingConfirmedTitle: "Réservation confirmée !",
    successfullyScheduled: "Vous avez bien programmé des rendez-vous avec",
    teachers: "enseignants",
    confirmationEmailSent: "Un e-mail de confirmation a été envoyé à",
    addToCalendar: "Ajouter au calendrier",
    done: "Terminé",
    selectedTeachers: "Enseignants sélectionnés",
    dragToReorder: "Glissez pour réorganiser",
    noTeachersSelected: "Aucun enseignant sélectionné.",
    goBackToAdd: "Revenez pour ajouter",
    youHaveTeachersToMeet: "Vous avez {count} enseignants à rencontrer.",
    desiredStartTime: "Heure de début souhaitée",
    autoSuggestSchedule: "Proposer un horaire automatiquement",
    bestForBackToBack: "Idéal pour des rendez-vous consécutifs",
    or: "Ou",
    selectManually: "Sélectionner manuellement",
    stepOf: "Étape {current} sur {total}",
    percentComplete: "{percent} % terminé",
    availableTimes: "Horaires disponibles",
    noAvailableTimes: "Aucun horaire disponible pour cet enseignant.",
    time: "Heure",
    howToUse: "Mode d'emploi",
    tipClickSlots: "Cliquez sur les créneaux pour choisir un rendez-vous avec chaque enseignant. Les créneaux choisis sont mis en évidence.",
    tipAutoSuggest: "La proposition automatique (en haut à droite) trouve le meilleur horaire consécutif.",
    tipDragTeachers: "Faites glisser les enseignants dans le panneau latéral pour les réorganiser. Cela influence l'ordre proposé.",
    tipGrayedSlots: "Les créneaux grisés sont bloqués car vous avez déjà choisi cette heure avec un autre enseignant.",
    tipEnterStudentNames: "Saisissez le nom de l'élève au-dessus de chaque colonne pour indiquer de quel enfant il s'agit.",
    foundOptimalSlots: "{count} créneaux optimaux trouvés !",
    suggestionsOnly: "Ce ne sont que des suggestions. Vérifiez l'horaire ci-dessous puis cliquez sur \"Confirmer\" pour réserver.",
    couldNotFindSchedule: "Impossible de trouver un horaire valide. Essayez de modifier l'heure de début ou de sélectionner moins d'enseignants.",
    pleaseFillIn: "Veuillez remplir les champs suivants :",
    validEmailRequired: "Veuillez saisir une adresse e-mail valide.",
    parentInformation: "Informations du parent",
    enterYourInfoAndChildren: "Saisissez vos coordonnées et ajoutez vos enfants pour programmer les rendez-vous.",
    children: "Enfants",
    addChild: "Ajouter un enfant",
    addUpToTwoChildren: "Vous pouvez programmer des rendez-vous pour 2 enfants à la fois.",
    addFirstChild: "Ajoutez votre premier enfant",
    childName: "Nom de l'enfant",
    continueToSelectTeachers: "Continuer vers la sélection des enseignants",
    forChild: "Pour :",
    selectChild: "Choisir un enfant...",
    for: "Pour",
    pleaseEnterName: "Veuillez saisir votre nom",
    pleaseEnterValidEmail: "Veuillez saisir une adresse e-mail valide",
    pleaseEnterValidPhone: "Veuillez saisir un numéro de téléphone valide",
    pleaseAddAtLeastOneChild: "Veuillez ajouter au moins un enfant",
    pleaseEnterChildNames: "Veuillez saisir le nom de tous les enfants",
    pleaseAssignChildToAllTeachers: "Veuillez attribuer un enfant à chaque enseignant sélectionné",
    enterYourName: "Saisissez votre nom complet",
    enterYourEmail: "Saisissez votre adresse e-mail",
    childOne: "Enfant 1",
    childTwo: "Enfant 2",
    // Common
    bookSlot: "Réserver",
    cancel: "Annuler",
    confirmBooking: "Confirmer",
    yourName: "Votre Nom",
    email: "Email",
    phone: "Téléphone",
    signUp: "S'inscrire",
    full: "Complet",
    teacher: "Enseignant",
    location: "Lieu",
    backToSchool: "Retour aux Conférences",
    required: "(Requis)",
    optional: "(Optionnel)",
    notifications: "Notifications",
    
    // School Page
    selectTeachers: "Sélectionner les Enseignants",
    searchTeachers: "Rechercher enseignants...",
    sortBy: "Trier par",
    lastName: "Nom de Famille",
    grade: "Classe",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Enseignant Sélectionné",
    teachersSelected: "Enseignants Sélectionnés",
    findTimes: "Trouver des Créneaux",
    clear: "Effacer",
    select: "Sélectionner",
    selected: "Sélectionné",
    add: "Ajouter",
    added: "Ajouté",
    availableSlots: "créneaux disponibles",
    teamOf: "Équipe de",
    selectAllTeam: "Sélectionner tous les enseignants de cette équipe?",
    yes: "Oui",
    no: "Non",
    allTeachers: "Tous les Enseignants",
    showAllTeachers: "Afficher tous les enseignants",
    chooseTimes: "Choisir les Horaires",
    pickAvailableSlots: "Choisissez les créneaux",
    confirm: "Confirmer",
    confirmMeetings: "Confirmer les Réunions",
    getConfirmation: "Recevoir confirmation",
    
    // Booking Limit
    bookingLimitTitle: "Limite de Réservation",
    maxSlotsAllowed: "Maximum",
    slotsPerParent: "conférences par parent pour cet événement.",
    enterEmailToSeeCount: "Entrez votre email lors de l'inscription pour voir vos réservations actuelles.",
    youHaveBooked: "Vous avez réservé",
    of: "sur",
    allowedSlots: "créneaux de conférence autorisés",
    toBookMore: "Pour réserver plus, veuillez d'abord annuler un rendez-vous existant.",
    youCanBook: "Vous pouvez réserver",
    moreSlots: "créneau(x) supplémentaire(s)",
    viewMySchedule: "Voir et Gérer Mon Emploi du Temps →",
    
    // Plan Page
    planYourSchedule: "Planifiez Votre Emploi du Temps",
    selectTimeSlots: "Sélectionnez des créneaux pour chaque enseignant",
    autoSuggest: "Suggestion Auto",
    autoSuggestDesc: "Laissez-nous trouver les meilleurs horaires",
    confirmAppointments: "Confirmer les Rendez-vous",
    appointment: "Rendez-vous",
    appointments: "Rendez-vous",
    noTimeSelected: "Aucun horaire sélectionné",
    childsName: "Nom de l'Enfant",
    enterChildName: "Entrez le nom de votre enfant",
    yourDetails: "Vos Coordonnées",
    parentName: "Nom du Parent",
    yourFullName: "Votre nom complet",
    yourEmail: "votre.email@exemple.com",
    yourPhone: "Votre numéro de téléphone",
    review: "Réviser",
    back: "Retour",
    nextTeacher: "Enseignant Suivant",
    yourSchedule: "Votre Emploi du Temps",
    tapToChange: "Appuyez pour modifier",
    confirmNow: "Confirmer Maintenant",
    confirming: "Confirmation...",
    
    // Booking Form
    bookingFor: "Réservation pour",
    selectSlot: "Sélectionnez un créneau",
    joinWaitlist: "Rejoindre la Liste d'Attente",
    booked: "Réservé",
    waitlisted: "En Liste d'Attente",
    notifyMe: "Me notifier par:",
    remindMeVia: "Me rappeler par",
    emailNotify: "Email",
    smsNotify: "SMS",
    bothNotify: "Les Deux",
    
    // Confirmation
    bookingConfirmed: "Réservation Confirmée!",
    addedToWaitlist: "Ajouté à la Liste d'Attente",
    confirmationSent: "Une confirmation a été envoyée par email.",
    waitlistInfo: "Nous vous notifierons si une place se libère.",
    viewSchedule: "Voir Mon Emploi du Temps",
    bookAnother: "Réserver un Autre",
    
    // Schedule Lookup
    mySchedule: "Mon Emploi du Temps",
    enterEmail: "Entrez votre email pour voir vos rendez-vous",
    lookupSchedule: "Rechercher l'Emploi du Temps",
    noAppointments: "Aucun rendez-vous trouvé",
    tryDifferentEmail: "Essayez une autre adresse email",
    
    // Dates & Times
    today: "Aujourd'hui",
    tomorrow: "Demain",
    am: "h",
    pm: "h",
    
    // Errors
    errorOccurred: "Une erreur s'est produite",
    pleaseRetry: "Veuillez réessayer",
    requiredField: "Ce champ est requis",
    invalidEmail: "Veuillez entrer un email valide",
    
    // Registration locked
    registrationOpens: "L'Inscription Ouvre",
    registrationClosed: "L'inscription est fermée",
    comingSoon: "Bientôt Disponible",
    
    // Additional UI elements
    teacherPortal: "Portail Enseignant",
    schoolConferences: "Conférences Scolaires",
    needToMakeChanges: "Besoin de modifier votre emploi du temps?",
    nameAZ: "Nom (A-Z)",
    nameZA: "Nom (Z-A)",
    gradeAsc: "Classe (Asc)",
    gradeDesc: "Classe (Desc)",
    noConferencesFound: "Aucune conférence trouvée",
    generalEvents: "Événements Généraux",
    generalEvent: "Événement Général",
    general: "Général",
    signup: "Inscription",
    appointmentsLabel: "Rendez-vous",
    virtual: "Virtuel",
    groupedTeachersTooltip: "Enseignants groupés - sélectionner un sélectionne tous",
    teachersAvailable: "enseignants disponibles",
    clickHere: "Cliquez ici",
    inCategory: "dans",
    noTeachersInCategory: "Aucun enseignant trouvé dans",
    
    // Booking page
    registrationOpensSoon: "L'inscription ouvre bientôt",
    opensIn: "Ouvre dans:",
    unitDays: "Jours",
    unitHours: "Heures",
    unitMinutes: "Minutes",
    unitSeconds: "Secondes",
    pageUnlocksAutomatically: "Cette page se déverrouillera automatiquement à l'ouverture des inscriptions.",
    eventInformation: "Informations sur l'Événement",
    dateTBA: "Date à confirmer",
    virtualMeeting: "Réunion Virtuelle",
    selectTimeSlot: "Sélectionnez un Créneau",
    choosePreferredSlot: "Choisissez votre créneau préféré pour cet événement.",
    noSlotsAvailable: "Aucun créneau disponible.",
    selectedTime: "Sélectionné:",
    waitlistOnly: "(Liste d'attente uniquement)",
    completeFormBelow: "Complétez le formulaire ci-dessous.",
    slotJustTaken: "Oups! Ce créneau vient d'être pris",
    slotTakenMessage: "Un autre parent a réservé ce créneau juste avant vous. Actualisez pour voir la disponibilité et sélectionnez un autre horaire.",
    refreshAvailability: "Actualiser la Disponibilité",
    registerForEvent: "S'inscrire à cet Événement",
    selectSlotFirst: "Veuillez d'abord sélectionner un créneau ci-dessus.",
    proceedToRegistration: "Cliquez sur continuer pour compléter votre inscription.",
    changeTime: "Changer l'Heure",
    yourAppointment: "Votre Rendez-vous",
    completeFormToConfirm: "Complétez le formulaire pour confirmer votre réservation.",
    fullName: "Nom Complet",
    parentNamePlaceholder: "Nom du Parent",
    emailAddress: "Adresse Email",
    emailPlaceholder: "email@exemple.com",
    emailOptionalNote: "Optionnel - fournissez pour recevoir des rappels par email",
    studentName: "Nom de l'Élève",
    studentNamePlaceholder: "Nom de l'Élève",
    phoneNumber: "Numéro de Téléphone",
    phonePlaceholder: "01 23 45 67 89",
    phoneOptionalNote: "Optionnel - fournissez pour recevoir des rappels SMS",
    commentsOptional: "Commentaires ou Demandes Spéciales (Optionnel)",
    commentsPlaceholder: "Besoins spéciaux, questions ou informations à partager avec l'enseignant...",
    needInterpreter: "J'ai besoin d'un interprète",
    interpreterLanguagePlaceholder: "Langue (ex., Espagnol, LSF)",
    reminderNotifications: "Rappels (Optionnel)",
    reminderNotificationsDesc: "Choisissez comment recevoir vos rappels. Vous devez fournir les coordonnées pour les méthodes sélectionnées.",
    emailReminders: "Rappels par email",
    emailRemindersDesc: "Recevez des rappels par email. Nécessite une adresse email ci-dessus.",
    smsReminders: "Rappels par SMS",
    smsRemindersDesc: "Recevez des rappels par SMS. Nécessite un numéro de téléphone ci-dessus. Tarifs standards applicables.",
    registering: "Inscription en cours...",
    confirmRegistration: "Confirmer l'Inscription",
    
    // Plan page additions
    scheduleYourConferences: "Planifiez Vos Conférences",
    youHaveMeetingsToSchedule: "Vous avez {count} {meeting} à planifier.",
    meeting: "réunion",
    meetings: "réunions",
    continue: "Continuer",
    continueButton: "continuer →",
    yourTeachers: "Vos Enseignants",
    selectTimesManually: "Sélectionner les Horaires Manuellement",
    suggestedSchedule: "Emploi du Temps Suggéré",
    starts: "Démarre:",
    totalTime: "Temps total:",
    tapContinue: "Appuyez sur \"Continuer\"",
    howToUsePlanner: "Comment Utiliser le Planificateur d'Emploi du Temps",
    reviewAndConfirm: "Réviser et Confirmer",
    nextBest: "Meilleur Suivant",
    
    poweredBy: "Propulsé par",
    on: "le",
    conferenceNotAvailable: "Conférence Non Disponible",
    startFresh: "Recommencer",
    yourPrivacyMatters: "Votre Vie Privée Compte",
    privacyInfoUsage: "Nous utilisons vos informations uniquement pour planifier vos rendez-vous de conférence.",
    privacyNoSelling: "Nous ne vendons jamais vos données.",
    viewPrivacyPolicy: "Voir la Politique de Confidentialité",
    autoSuggestWarning: "L'auto-suggestion remplacera vos sélections d'horaires actuelles. Voulez-vous continuer ? Cela effacera vos sélections actuelles.",
    interpreterNeeded: "J'ai besoin d'un interprète",
    selectInterpreterLanguage: "Sélectionner la langue",
    allSlotsFull: "Tous les créneaux sont complets",
    joinedWaitlistSuccess: "Vous avez été ajouté à la liste d'attente ! Nous vous informerons si une place se libère.",
    stepYourInfo: "Vos<br />Infos",
    stepSelectTeachers: "Choisir<br />Enseignants",
    stepChooseTimes: "Choisir<br />Horaires",
    stepConfirmMeetings: "Confirmer<br />Rendez-vous",
    draftModeMessage: "Cette conférence est actuellement en mode brouillon et n'est pas encore disponible pour l'inscription publique. Veuillez revenir plus tard ou contacter l'école pour plus d'informations.",
    draftModeAdminNote: "La conférence sera visible une fois publiée par l'administrateur de l'école.",
    tryAutoSuggest: "Essayer Auto-Suggestion !",
    autoSuggestDescription: "Trouve le meilleur emploi du temps consécutif instantanément.",
    dismiss: "Fermer",
    manageSchedule: "Gérer le Planning",
    findYourSchedule: "Trouvez Votre Planning",
    enterEmailToFindSchedule: "Entrez l'adresse e-mail utilisée pour réserver vos rendez-vous.",
    emailAddressLabel: "Adresse E-mail",
    sendVerificationCode: "Envoyer le Code de Vérification",
    checkYourEmail: "Vérifiez Votre E-mail",
    weSentCodeTo: "Nous avons envoyé un code à 6 chiffres à",
    verificationCode: "Code de Vérification",
    verifyAndViewSchedule: "Vérifier et Voir le Planning",
    useDifferentEmail: "Utiliser un autre e-mail",
    viewingScheduleFor: "Planning pour",
    noUpcomingAppointments: "Aucun rendez-vous à venir.",
    bookAnAppointment: "Prendre un Rendez-vous",
    studentLabel: "Élève :",
    rescheduleAppointment: "Reprogrammer le Rendez-vous",
    cancelAppointment: "Annuler le Rendez-vous",
    downloadCalendar: "Télécharger le Calendrier (.ics)",
    bookMoreAppointments: "+ Réserver Plus de Rendez-vous",
    reschedule: "Reprogrammer",
    currentAppointment: "Rendez-vous Actuel",
    selectNewTime: "Sélectionner un Nouvel Horaire",
    loadingAvailableTimes: "Chargement des horaires disponibles...",
    noOtherTimesAvailable: "Aucun autre horaire disponible.",
    allSlotsFullOrPast: "Tous les créneaux sont pleins ou passés.",
    confirmReschedule: "Confirmer la Reprogrammation",
    cancelConfirmation: "Êtes-vous sûr de vouloir annuler ce rendez-vous ?",
  },
  
  de: {
    bookingConfirmedTitle: "Buchung bestätigt!",
    successfullyScheduled: "Sie haben erfolgreich Termine vereinbart mit",
    teachers: "Lehrkräften",
    confirmationEmailSent: "Eine Bestätigungs-E-Mail wurde gesendet an",
    addToCalendar: "Zum Kalender hinzufügen",
    done: "Fertig",
    selectedTeachers: "Ausgewählte Lehrkräfte",
    dragToReorder: "Zum Umsortieren ziehen",
    noTeachersSelected: "Keine Lehrkräfte ausgewählt.",
    goBackToAdd: "Zurückgehen zum Hinzufügen",
    youHaveTeachersToMeet: "Sie haben {count} Lehrkräfte zu treffen.",
    desiredStartTime: "Gewünschte Startzeit",
    autoSuggestSchedule: "Zeitplan automatisch vorschlagen",
    bestForBackToBack: "Ideal für aufeinanderfolgende Termine",
    or: "Oder",
    selectManually: "Manuell auswählen",
    stepOf: "Schritt {current} von {total}",
    percentComplete: "{percent} % abgeschlossen",
    availableTimes: "Verfügbare Zeiten",
    noAvailableTimes: "Für diese Lehrkraft sind keine Zeiten verfügbar.",
    time: "Uhrzeit",
    howToUse: "So funktioniert es",
    tipClickSlots: "Klicken Sie auf die Zeitfenster, um Termine mit jeder Lehrkraft auszuwählen. Gewählte Zeitfenster werden hervorgehoben.",
    tipAutoSuggest: "Der automatische Vorschlag (oben rechts) findet den besten zusammenhängenden Zeitplan.",
    tipDragTeachers: "Ziehen Sie die Lehrkräfte in der Seitenleiste, um sie umzusortieren. Das beeinflusst die vorgeschlagene Reihenfolge.",
    tipGrayedSlots: "Ausgegraute Zeitfenster sind blockiert, weil Sie diese Zeit bereits für eine andere Lehrkraft gewählt haben.",
    tipEnterStudentNames: "Geben Sie über jeder Spalte den Namen des Kindes ein, um festzulegen, für wen der Termin ist.",
    foundOptimalSlots: "{count} optimale Zeitfenster gefunden!",
    suggestionsOnly: "Dies sind nur Vorschläge. Prüfen Sie den Zeitplan unten und klicken Sie auf \"Bestätigen\", wenn Sie buchen möchten.",
    couldNotFindSchedule: "Es konnte kein gültiger Zeitplan gefunden werden. Passen Sie die Startzeit an oder wählen Sie weniger Lehrkräfte.",
    pleaseFillIn: "Bitte füllen Sie Folgendes aus:",
    validEmailRequired: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    parentInformation: "Angaben der Eltern",
    enterYourInfoAndChildren: "Geben Sie Ihre Kontaktdaten ein und fügen Sie Ihre Kinder hinzu, um Termine zu vereinbaren.",
    children: "Kinder",
    addChild: "Kind hinzufügen",
    addUpToTwoChildren: "Sie können Termine für bis zu 2 Kinder gleichzeitig vereinbaren.",
    addFirstChild: "Fügen Sie Ihr erstes Kind hinzu",
    childName: "Name des Kindes",
    continueToSelectTeachers: "Weiter zur Auswahl der Lehrkräfte",
    forChild: "Für:",
    selectChild: "Kind auswählen...",
    for: "Für",
    pleaseEnterName: "Bitte geben Sie Ihren Namen ein",
    pleaseEnterValidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    pleaseEnterValidPhone: "Bitte geben Sie eine gültige Telefonnummer ein",
    pleaseAddAtLeastOneChild: "Bitte fügen Sie mindestens ein Kind hinzu",
    pleaseEnterChildNames: "Bitte geben Sie die Namen aller Kinder ein",
    pleaseAssignChildToAllTeachers: "Bitte ordnen Sie allen ausgewählten Lehrkräften ein Kind zu",
    enterYourName: "Geben Sie Ihren vollständigen Namen ein",
    enterYourEmail: "Geben Sie Ihre E-Mail-Adresse ein",
    childOne: "Kind 1",
    childTwo: "Kind 2",
    // Common
    bookSlot: "Termin buchen",
    cancel: "Abbrechen",
    confirmBooking: "Buchung bestätigen",
    yourName: "Ihr Name",
    email: "E-Mail",
    phone: "Telefon",
    signUp: "Anmelden",
    full: "Voll",
    teacher: "Lehrer",
    location: "Ort",
    backToSchool: "Zurück zu Konferenzen",
    required: "(Erforderlich)",
    optional: "(Optional)",
    notifications: "Benachrichtigungen",
    
    // School Page
    selectTeachers: "Lehrer Auswählen",
    searchTeachers: "Lehrer suchen...",
    sortBy: "Sortieren nach",
    lastName: "Nachname",
    grade: "Klasse",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Lehrer Ausgewählt",
    teachersSelected: "Lehrer Ausgewählt",
    findTimes: "Zeiten Finden",
    clear: "Löschen",
    select: "Auswählen",
    selected: "Ausgewählt",
    add: "Hinzufügen",
    added: "Hinzugefügt",
    availableSlots: "verfügbare Termine",
    teamOf: "Team von",
    selectAllTeam: "Alle Lehrer dieses Teams auswählen?",
    yes: "Ja",
    no: "Nein",
    allTeachers: "Alle Lehrer",
    showAllTeachers: "Alle Lehrer anzeigen",
    chooseTimes: "Zeiten Wählen",
    pickAvailableSlots: "Verfügbare Termine auswählen",
    confirm: "Bestätigen",
    confirmMeetings: "Termine Bestätigen",
    getConfirmation: "Bestätigung erhalten",
    
    // Booking Limit
    bookingLimitTitle: "Buchungslimit",
    maxSlotsAllowed: "Maximal",
    slotsPerParent: "Konferenzen pro Elternteil für diese Veranstaltung.",
    enterEmailToSeeCount: "Geben Sie bei der Registrierung Ihre E-Mail ein, um Ihre aktuellen Buchungen zu sehen.",
    youHaveBooked: "Sie haben gebucht",
    of: "von",
    allowedSlots: "erlaubten Konferenzplätzen",
    toBookMore: "Um mehr zu buchen, stornieren Sie bitte zuerst einen bestehenden Termin.",
    youCanBook: "Sie können buchen",
    moreSlots: "weitere(n) Platz/Plätze",
    viewMySchedule: "Meinen Zeitplan Anzeigen & Verwalten →",
    
    // Plan Page
    planYourSchedule: "Planen Sie Ihren Zeitplan",
    selectTimeSlots: "Wählen Sie Zeitfenster für jeden Lehrer",
    autoSuggest: "Auto-Vorschlag",
    autoSuggestDesc: "Lassen Sie uns die besten Zeiten finden",
    confirmAppointments: "Termine Bestätigen",
    appointment: "Termin",
    appointments: "Termine",
    noTimeSelected: "Keine Zeit ausgewählt",
    childsName: "Name des Kindes",
    enterChildName: "Geben Sie den Namen Ihres Kindes ein",
    yourDetails: "Ihre Angaben",
    parentName: "Elternname",
    yourFullName: "Ihr vollständiger Name",
    yourEmail: "ihre.email@beispiel.com",
    yourPhone: "Ihre Telefonnummer",
    review: "Überprüfen",
    back: "Zurück",
    nextTeacher: "Nächster Lehrer",
    yourSchedule: "Ihr Zeitplan",
    tapToChange: "Tippen zum Ändern",
    confirmNow: "Jetzt Bestätigen",
    confirming: "Bestätigen...",
    
    // Booking Form
    bookingFor: "Buchung für",
    selectSlot: "Wählen Sie einen Termin",
    joinWaitlist: "Warteliste Beitreten",
    booked: "Gebucht",
    waitlisted: "Auf Warteliste",
    notifyMe: "Benachrichtigen per:",
    remindMeVia: "Erinnern per",
    emailNotify: "E-Mail",
    smsNotify: "SMS",
    bothNotify: "Beide",
    
    // Confirmation
    bookingConfirmed: "Buchung Bestätigt!",
    addedToWaitlist: "Zur Warteliste Hinzugefügt",
    confirmationSent: "Eine Bestätigung wurde per E-Mail gesendet.",
    waitlistInfo: "Wir benachrichtigen Sie, wenn ein Platz frei wird.",
    viewSchedule: "Meinen Zeitplan Ansehen",
    bookAnother: "Weiteren Buchen",
    
    // Schedule Lookup
    mySchedule: "Mein Zeitplan",
    enterEmail: "Geben Sie Ihre E-Mail ein, um Ihre Termine zu sehen",
    lookupSchedule: "Zeitplan Nachschlagen",
    noAppointments: "Keine Termine gefunden",
    tryDifferentEmail: "Versuchen Sie eine andere E-Mail-Adresse",
    
    // Dates & Times
    today: "Heute",
    tomorrow: "Morgen",
    am: "Uhr",
    pm: "Uhr",
    
    // Errors
    errorOccurred: "Ein Fehler ist aufgetreten",
    pleaseRetry: "Bitte versuchen Sie es erneut",
    requiredField: "Dieses Feld ist erforderlich",
    invalidEmail: "Bitte geben Sie eine gültige E-Mail ein",
    
    // Registration locked
    registrationOpens: "Anmeldung Öffnet",
    registrationClosed: "Die Anmeldung ist geschlossen",
    comingSoon: "Demnächst",
    
    // Additional UI elements
    teacherPortal: "Lehrer-Portal",
    schoolConferences: "Schulkonferenzen",
    needToMakeChanges: "Müssen Sie Änderungen an Ihrem Zeitplan vornehmen?",
    nameAZ: "Name (A-Z)",
    nameZA: "Name (Z-A)",
    gradeAsc: "Klasse (Aufst.)",
    gradeDesc: "Klasse (Abst.)",
    noConferencesFound: "Keine Konferenzen gefunden",
    generalEvents: "Allgemeine Veranstaltungen",
    generalEvent: "Allgemeine Veranstaltung",
    general: "Allgemein",
    signup: "Anmeldung",
    appointmentsLabel: "Termine",
    virtual: "Virtuell",
    groupedTeachersTooltip: "Gruppierte Lehrer - bei Auswahl werden alle ausgewählt",
    teachersAvailable: "Lehrer verfügbar",
    clickHere: "Hier klicken",
    inCategory: "in",
    noTeachersInCategory: "Keine Lehrer gefunden in",
    
    // Booking page
    registrationOpensSoon: "Anmeldung öffnet bald",
    opensIn: "Öffnet in:",
    unitDays: "Tage",
    unitHours: "Stunden",
    unitMinutes: "Minuten",
    unitSeconds: "Sekunden",
    pageUnlocksAutomatically: "Diese Seite wird automatisch entsperrt, wenn die Anmeldung beginnt.",
    eventInformation: "Veranstaltungsinformationen",
    dateTBA: "Datum noch offen",
    virtualMeeting: "Virtuelles Treffen",
    selectTimeSlot: "Zeitfenster Auswählen",
    choosePreferredSlot: "Wählen Sie Ihr bevorzugtes Zeitfenster für diese Veranstaltung.",
    noSlotsAvailable: "Keine Termine verfügbar.",
    selectedTime: "Ausgewählt:",
    waitlistOnly: "(Nur Warteliste)",
    completeFormBelow: "Formular unten ausfüllen.",
    slotJustTaken: "Ups! Dieser Termin wurde gerade vergeben",
    slotTakenMessage: "Ein anderer Elternteil hat diesen Termin kurz vor Ihnen gebucht. Aktualisieren Sie die Verfügbarkeit und wählen Sie eine andere Zeit.",
    refreshAvailability: "Verfügbarkeit Aktualisieren",
    registerForEvent: "Für diese Veranstaltung Anmelden",
    selectSlotFirst: "Bitte wählen Sie zuerst einen Termin oben aus.",
    proceedToRegistration: "Klicken Sie auf Weiter, um Ihre Anmeldung abzuschließen.",
    changeTime: "Zeit Ändern",
    yourAppointment: "Ihr Termin",
    completeFormToConfirm: "Füllen Sie das Formular aus, um Ihre Buchung zu bestätigen.",
    fullName: "Vollständiger Name",
    parentNamePlaceholder: "Name des Elternteils",
    emailAddress: "E-Mail-Adresse",
    emailPlaceholder: "email@beispiel.de",
    emailOptionalNote: "Optional - angeben für E-Mail-Erinnerungen",
    studentName: "Name des Schülers",
    studentNamePlaceholder: "Name des Schülers",
    phoneNumber: "Telefonnummer",
    phonePlaceholder: "0123 456789",
    phoneOptionalNote: "Optional - angeben für SMS-Erinnerungen",
    commentsOptional: "Kommentare oder Sonderwünsche (Optional)",
    commentsPlaceholder: "Besondere Bedürfnisse, Fragen oder Informationen für den Lehrer...",
    needInterpreter: "Ich benötige einen Dolmetscher",
    interpreterLanguagePlaceholder: "Sprache (z.B., Spanisch, DGS)",
    reminderNotifications: "Erinnerungen (Optional)",
    reminderNotificationsDesc: "Wählen Sie, wie Sie Erinnerungen erhalten möchten. Sie müssen die Kontaktdaten für die gewählten Methoden angeben.",
    emailReminders: "E-Mail-Erinnerungen",
    emailRemindersDesc: "Erhalten Sie E-Mail-Erinnerungen. Erfordert E-Mail-Adresse oben.",
    smsReminders: "SMS-Erinnerungen",
    smsRemindersDesc: "Erhalten Sie SMS-Erinnerungen. Erfordert Telefonnummer oben. Standardtarife können anfallen.",
    registering: "Anmeldung läuft...",
    confirmRegistration: "Anmeldung Bestätigen",
    
    // Plan page additions
    scheduleYourConferences: "Planen Sie Ihre Konferenzen",
    youHaveMeetingsToSchedule: "Sie haben {count} {meeting} zu planen.",
    meeting: "Besprechung",
    meetings: "Besprechungen",
    continue: "Weiter",
    continueButton: "weiter →",
    yourTeachers: "Ihre Lehrer",
    selectTimesManually: "Zeiten Manuell Auswählen",
    suggestedSchedule: "Vorgeschlagener Zeitplan",
    starts: "Beginnt:",
    totalTime: "Gesamtzeit:",
    tapContinue: "Tippen Sie auf \"Weiter\"",
    howToUsePlanner: "So Verwenden Sie Den Zeitplaner",
    reviewAndConfirm: "Überprüfen und Bestätigen",
    nextBest: "Nächstbester",
    
    poweredBy: "Bereitgestellt von",
    on: "am",
    conferenceNotAvailable: "Konferenz Nicht Verfügbar",
    startFresh: "Neu Beginnen",
    yourPrivacyMatters: "Ihre Privatsphäre ist Wichtig",
    privacyInfoUsage: "Wir verwenden Ihre Informationen ausschließlich zur Planung Ihrer Konferenztermine.",
    privacyNoSelling: "Wir verkaufen Ihre Daten niemals.",
    viewPrivacyPolicy: "Datenschutzrichtlinie Anzeigen",
    autoSuggestWarning: "Die Auto-Vorschlag-Funktion ersetzt Ihre aktuellen Zeitauswahlen. Möchten Sie fortfahren? Dies löscht Ihre aktuellen Auswahlen.",
    interpreterNeeded: "Ich brauche einen Dolmetscher",
    selectInterpreterLanguage: "Sprache auswählen",
    allSlotsFull: "Alle Termine belegt",
    joinedWaitlistSuccess: "Sie wurden zur Warteliste hinzugefügt! Wir benachrichtigen Sie, wenn ein Platz frei wird.",
    stepYourInfo: "Ihre<br />Daten",
    stepSelectTeachers: "Lehrer<br />wählen",
    stepChooseTimes: "Zeiten<br />wählen",
    stepConfirmMeetings: "Termine<br />bestätigen",
    draftModeMessage: "Diese Konferenz befindet sich derzeit im Entwurfsmodus und ist noch nicht für die öffentliche Registrierung verfügbar. Bitte schauen Sie später wieder vorbei oder kontaktieren Sie die Schule für weitere Informationen.",
    draftModeAdminNote: "Die Konferenz wird sichtbar, sobald sie vom Schuladministrator veröffentlicht wird.",
    tryAutoSuggest: "Auto-Vorschlag testen!",
    autoSuggestDescription: "Findet sofort den besten aufeinanderfolgenden Zeitplan.",
    dismiss: "Schließen",
    manageSchedule: "Zeitplan Verwalten",
    findYourSchedule: "Finden Sie Ihren Zeitplan",
    enterEmailToFindSchedule: "Geben Sie die E-Mail-Adresse ein, die Sie für die Buchung verwendet haben.",
    emailAddressLabel: "E-Mail-Adresse",
    sendVerificationCode: "Bestätigungscode Senden",
    checkYourEmail: "Überprüfen Sie Ihre E-Mail",
    weSentCodeTo: "Wir haben einen 6-stelligen Code gesendet an",
    verificationCode: "Bestätigungscode",
    verifyAndViewSchedule: "Verifizieren und Zeitplan Anzeigen",
    useDifferentEmail: "Andere E-Mail verwenden",
    viewingScheduleFor: "Zeitplan für",
    noUpcomingAppointments: "Keine anstehenden Termine gefunden.",
    bookAnAppointment: "Termin Buchen",
    studentLabel: "Schüler:",
    rescheduleAppointment: "Termin Verschieben",
    cancelAppointment: "Termin Stornieren",
    downloadCalendar: "Kalender Herunterladen (.ics)",
    bookMoreAppointments: "+ Weitere Termine Buchen",
    reschedule: "Verschieben",
    currentAppointment: "Aktueller Termin",
    selectNewTime: "Neue Zeit Auswählen",
    loadingAvailableTimes: "Verfügbare Zeiten werden geladen...",
    noOtherTimesAvailable: "Keine anderen Zeiten verfügbar.",
    allSlotsFullOrPast: "Alle Termine sind voll oder vergangen.",
    confirmReschedule: "Verschiebung Bestätigen",
    cancelConfirmation: "Sind Sie sicher, dass Sie diesen Termin stornieren möchten?",
  },
  
  zh: {
    bookingConfirmedTitle: "预约已确认！",
    successfullyScheduled: "您已成功预约以下教师",
    teachers: "位教师",
    confirmationEmailSent: "确认邮件已发送至",
    addToCalendar: "添加到日历",
    done: "完成",
    selectedTeachers: "已选教师",
    dragToReorder: "拖动可调整顺序",
    noTeachersSelected: "尚未选择教师。",
    goBackToAdd: "返回添加",
    youHaveTeachersToMeet: "您有 {count} 位教师需要会面。",
    desiredStartTime: "期望开始时间",
    autoSuggestSchedule: "自动推荐时间安排",
    bestForBackToBack: "适合连续安排的时段",
    or: "或",
    selectManually: "手动选择",
    stepOf: "第 {current} 步，共 {total} 步",
    percentComplete: "已完成 {percent}%",
    availableTimes: "可预约时间",
    noAvailableTimes: "该教师暂无可预约时间。",
    time: "时间",
    howToUse: "使用说明",
    tipClickSlots: "点击时段即可为每位教师选择预约，已选时段会高亮显示。",
    tipAutoSuggest: "右上角的“自动推荐”会为您找到最佳的连续时间安排。",
    tipDragTeachers: "在侧边栏拖动教师即可调整顺序，这会影响推荐的排序。",
    tipGrayedSlots: "灰色时段不可选，因为您已将该时间安排给了另一位教师。",
    tipEnterStudentNames: "在每位教师所在列的上方填写学生姓名，以指明该次会面对应哪个孩子。",
    foundOptimalSlots: "已找到 {count} 个最佳时段！",
    suggestionsOnly: "以下仅为建议。请核对下方安排，确认无误后点击“确认”完成预约。",
    couldNotFindSchedule: "未能找到合适的时间安排。请调整开始时间或减少所选教师。",
    pleaseFillIn: "请填写以下内容：",
    validEmailRequired: "请输入有效的电子邮箱地址。",
    parentInformation: "家长信息",
    enterYourInfoAndChildren: "请填写您的联系方式并添加您的孩子，以便预约会面。",
    children: "孩子",
    addChild: "添加孩子",
    addUpToTwoChildren: "您一次最多可为 2 个孩子预约。",
    addFirstChild: "添加第一个孩子",
    childName: "孩子姓名",
    continueToSelectTeachers: "继续选择教师",
    forChild: "对应：",
    selectChild: "选择孩子...",
    for: "对应",
    pleaseEnterName: "请输入您的姓名",
    pleaseEnterValidEmail: "请输入有效的电子邮箱地址",
    pleaseEnterValidPhone: "请输入有效的电话号码",
    pleaseAddAtLeastOneChild: "请至少添加一个孩子",
    pleaseEnterChildNames: "请填写所有孩子的姓名",
    pleaseAssignChildToAllTeachers: "请为所有已选教师指定对应的孩子",
    enterYourName: "请输入您的全名",
    enterYourEmail: "请输入您的电子邮箱地址",
    childOne: "孩子 1",
    childTwo: "孩子 2",
    // Common
    bookSlot: "预约",
    cancel: "取消",
    confirmBooking: "确认预约",
    yourName: "您的姓名",
    email: "电子邮件",
    phone: "电话",
    signUp: "报名",
    full: "已满",
    teacher: "老师",
    location: "地点",
    backToSchool: "返回学校会议",
    required: "(必填)",
    optional: "(选填)",
    notifications: "通知",
    
    // School Page
    selectTeachers: "选择老师",
    searchTeachers: "搜索老师...",
    sortBy: "排序方式",
    lastName: "姓氏",
    grade: "年级",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "已选择老师",
    teachersSelected: "已选择老师",
    findTimes: "查找时间",
    clear: "清除",
    select: "选择",
    selected: "已选择",
    add: "添加",
    added: "已添加",
    availableSlots: "可用时段",
    teamOf: "团队",
    selectAllTeam: "选择此团队的所有老师？",
    yes: "是",
    no: "否",
    allTeachers: "所有老师",
    showAllTeachers: "显示所有老师",
    chooseTimes: "选择时间",
    pickAvailableSlots: "选择可用时段",
    confirm: "确认",
    confirmMeetings: "确认会议",
    getConfirmation: "获取确认",
    
    // Booking Limit
    bookingLimitTitle: "预约限制",
    maxSlotsAllowed: "最多",
    slotsPerParent: "个家长会议名额。",
    enterEmailToSeeCount: "注册时输入邮箱以查看当前预约情况。",
    youHaveBooked: "您已预约",
    of: "/",
    allowedSlots: "个允许的会议名额",
    toBookMore: "如需预约更多，请先取消现有预约。",
    youCanBook: "您还可以预约",
    moreSlots: "个名额",
    viewMySchedule: "查看和管理我的日程 →",
    
    // Plan Page
    planYourSchedule: "安排您的日程",
    selectTimeSlots: "为每位老师选择时间段",
    autoSuggest: "自动建议",
    autoSuggestDesc: "让我们为您找到最佳时间",
    confirmAppointments: "确认预约",
    appointment: "预约",
    appointments: "预约",
    noTimeSelected: "未选择时间",
    childsName: "孩子的名字",
    enterChildName: "输入您孩子的名字",
    yourDetails: "您的信息",
    parentName: "家长姓名",
    yourFullName: "您的全名",
    yourEmail: "您的邮箱@示例.com",
    yourPhone: "您的电话号码",
    review: "审核",
    back: "返回",
    nextTeacher: "下一位老师",
    yourSchedule: "您的日程",
    tapToChange: "点击更改",
    confirmNow: "立即确认",
    confirming: "确认中...",
    
    // Booking Form
    bookingFor: "预约",
    selectSlot: "选择时间段",
    joinWaitlist: "加入候补名单",
    booked: "已预约",
    waitlisted: "已加入候补",
    notifyMe: "通过以下方式通知我：",
    remindMeVia: "提醒方式",
    emailNotify: "邮件",
    smsNotify: "短信",
    bothNotify: "两者都要",
    
    // Confirmation
    bookingConfirmed: "预约已确认！",
    addedToWaitlist: "已加入候补名单",
    confirmationSent: "确认信息已发送到您的邮箱。",
    waitlistInfo: "如有空位，我们会通知您。",
    viewSchedule: "查看我的日程",
    bookAnother: "再次预约",
    
    // Schedule Lookup
    mySchedule: "我的日程",
    enterEmail: "输入您的邮箱查看预约",
    lookupSchedule: "查询日程",
    noAppointments: "未找到预约",
    tryDifferentEmail: "尝试其他邮箱地址",
    
    // Dates & Times
    today: "今天",
    tomorrow: "明天",
    am: "上午",
    pm: "下午",
    
    // Errors
    errorOccurred: "发生错误",
    pleaseRetry: "请重试",
    requiredField: "此字段为必填项",
    invalidEmail: "请输入有效的邮箱",
    
    // Registration locked
    registrationOpens: "注册开放时间",
    registrationClosed: "注册已关闭",
    comingSoon: "即将推出",
    
    // Additional UI elements
    teacherPortal: "教师门户",
    schoolConferences: "学校会议",
    needToMakeChanges: "需要修改您的日程吗？",
    nameAZ: "姓名 (A-Z)",
    nameZA: "姓名 (Z-A)",
    gradeAsc: "年级 (升序)",
    gradeDesc: "年级 (降序)",
    noConferencesFound: "未找到会议",
    generalEvents: "一般活动",
    generalEvent: "一般活动",
    general: "一般",
    signup: "报名",
    appointmentsLabel: "预约",
    virtual: "在线",
    groupedTeachersTooltip: "分组教师 - 选择一位将选择全部",
    teachersAvailable: "位老师可选",
    clickHere: "点击这里",
    inCategory: "在",
    noTeachersInCategory: "未找到该类别的老师",
    
    // Booking page
    registrationOpensSoon: "注册即将开放",
    opensIn: "开放倒计时:",
    unitDays: "天",
    unitHours: "小时",
    unitMinutes: "分钟",
    unitSeconds: "秒",
    pageUnlocksAutomatically: "注册开放时此页面将自动解锁。",
    eventInformation: "活动信息",
    dateTBA: "日期待定",
    virtualMeeting: "在线会议",
    selectTimeSlot: "选择时间段",
    choosePreferredSlot: "请选择您喜欢的时间段。",
    noSlotsAvailable: "没有可用时段。",
    selectedTime: "已选择:",
    waitlistOnly: "(仅限候补)",
    completeFormBelow: "请完成下方表格。",
    slotJustTaken: "抱歉！该时段刚被预约",
    slotTakenMessage: "另一位家长刚刚预约了此时段。请刷新查看最新可用时间并选择其他时段。",
    refreshAvailability: "刷新可用时间",
    registerForEvent: "注册此活动",
    selectSlotFirst: "请先选择上方的时间段。",
    proceedToRegistration: "点击继续完成您的注册。",
    changeTime: "更改时间",
    yourAppointment: "您的预约",
    completeFormToConfirm: "完成表格以确认您的预约。",
    fullName: "全名",
    parentNamePlaceholder: "家长姓名",
    emailAddress: "电子邮箱",
    emailPlaceholder: "邮箱@示例.com",
    emailOptionalNote: "可选 - 提供以接收邮件提醒",
    studentName: "学生姓名",
    studentNamePlaceholder: "学生姓名",
    phoneNumber: "电话号码",
    phonePlaceholder: "138-0000-0000",
    phoneOptionalNote: "可选 - 提供以接收短信提醒",
    commentsOptional: "备注或特殊要求（可选）",
    commentsPlaceholder: "任何特殊需求、问题或想与老师分享的信息...",
    needInterpreter: "我需要翻译",
    interpreterLanguagePlaceholder: "语言（如：英语、手语）",
    reminderNotifications: "提醒通知（可选）",
    reminderNotificationsDesc: "选择您希望如何接收预约提醒。您必须提供所选方式的联系信息。",
    emailReminders: "邮件提醒",
    emailRemindersDesc: "接收邮件预约提醒。需要提供上方的电子邮箱。",
    smsReminders: "短信提醒",
    smsRemindersDesc: "接收短信提醒。需要提供上方的电话号码。可能产生标准短信费用。",
    registering: "注册中...",
    confirmRegistration: "确认注册",
    
    // Plan page additions
    scheduleYourConferences: "安排您的会议",
    youHaveMeetingsToSchedule: "您有 {count} 个{meeting}需要安排。",
    meeting: "会议",
    meetings: "会议",
    continue: "继续",
    continueButton: "继续 →",
    yourTeachers: "您的老师",
    selectTimesManually: "手动选择时间",
    suggestedSchedule: "建议时间表",
    starts: "开始时间：",
    totalTime: "总时间：",
    tapContinue: "点击\"继续\"",
    howToUsePlanner: "如何使用时间表规划器",
    reviewAndConfirm: "审查并确认",
    nextBest: "次佳选择",
    
    poweredBy: "技术支持",
    on: "",
    conferenceNotAvailable: "会议暂不可用",
    startFresh: "重新开始",
    yourPrivacyMatters: "您的隐私很重要",
    privacyInfoUsage: "我们仅使用您的信息来安排您的会议预约。",
    privacyNoSelling: "我们绝不出售您的数据。",
    viewPrivacyPolicy: "查看隐私政策",
    autoSuggestWarning: "自动建议将替换您当前的时间选择。是否继续？这将清除您当前的选择。",
    interpreterNeeded: "我需要口译员",
    selectInterpreterLanguage: "选择语言",
    allSlotsFull: "所有时段已满",
    joinedWaitlistSuccess: "您已加入候补名单！如有空位，我们会通知您。",
    stepYourInfo: "您的<br />信息",
    stepSelectTeachers: "选择<br />教师",
    stepChooseTimes: "选择<br />时间",
    stepConfirmMeetings: "确认<br />会议",
    draftModeMessage: "此会议目前处于草稿模式，尚未开放公开注册。请稍后再查看或联系学校了解更多信息。",
    draftModeAdminNote: "会议将在学校管理员发布后可见。",
    tryAutoSuggest: "试试自动推荐！",
    autoSuggestDescription: "即时找到最佳连续时间表。",
    dismiss: "关闭",
    manageSchedule: "管理日程",
    findYourSchedule: "查找您的日程",
    enterEmailToFindSchedule: "输入您预约时使用的电子邮件地址。",
    emailAddressLabel: "电子邮件地址",
    sendVerificationCode: "发送验证码",
    checkYourEmail: "检查您的邮箱",
    weSentCodeTo: "我们已向以下地址发送了6位验证码",
    verificationCode: "验证码",
    verifyAndViewSchedule: "验证并查看日程",
    useDifferentEmail: "使用其他邮箱",
    viewingScheduleFor: "正在查看日程",
    noUpcomingAppointments: "未找到即将到来的预约。",
    bookAnAppointment: "预约",
    studentLabel: "学生：",
    rescheduleAppointment: "重新安排预约",
    cancelAppointment: "取消预约",
    downloadCalendar: "下载日历 (.ics)",
    bookMoreAppointments: "+ 预约更多",
    reschedule: "重新安排",
    currentAppointment: "当前预约",
    selectNewTime: "选择新时间",
    loadingAvailableTimes: "正在加载可用时间...",
    noOtherTimesAvailable: "没有其他可用时间。",
    allSlotsFullOrPast: "所有时段已满或已过。",
    confirmReschedule: "确认重新安排",
    cancelConfirmation: "您确定要取消此预约吗？",
  },
  
  ja: {
    bookingConfirmedTitle: "予約が確定しました！",
    successfullyScheduled: "次の先生との面談を予約しました",
    teachers: "名の先生",
    confirmationEmailSent: "確認メールを送信しました:",
    addToCalendar: "カレンダーに追加",
    done: "完了",
    selectedTeachers: "選択した先生",
    dragToReorder: "ドラッグして並べ替え",
    noTeachersSelected: "先生が選択されていません。",
    goBackToAdd: "戻って追加",
    youHaveTeachersToMeet: "面談する先生が {count} 名います。",
    desiredStartTime: "希望開始時刻",
    autoSuggestSchedule: "スケジュールを自動提案",
    bestForBackToBack: "連続した時間帯におすすめ",
    or: "または",
    selectManually: "手動で選択",
    stepOf: "ステップ {current} / {total}",
    percentComplete: "{percent}% 完了",
    availableTimes: "予約可能な時間",
    noAvailableTimes: "この先生に空き時間はありません。",
    time: "時刻",
    howToUse: "使い方",
    tipClickSlots: "時間帯をクリックすると、各先生との面談を選択できます。選択した時間帯は強調表示されます。",
    tipAutoSuggest: "右上の「自動提案」を使うと、最適な連続スケジュールが見つかります。",
    tipDragTeachers: "サイドバーで先生をドラッグすると並べ替えられます。これは提案される順序に影響します。",
    tipGrayedSlots: "グレー表示の時間帯は、別の先生で同じ時刻を選択済みのため選べません。",
    tipEnterStudentNames: "各先生の列の上に生徒名を入力し、どのお子さまの面談かを指定してください。",
    foundOptimalSlots: "最適な時間帯が {count} 件見つかりました！",
    suggestionsOnly: "これは提案です。下のスケジュールを確認し、よろしければ「確定」をクリックしてください。",
    couldNotFindSchedule: "有効なスケジュールが見つかりませんでした。開始時刻を変更するか、先生の人数を減らしてください。",
    pleaseFillIn: "次の項目を入力してください:",
    validEmailRequired: "有効なメールアドレスを入力してください。",
    parentInformation: "保護者の情報",
    enterYourInfoAndChildren: "連絡先を入力し、面談を予約するお子さまを追加してください。",
    children: "お子さま",
    addChild: "お子さまを追加",
    addUpToTwoChildren: "一度に 2 名までのお子さまの面談を予約できます。",
    addFirstChild: "最初のお子さまを追加",
    childName: "お子さまの名前",
    continueToSelectTeachers: "先生の選択に進む",
    forChild: "対象:",
    selectChild: "お子さまを選択...",
    for: "対象",
    pleaseEnterName: "お名前を入力してください",
    pleaseEnterValidEmail: "有効なメールアドレスを入力してください",
    pleaseEnterValidPhone: "有効な電話番号を入力してください",
    pleaseAddAtLeastOneChild: "お子さまを 1 名以上追加してください",
    pleaseEnterChildNames: "すべてのお子さまの名前を入力してください",
    pleaseAssignChildToAllTeachers: "選択したすべての先生にお子さまを割り当ててください",
    enterYourName: "氏名を入力してください",
    enterYourEmail: "メールアドレスを入力してください",
    childOne: "お子さま 1",
    childTwo: "お子さま 2",
    // Common
    bookSlot: "予約する",
    cancel: "キャンセル",
    confirmBooking: "予約を確認",
    yourName: "お名前",
    email: "メール",
    phone: "電話番号",
    signUp: "申し込む",
    full: "満席",
    teacher: "先生",
    location: "場所",
    backToSchool: "学校会議に戻る",
    required: "(必須)",
    optional: "(任意)",
    notifications: "通知",
    
    // School Page
    selectTeachers: "先生を選択",
    searchTeachers: "先生を検索...",
    sortBy: "並び替え",
    lastName: "姓",
    grade: "学年",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "先生を選択済み",
    teachersSelected: "先生を選択済み",
    findTimes: "時間を探す",
    clear: "クリア",
    select: "選択",
    selected: "選択済み",
    add: "追加",
    added: "追加済み",
    availableSlots: "空き枠",
    teamOf: "チーム",
    selectAllTeam: "このチームの全ての先生を選択しますか？",
    yes: "はい",
    no: "いいえ",
    allTeachers: "全ての先生",
    showAllTeachers: "全ての先生を表示",
    chooseTimes: "時間を選択",
    pickAvailableSlots: "空き枠を選択",
    confirm: "確認",
    confirmMeetings: "会議を確認",
    getConfirmation: "確認を受け取る",
    
    // Booking Limit
    bookingLimitTitle: "予約制限",
    maxSlotsAllowed: "最大",
    slotsPerParent: "件の保護者面談を予約できます。",
    enterEmailToSeeCount: "登録時にメールアドレスを入力すると、現在の予約状況を確認できます。",
    youHaveBooked: "予約済み",
    of: "/",
    allowedSlots: "件の許可された面談枠",
    toBookMore: "追加予約するには、まず既存の予約をキャンセルしてください。",
    youCanBook: "あと",
    moreSlots: "件予約可能",
    viewMySchedule: "スケジュールを表示・管理 →",
    
    // Plan Page
    planYourSchedule: "スケジュールを計画",
    selectTimeSlots: "各先生の時間枠を選択",
    autoSuggest: "自動提案",
    autoSuggestDesc: "最適な時間を見つけます",
    confirmAppointments: "予約を確認",
    appointment: "予約",
    appointments: "予約",
    noTimeSelected: "時間未選択",
    childsName: "お子様の名前",
    enterChildName: "お子様の名前を入力",
    yourDetails: "ご連絡先",
    parentName: "保護者名",
    yourFullName: "フルネーム",
    yourEmail: "メールアドレス",
    yourPhone: "電話番号",
    review: "確認",
    back: "戻る",
    nextTeacher: "次の先生",
    yourSchedule: "あなたのスケジュール",
    tapToChange: "タップして変更",
    confirmNow: "今すぐ確認",
    confirming: "確認中...",
    
    // Booking Form
    bookingFor: "予約対象",
    selectSlot: "時間枠を選択",
    joinWaitlist: "キャンセル待ちに参加",
    booked: "予約済み",
    waitlisted: "キャンセル待ち",
    notifyMe: "通知方法：",
    remindMeVia: "リマインダー方法",
    emailNotify: "メール",
    smsNotify: "SMS",
    bothNotify: "両方",
    
    // Confirmation
    bookingConfirmed: "予約が確認されました！",
    addedToWaitlist: "キャンセル待ちに追加されました",
    confirmationSent: "確認メールを送信しました。",
    waitlistInfo: "空きが出ましたらお知らせします。",
    viewSchedule: "スケジュールを見る",
    bookAnother: "別の予約",
    
    // Schedule Lookup
    mySchedule: "マイスケジュール",
    enterEmail: "メールアドレスを入力して予約を確認",
    lookupSchedule: "スケジュール検索",
    noAppointments: "予約が見つかりません",
    tryDifferentEmail: "別のメールアドレスをお試しください",
    
    // Dates & Times
    today: "今日",
    tomorrow: "明日",
    am: "午前",
    pm: "午後",
    
    // Errors
    errorOccurred: "エラーが発生しました",
    pleaseRetry: "もう一度お試しください",
    requiredField: "この項目は必須です",
    invalidEmail: "有効なメールアドレスを入力してください",
    
    // Registration locked
    registrationOpens: "登録開始",
    registrationClosed: "登録は終了しました",
    comingSoon: "近日公開",
    
    // Additional UI elements
    teacherPortal: "先生ポータル",
    schoolConferences: "学校会議",
    needToMakeChanges: "スケジュールを変更する必要がありますか？",
    nameAZ: "名前 (A-Z)",
    nameZA: "名前 (Z-A)",
    gradeAsc: "学年 (昇順)",
    gradeDesc: "学年 (降順)",
    noConferencesFound: "会議が見つかりません",
    generalEvents: "一般イベント",
    generalEvent: "一般イベント",
    general: "一般",
    signup: "申し込み",
    appointmentsLabel: "予約",
    virtual: "オンライン",
    groupedTeachersTooltip: "グループの先生 - 一人を選択すると全員が選択されます",
    teachersAvailable: "人の先生が選択可能",
    clickHere: "ここをクリック",
    inCategory: "の",
    noTeachersInCategory: "このカテゴリーに先生がいません",
    
    // Booking page
    registrationOpensSoon: "まもなく登録開始",
    opensIn: "開始まで:",
    unitDays: "日",
    unitHours: "時間",
    unitMinutes: "分",
    unitSeconds: "秒",
    pageUnlocksAutomatically: "登録開始時にこのページは自動的にロック解除されます。",
    eventInformation: "イベント情報",
    dateTBA: "日程未定",
    virtualMeeting: "オンラインミーティング",
    selectTimeSlot: "時間帯を選択",
    choosePreferredSlot: "ご希望の時間帯を選択してください。",
    noSlotsAvailable: "空き枠がありません。",
    selectedTime: "選択済み:",
    waitlistOnly: "(キャンセル待ちのみ)",
    completeFormBelow: "下のフォームを記入してください。",
    slotJustTaken: "申し訳ありません！その時間帯は予約されました",
    slotTakenMessage: "別の保護者様が直前にこの時間帯を予約されました。更新して最新の空き状況を確認し、別の時間帯をお選びください。",
    refreshAvailability: "空き状況を更新",
    registerForEvent: "このイベントに登録",
    selectSlotFirst: "まず上の時間帯を選択してください。",
    proceedToRegistration: "続けるをクリックして登録を完了してください。",
    changeTime: "時間を変更",
    yourAppointment: "ご予約",
    completeFormToConfirm: "フォームを記入して予約を確定してください。",
    fullName: "氏名",
    parentNamePlaceholder: "保護者名",
    emailAddress: "メールアドレス",
    emailPlaceholder: "メール@例.com",
    emailOptionalNote: "任意 - メールリマインダーを受け取る場合は入力",
    studentName: "生徒名",
    studentNamePlaceholder: "生徒名",
    phoneNumber: "電話番号",
    phonePlaceholder: "090-0000-0000",
    phoneOptionalNote: "任意 - SMSリマインダーを受け取る場合は入力",
    commentsOptional: "コメント・特別なご要望（任意）",
    commentsPlaceholder: "特別なニーズ、質問、先生にお伝えしたい情報など...",
    needInterpreter: "通訳が必要です",
    interpreterLanguagePlaceholder: "言語（例：英語、手話）",
    reminderNotifications: "リマインダー通知（任意）",
    reminderNotificationsDesc: "リマインダーの受け取り方法を選択してください。選択した方法の連絡先情報が必要です。",
    emailReminders: "メールリマインダー",
    emailRemindersDesc: "予約のメールリマインダーを受け取ります。上のメールアドレスが必要です。",
    smsReminders: "SMSリマインダー",
    smsRemindersDesc: "SMSリマインダーを受け取ります。上の電話番号が必要です。通常の通信料がかかる場合があります。",
    registering: "登録中...",
    confirmRegistration: "登録を確定",
    
    // Plan page additions
    scheduleYourConferences: "会議をスケジュール",
    youHaveMeetingsToSchedule: "{count}つの{meeting}をスケジュールする必要があります。",
    meeting: "会議",
    meetings: "会議",
    continue: "続ける",
    continueButton: "続ける →",
    yourTeachers: "あなたの先生",
    selectTimesManually: "時間を手動で選択",
    suggestedSchedule: "推奨スケジュール",
    starts: "開始：",
    totalTime: "合計時間：",
    tapContinue: "\"続ける\"をタップ",
    howToUsePlanner: "スケジュールプランナーの使い方",
    reviewAndConfirm: "確認して確認",
    nextBest: "次に最適",
    
    poweredBy: "提供",
    on: "",
    conferenceNotAvailable: "カンファレンスは利用できません",
    startFresh: "最初からやり直す",
    yourPrivacyMatters: "プライバシーは重要です",
    privacyInfoUsage: "お客様の情報は、カンファレンスの予約スケジュールにのみ使用します。",
    privacyNoSelling: "お客様のデータを販売することはありません。",
    viewPrivacyPolicy: "プライバシーポリシーを見る",
    autoSuggestWarning: "自動提案は現在の時間選択を置き換えます。続行しますか？現在の選択はクリアされます。",
    interpreterNeeded: "通訳が必要です",
    selectInterpreterLanguage: "言語を選択",
    allSlotsFull: "全枠満席",
    joinedWaitlistSuccess: "キャンセル待ちに追加されました！空きが出ましたらお知らせします。",
    stepYourInfo: "あなたの<br />情報",
    stepSelectTeachers: "教師を<br />選択",
    stepChooseTimes: "時間を<br />選択",
    stepConfirmMeetings: "面談を<br />確認",
    draftModeMessage: "この会議は現在下書きモードであり、まだ公開登録できません。後でもう一度ご確認いただくか、学校にお問い合わせください。",
    draftModeAdminNote: "会議は学校管理者が公開すると表示されます。",
    tryAutoSuggest: "自動提案を試す！",
    autoSuggestDescription: "最適な連続スケジュールを瞬時に見つけます。",
    dismiss: "閉じる",
    manageSchedule: "スケジュール管理",
    findYourSchedule: "スケジュールを検索",
    enterEmailToFindSchedule: "予約時に使用したメールアドレスを入力してください。",
    emailAddressLabel: "メールアドレス",
    sendVerificationCode: "認証コードを送信",
    checkYourEmail: "メールを確認してください",
    weSentCodeTo: "6桁の認証コードを送信しました",
    verificationCode: "認証コード",
    verifyAndViewSchedule: "認証してスケジュールを表示",
    useDifferentEmail: "別のメールアドレスを使用",
    viewingScheduleFor: "スケジュールを表示中",
    noUpcomingAppointments: "今後の予約はありません。",
    bookAnAppointment: "予約する",
    studentLabel: "生徒：",
    rescheduleAppointment: "予約を変更",
    cancelAppointment: "予約をキャンセル",
    downloadCalendar: "カレンダーをダウンロード (.ics)",
    bookMoreAppointments: "+ 追加予約",
    reschedule: "変更",
    currentAppointment: "現在の予約",
    selectNewTime: "新しい時間を選択",
    loadingAvailableTimes: "利用可能な時間を読み込み中...",
    noOtherTimesAvailable: "他に利用可能な時間はありません。",
    allSlotsFullOrPast: "すべての枠が満員または終了しています。",
    confirmReschedule: "変更を確認",
    cancelConfirmation: "この予約をキャンセルしてよろしいですか？",
  },
  
  ar: {
    bookingConfirmedTitle: "تم تأكيد الحجز!",
    successfullyScheduled: "لقد حجزت مواعيد بنجاح مع",
    teachers: "معلمين",
    confirmationEmailSent: "تم إرسال رسالة تأكيد إلى",
    addToCalendar: "إضافة إلى التقويم",
    done: "تم",
    selectedTeachers: "المعلمون المختارون",
    dragToReorder: "اسحب لإعادة الترتيب",
    noTeachersSelected: "لم يتم اختيار أي معلم.",
    goBackToAdd: "ارجع للإضافة",
    youHaveTeachersToMeet: "لديك {count} معلمين لمقابلتهم.",
    desiredStartTime: "وقت البدء المطلوب",
    autoSuggestSchedule: "اقتراح جدول تلقائي",
    bestForBackToBack: "مناسب للمواعيد المتتالية",
    or: "أو",
    selectManually: "الاختيار يدويًا",
    stepOf: "الخطوة {current} من {total}",
    percentComplete: "اكتمل {percent}%",
    availableTimes: "الأوقات المتاحة",
    noAvailableTimes: "لا توجد أوقات متاحة لهذا المعلم.",
    time: "الوقت",
    howToUse: "طريقة الاستخدام",
    tipClickSlots: "انقر على الأوقات لاختيار موعد مع كل معلم. تظهر الأوقات المختارة مميزة.",
    tipAutoSuggest: "يعثر الاقتراح التلقائي (أعلى اليمين) على أفضل جدول متتالٍ.",
    tipDragTeachers: "اسحب المعلمين في الشريط الجانبي لإعادة ترتيبهم، وهذا يؤثر على الترتيب المقترح.",
    tipGrayedSlots: "الأوقات الرمادية غير متاحة لأنك اخترت ذلك الوقت مع معلم آخر.",
    tipEnterStudentNames: "اكتب اسم الطالب أعلى عمود كل معلم لتحديد الطفل المعني بكل لقاء.",
    foundOptimalSlots: "تم العثور على {count} أوقات مثالية!",
    suggestionsOnly: "هذه اقتراحات فقط. راجع الجدول أدناه ثم انقر على \"تأكيد\" عندما تكون جاهزًا للحجز.",
    couldNotFindSchedule: "تعذر إيجاد جدول مناسب. جرّب تعديل وقت البدء أو اختيار عدد أقل من المعلمين.",
    pleaseFillIn: "يرجى تعبئة ما يلي:",
    validEmailRequired: "يرجى إدخال بريد إلكتروني صالح.",
    // Common
    bookSlot: "حجز موعد",
    cancel: "إلغاء",
    confirmBooking: "تأكيد الحجز",
    yourName: "اسمك",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    signUp: "تسجيل",
    full: "ممتلئ",
    teacher: "المعلم",
    location: "الموقع",
    backToSchool: "العودة إلى المؤتمرات",
    required: "(مطلوب)",
    optional: "(اختياري)",
    notifications: "إشعارات",
    
    // School Page
    selectTeachers: "اختر المعلمين",
    searchTeachers: "البحث عن معلمين...",
    sortBy: "ترتيب حسب",
    lastName: "اسم العائلة",
    grade: "الصف",
    ascending: "أ-ي",
    descending: "ي-أ",
    teacherSelected: "تم اختيار المعلم",
    teachersSelected: "تم اختيار المعلمين",
    findTimes: "البحث عن أوقات",
    clear: "مسح",
    select: "اختيار",
    selected: "تم الاختيار",
    add: "إضافة",
    added: "تمت الإضافة",
    availableSlots: "أوقات متاحة",
    teamOf: "فريق من",
    selectAllTeam: "اختيار جميع معلمي هذا الفريق؟",
    yes: "نعم",
    no: "لا",
    allTeachers: "جميع المعلمين",
    showAllTeachers: "عرض جميع المعلمين",
    chooseTimes: "اختر الأوقات",
    pickAvailableSlots: "اختر الأوقات المتاحة",
    confirm: "تأكيد",
    confirmMeetings: "تأكيد الاجتماعات",
    getConfirmation: "الحصول على تأكيد",
    
    // Booking Limit
    bookingLimitTitle: "حد الحجز",
    maxSlotsAllowed: "الحد الأقصى",
    slotsPerParent: "مؤتمرات لكل ولي أمر لهذا الحدث.",
    enterEmailToSeeCount: "أدخل بريدك الإلكتروني أثناء التسجيل لرؤية حجوزاتك الحالية.",
    youHaveBooked: "لقد حجزت",
    of: "من",
    allowedSlots: "من مواعيد المؤتمرات المسموحة",
    toBookMore: "لحجز المزيد، يرجى إلغاء موعد موجود أولاً.",
    youCanBook: "يمكنك حجز",
    moreSlots: "موعد/مواعيد إضافية",
    viewMySchedule: "عرض وإدارة جدولي ←",
    
    // Plan Page
    planYourSchedule: "خطط جدولك",
    selectTimeSlots: "اختر أوقات لكل معلم",
    autoSuggest: "اقتراح تلقائي",
    autoSuggestDesc: "دعنا نجد أفضل الأوقات لك",
    confirmAppointments: "تأكيد المواعيد",
    appointment: "موعد",
    appointments: "مواعيد",
    noTimeSelected: "لم يتم اختيار وقت",
    childsName: "اسم الطفل",
    enterChildName: "أدخل اسم طفلك",
    yourDetails: "بياناتك",
    parentName: "اسم ولي الأمر",
    yourFullName: "اسمك الكامل",
    yourEmail: "بريدك@مثال.com",
    yourPhone: "رقم هاتفك",
    review: "مراجعة",
    back: "رجوع",
    nextTeacher: "المعلم التالي",
    yourSchedule: "جدولك",
    tapToChange: "انقر للتغيير",
    confirmNow: "تأكيد الآن",
    confirming: "جارٍ التأكيد...",
    
    // Booking Form
    bookingFor: "الحجز لـ",
    selectSlot: "اختر وقتاً",
    joinWaitlist: "الانضمام لقائمة الانتظار",
    booked: "محجوز",
    waitlisted: "في قائمة الانتظار",
    notifyMe: "أبلغني عبر:",
    remindMeVia: "ذكرني عبر",
    emailNotify: "البريد",
    smsNotify: "رسالة نصية",
    bothNotify: "كلاهما",
    
    // Confirmation
    bookingConfirmed: "تم تأكيد الحجز!",
    addedToWaitlist: "تمت الإضافة لقائمة الانتظار",
    confirmationSent: "تم إرسال التأكيد إلى بريدك.",
    waitlistInfo: "سنعلمك إذا توفر مكان.",
    viewSchedule: "عرض جدولي",
    bookAnother: "حجز آخر",
    
    // Schedule Lookup
    mySchedule: "جدولي",
    enterEmail: "أدخل بريدك لعرض مواعيدك",
    lookupSchedule: "البحث عن الجدول",
    noAppointments: "لا توجد مواعيد",
    tryDifferentEmail: "جرب بريداً إلكترونياً آخر",
    
    // Dates & Times
    today: "اليوم",
    tomorrow: "غداً",
    am: "ص",
    pm: "م",
    
    // Errors
    errorOccurred: "حدث خطأ",
    pleaseRetry: "يرجى المحاولة مرة أخرى",
    requiredField: "هذا الحقل مطلوب",
    invalidEmail: "أدخل بريداً إلكترونياً صحيحاً",
    
    // Registration locked
    registrationOpens: "يفتح التسجيل",
    registrationClosed: "التسجيل مغلق",
    comingSoon: "قريباً",
    
    // Additional UI elements
    teacherPortal: "بوابة المعلم",
    schoolConferences: "مؤتمرات المدرسة",
    needToMakeChanges: "هل تحتاج لإجراء تغييرات على جدولك؟",
    nameAZ: "الاسم (أ-ي)",
    nameZA: "الاسم (ي-أ)",
    gradeAsc: "الصف (تصاعدي)",
    gradeDesc: "الصف (تنازلي)",
    noConferencesFound: "لم يتم العثور على مؤتمرات",
    generalEvents: "أحداث عامة",
    generalEvent: "حدث عام",
    general: "عام",
    signup: "تسجيل",
    appointmentsLabel: "مواعيد",
    virtual: "افتراضي",
    groupedTeachersTooltip: "معلمون مجمعون - اختيار واحد يختار الجميع",
    teachersAvailable: "معلم متاح",
    clickHere: "انقر هنا",
    inCategory: "في",
    noTeachersInCategory: "لم يتم العثور على معلمين في",
    
    // Booking page
    registrationOpensSoon: "التسجيل يفتح قريباً",
    opensIn: "يفتح في:",
    unitDays: "أيام",
    unitHours: "ساعات",
    unitMinutes: "دقائق",
    unitSeconds: "ثوانٍ",
    pageUnlocksAutomatically: "ستُفتح هذه الصفحة تلقائياً عند بدء التسجيل.",
    eventInformation: "معلومات الحدث",
    dateTBA: "التاريخ سيُحدد لاحقاً",
    virtualMeeting: "اجتماع افتراضي",
    selectTimeSlot: "اختر وقتاً",
    choosePreferredSlot: "اختر الوقت المفضل لديك لهذا الحدث.",
    noSlotsAvailable: "لا توجد أوقات متاحة.",
    selectedTime: "تم الاختيار:",
    waitlistOnly: "(قائمة الانتظار فقط)",
    completeFormBelow: "أكمل النموذج أدناه.",
    slotJustTaken: "عذراً! تم حجز هذا الوقت للتو",
    slotTakenMessage: "قام ولي أمر آخر بحجز هذا الوقت قبلك مباشرة. يرجى تحديث الصفحة لرؤية الأوقات المتاحة واختيار وقت آخر.",
    refreshAvailability: "تحديث المتاح",
    registerForEvent: "التسجيل في هذا الحدث",
    selectSlotFirst: "يرجى اختيار وقت أعلاه أولاً.",
    proceedToRegistration: "انقر على متابعة لإكمال التسجيل.",
    changeTime: "تغيير الوقت",
    yourAppointment: "موعدك",
    completeFormToConfirm: "أكمل النموذج لتأكيد حجزك.",
    fullName: "الاسم الكامل",
    parentNamePlaceholder: "اسم ولي الأمر",
    emailAddress: "البريد الإلكتروني",
    emailPlaceholder: "بريد@مثال.com",
    emailOptionalNote: "اختياري - قدمه لتلقي تذكيرات بالبريد الإلكتروني",
    studentName: "اسم الطالب",
    studentNamePlaceholder: "اسم الطالب",
    phoneNumber: "رقم الهاتف",
    phonePlaceholder: "05XX XXX XXXX",
    phoneOptionalNote: "اختياري - قدمه لتلقي تذكيرات SMS",
    commentsOptional: "تعليقات أو طلبات خاصة (اختياري)",
    commentsPlaceholder: "أي احتياجات خاصة أو أسئلة أو معلومات تود مشاركتها مع المعلم...",
    needInterpreter: "أحتاج مترجماً",
    interpreterLanguagePlaceholder: "اللغة (مثل: الإنجليزية، لغة الإشارة)",
    reminderNotifications: "إشعارات التذكير (اختياري)",
    reminderNotificationsDesc: "اختر طريقة تلقي التذكيرات. يجب تقديم معلومات الاتصال للطرق المختارة.",
    emailReminders: "تذكيرات بالبريد",
    emailRemindersDesc: "تلقي تذكيرات بالبريد الإلكتروني. يتطلب عنوان بريد إلكتروني أعلاه.",
    smsReminders: "تذكيرات SMS",
    smsRemindersDesc: "تلقي تذكيرات برسائل نصية. يتطلب رقم هاتف أعلاه. قد تطبق رسوم قياسية.",
    registering: "جارٍ التسجيل...",
    confirmRegistration: "تأكيد التسجيل",
    
    // Plan page additions
    scheduleYourConferences: "جدولة مؤتمراتك",
    youHaveMeetingsToSchedule: "لديك {count} {meeting} للجدولة.",
    meeting: "اجتماع",
    meetings: "اجتماعات",
    continue: "متابعة",
    continueButton: "متابعة →",
    yourTeachers: "معلموك",
    selectTimesManually: "اختر الأوقات يدوياً",
    suggestedSchedule: "الجدول المقترح",
    starts: "يبدأ:",
    totalTime: "الوقت الإجمالي:",
    tapContinue: "اضغط على \"متابعة\"",
    howToUsePlanner: "كيفية استخدام مخطط الجدول",
    reviewAndConfirm: "مراجعة وتأكيد",
    nextBest: "التالي الأفضل",
    
    poweredBy: "مدعوم من",
    on: "في",
    
    // Multi-Child Support
    parentInformation: "معلومات الوالد/الوالدة",
    enterYourInfoAndChildren: "أدخل معلومات الاتصال الخاصة بك وأضف أطفالك لجدولة المؤتمرات.",
    children: "الأطفال",
    addChild: "إضافة طفل",
    addUpToTwoChildren: "يمكنك جدولة مؤتمرات لما يصل إلى طفلين في وقت واحد.",
    addFirstChild: "أضف طفلك الأول",
    childName: "اسم الطفل",
    continueToSelectTeachers: "متابعة لاختيار المعلمين",
    forChild: "لـ:",
    selectChild: "اختر طفلاً...",
    for: "لـ",
    pleaseEnterName: "الرجاء إدخال اسمك",
    pleaseEnterValidEmail: "الرجاء إدخال عنوان بريد إلكتروني صالح",
    pleaseEnterValidPhone: "الرجاء إدخال رقم هاتف صالح",
    pleaseAddAtLeastOneChild: "الرجاء إضافة طفل واحد على الأقل",
    pleaseEnterChildNames: "الرجاء إدخال أسماء لجميع الأطفال",
    pleaseAssignChildToAllTeachers: "الرجاء تعيين طفل لجميع المعلمين المختارين",
    enterYourName: "أدخل اسمك الكامل",
    enterYourEmail: "أدخل بريدك الإلكتروني",
    childOne: "الطفل 1",
    childTwo: "الطفل 2",
    conferenceNotAvailable: "المؤتمر غير متاح",
    startFresh: "البدء من جديد",
    yourPrivacyMatters: "خصوصيتك مهمة",
    privacyInfoUsage: "نستخدم معلوماتك فقط لجدولة مواعيد المؤتمر الخاصة بك.",
    privacyNoSelling: "نحن لا نبيع بياناتك أبداً.",
    viewPrivacyPolicy: "عرض سياسة الخصوصية",
    autoSuggestWarning: "سيقوم الاقتراح التلقائي باستبدال اختيارات الوقت الحالية. هل تريد المتابعة؟ سيؤدي هذا إلى مسح اختياراتك الحالية.",
    interpreterNeeded: "أحتاج إلى مترجم",
    selectInterpreterLanguage: "اختر اللغة",
    allSlotsFull: "جميع المواعيد ممتلئة",
    joinedWaitlistSuccess: "تمت إضافتك إلى قائمة الانتظار! سنُعلمك إذا توفر مكان.",
    stepYourInfo: "معلوماتك<br />الشخصية",
    stepSelectTeachers: "اختيار<br />المعلمين",
    stepChooseTimes: "اختيار<br />الأوقات",
    stepConfirmMeetings: "تأكيد<br />الاجتماعات",
    draftModeMessage: "هذا المؤتمر حالياً في وضع المسودة وغير متاح للتسجيل العام. يرجى المراجعة لاحقاً أو الاتصال بالمدرسة لمزيد من المعلومات.",
    draftModeAdminNote: "سيكون المؤتمر مرئياً بمجرد نشره من قبل مدير المدرسة.",
    tryAutoSuggest: "جرب الاقتراح التلقائي!",
    autoSuggestDescription: "يجد أفضل جدول متتالي على الفور.",
    dismiss: "إغلاق",
    manageSchedule: "إدارة الجدول",
    findYourSchedule: "ابحث عن جدولك",
    enterEmailToFindSchedule: "أدخل البريد الإلكتروني الذي استخدمته لحجز مواعيدك.",
    emailAddressLabel: "البريد الإلكتروني",
    sendVerificationCode: "إرسال رمز التحقق",
    checkYourEmail: "تحقق من بريدك الإلكتروني",
    weSentCodeTo: "أرسلنا رمزاً مكوناً من 6 أرقام إلى",
    verificationCode: "رمز التحقق",
    verifyAndViewSchedule: "تحقق واعرض الجدول",
    useDifferentEmail: "استخدم بريداً إلكترونياً آخر",
    viewingScheduleFor: "عرض الجدول لـ",
    noUpcomingAppointments: "لم يتم العثور على مواعيد قادمة.",
    bookAnAppointment: "حجز موعد",
    studentLabel: "الطالب:",
    rescheduleAppointment: "إعادة جدولة الموعد",
    cancelAppointment: "إلغاء الموعد",
    downloadCalendar: "تحميل التقويم (.ics)",
    bookMoreAppointments: "+ حجز المزيد من المواعيد",
    reschedule: "إعادة جدولة",
    currentAppointment: "الموعد الحالي",
    selectNewTime: "اختر وقتاً جديداً",
    loadingAvailableTimes: "جارٍ تحميل الأوقات المتاحة...",
    noOtherTimesAvailable: "لا توجد أوقات أخرى متاحة.",
    allSlotsFullOrPast: "جميع المواعيد ممتلئة أو منتهية.",
    confirmReschedule: "تأكيد إعادة الجدولة",
    cancelConfirmation: "هل أنت متأكد أنك تريد إلغاء هذا الموعد؟",
  },
  
  vi: {
    bookingConfirmedTitle: "Đã xác nhận đặt lịch!",
    successfullyScheduled: "Bạn đã đặt lịch hẹn thành công với",
    teachers: "giáo viên",
    confirmationEmailSent: "Email xác nhận đã được gửi tới",
    addToCalendar: "Thêm vào lịch",
    done: "Xong",
    selectedTeachers: "Giáo viên đã chọn",
    dragToReorder: "Kéo để sắp xếp lại",
    noTeachersSelected: "Chưa chọn giáo viên nào.",
    goBackToAdd: "Quay lại để thêm",
    youHaveTeachersToMeet: "Bạn có {count} giáo viên cần gặp.",
    desiredStartTime: "Thời gian bắt đầu mong muốn",
    autoSuggestSchedule: "Tự động gợi ý lịch",
    bestForBackToBack: "Phù hợp với các khung giờ liên tiếp",
    or: "Hoặc",
    selectManually: "Chọn thủ công",
    stepOf: "Bước {current} trên {total}",
    percentComplete: "Hoàn thành {percent}%",
    availableTimes: "Khung giờ còn trống",
    noAvailableTimes: "Giáo viên này không còn khung giờ trống.",
    time: "Thời gian",
    howToUse: "Cách sử dụng",
    tipClickSlots: "Nhấp vào khung giờ để chọn lịch hẹn với từng giáo viên. Khung giờ đã chọn sẽ được làm nổi bật.",
    tipAutoSuggest: "Tự động gợi ý (góc trên bên phải) sẽ tìm lịch liên tiếp tốt nhất.",
    tipDragTeachers: "Kéo giáo viên trong thanh bên để sắp xếp lại. Việc này ảnh hưởng đến thứ tự được gợi ý.",
    tipGrayedSlots: "Khung giờ màu xám bị khóa vì bạn đã chọn giờ đó cho một giáo viên khác.",
    tipEnterStudentNames: "Nhập tên học sinh phía trên cột của mỗi giáo viên để cho biết buổi gặp dành cho con nào.",
    foundOptimalSlots: "Đã tìm thấy {count} khung giờ tối ưu!",
    suggestionsOnly: "Đây chỉ là gợi ý. Hãy xem lại lịch bên dưới rồi nhấp \"Xác nhận\" khi bạn sẵn sàng đặt lịch.",
    couldNotFindSchedule: "Không tìm được lịch phù hợp. Hãy thử đổi thời gian bắt đầu hoặc chọn ít giáo viên hơn.",
    pleaseFillIn: "Vui lòng điền những mục sau:",
    validEmailRequired: "Vui lòng nhập địa chỉ email hợp lệ.",
    parentInformation: "Thông tin phụ huynh",
    enterYourInfoAndChildren: "Nhập thông tin liên hệ và thêm con của bạn để đặt lịch gặp.",
    children: "Con",
    addChild: "Thêm con",
    addUpToTwoChildren: "Bạn có thể đặt lịch cho tối đa 2 con cùng lúc.",
    addFirstChild: "Thêm con đầu tiên",
    childName: "Tên của con",
    continueToSelectTeachers: "Tiếp tục chọn giáo viên",
    forChild: "Dành cho:",
    selectChild: "Chọn con...",
    for: "Dành cho",
    pleaseEnterName: "Vui lòng nhập tên của bạn",
    pleaseEnterValidEmail: "Vui lòng nhập địa chỉ email hợp lệ",
    pleaseEnterValidPhone: "Vui lòng nhập số điện thoại hợp lệ",
    pleaseAddAtLeastOneChild: "Vui lòng thêm ít nhất một con",
    pleaseEnterChildNames: "Vui lòng nhập tên cho tất cả các con",
    pleaseAssignChildToAllTeachers: "Vui lòng chỉ định con cho tất cả giáo viên đã chọn",
    enterYourName: "Nhập họ và tên của bạn",
    enterYourEmail: "Nhập địa chỉ email của bạn",
    childOne: "Con thứ 1",
    childTwo: "Con thứ 2",
    // Common
    bookSlot: "Đặt Lịch",
    cancel: "Hủy",
    confirmBooking: "Xác Nhận Đặt Lịch",
    yourName: "Tên Của Bạn",
    email: "Email",
    phone: "Điện Thoại",
    signUp: "Đăng Ký",
    full: "Đã Đầy",
    teacher: "Giáo Viên",
    location: "Địa Điểm",
    backToSchool: "Quay Lại Hội Nghị",
    required: "(Bắt buộc)",
    optional: "(Tùy chọn)",
    notifications: "Thông Báo",
    
    // School Page
    selectTeachers: "Chọn Giáo Viên",
    searchTeachers: "Tìm giáo viên...",
    sortBy: "Sắp xếp theo",
    lastName: "Họ",
    grade: "Lớp",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Đã Chọn Giáo Viên",
    teachersSelected: "Đã Chọn Giáo Viên",
    findTimes: "Tìm Thời Gian",
    clear: "Xóa",
    select: "Chọn",
    selected: "Đã Chọn",
    add: "Thêm",
    added: "Đã Thêm",
    availableSlots: "chỗ trống",
    teamOf: "Nhóm",
    selectAllTeam: "Chọn tất cả giáo viên trong nhóm này?",
    yes: "Có",
    no: "Không",
    allTeachers: "Tất Cả Giáo Viên",
    showAllTeachers: "Hiển thị tất cả giáo viên",
    chooseTimes: "Chọn Thời Gian",
    pickAvailableSlots: "Chọn khung giờ trống",
    confirm: "Xác Nhận",
    confirmMeetings: "Xác Nhận Cuộc Họp",
    getConfirmation: "Nhận xác nhận",
    
    // Booking Limit
    bookingLimitTitle: "Giới Hạn Đặt Lịch",
    maxSlotsAllowed: "Tối đa",
    slotsPerParent: "cuộc họp cho mỗi phụ huynh trong sự kiện này.",
    enterEmailToSeeCount: "Nhập email khi đăng ký để xem các lịch đặt hiện tại của bạn.",
    youHaveBooked: "Bạn đã đặt",
    of: "trong số",
    allowedSlots: "lịch họp được phép",
    toBookMore: "Để đặt thêm, vui lòng hủy một cuộc hẹn hiện có trước.",
    youCanBook: "Bạn có thể đặt thêm",
    moreSlots: "lịch nữa",
    viewMySchedule: "Xem & Quản Lý Lịch Trình →",
    
    // Plan Page
    planYourSchedule: "Lên Kế Hoạch Lịch Trình",
    selectTimeSlots: "Chọn khung giờ cho mỗi giáo viên",
    autoSuggest: "Gợi Ý Tự Động",
    autoSuggestDesc: "Để chúng tôi tìm thời gian tốt nhất",
    confirmAppointments: "Xác Nhận Cuộc Hẹn",
    appointment: "Cuộc Hẹn",
    appointments: "Cuộc Hẹn",
    noTimeSelected: "Chưa chọn thời gian",
    childsName: "Tên Con",
    enterChildName: "Nhập tên con của bạn",
    yourDetails: "Thông Tin Của Bạn",
    parentName: "Tên Phụ Huynh",
    yourFullName: "Họ và tên đầy đủ",
    yourEmail: "email@vidu.com",
    yourPhone: "Số điện thoại của bạn",
    review: "Xem Lại",
    back: "Quay Lại",
    nextTeacher: "Giáo Viên Tiếp",
    yourSchedule: "Lịch Trình Của Bạn",
    tapToChange: "Nhấn để thay đổi",
    confirmNow: "Xác Nhận Ngay",
    confirming: "Đang xác nhận...",
    
    // Booking Form
    bookingFor: "Đặt cho",
    selectSlot: "Chọn khung giờ",
    joinWaitlist: "Tham Gia Danh Sách Chờ",
    booked: "Đã Đặt",
    waitlisted: "Trong Danh Sách Chờ",
    notifyMe: "Thông báo qua:",
    remindMeVia: "Nhắc nhở qua",
    emailNotify: "Email",
    smsNotify: "SMS",
    bothNotify: "Cả hai",
    
    // Confirmation
    bookingConfirmed: "Đã Xác Nhận Đặt Lịch!",
    addedToWaitlist: "Đã Thêm Vào Danh Sách Chờ",
    confirmationSent: "Email xác nhận đã được gửi.",
    waitlistInfo: "Chúng tôi sẽ thông báo nếu có chỗ trống.",
    viewSchedule: "Xem Lịch Trình",
    bookAnother: "Đặt Thêm",
    
    // Schedule Lookup
    mySchedule: "Lịch Trình Của Tôi",
    enterEmail: "Nhập email để xem cuộc hẹn",
    lookupSchedule: "Tra Cứu Lịch",
    noAppointments: "Không tìm thấy cuộc hẹn",
    tryDifferentEmail: "Thử địa chỉ email khác",
    
    // Dates & Times
    today: "Hôm Nay",
    tomorrow: "Ngày Mai",
    am: "SA",
    pm: "CH",
    
    // Errors
    errorOccurred: "Đã xảy ra lỗi",
    pleaseRetry: "Vui lòng thử lại",
    requiredField: "Trường này là bắt buộc",
    invalidEmail: "Vui lòng nhập email hợp lệ",
    
    // Registration locked
    registrationOpens: "Đăng Ký Mở",
    registrationClosed: "Đăng ký đã đóng",
    comingSoon: "Sắp Ra Mắt",
    
    // Additional UI elements
    teacherPortal: "Cổng Giáo Viên",
    schoolConferences: "Hội Nghị Trường",
    needToMakeChanges: "Cần thay đổi lịch trình của bạn?",
    nameAZ: "Tên (A-Z)",
    nameZA: "Tên (Z-A)",
    gradeAsc: "Lớp (Tăng)",
    gradeDesc: "Lớp (Giảm)",
    noConferencesFound: "Không tìm thấy hội nghị",
    generalEvents: "Sự Kiện Chung",
    generalEvent: "Sự Kiện Chung",
    general: "Chung",
    signup: "Đăng Ký",
    appointmentsLabel: "Cuộc Hẹn",
    virtual: "Trực Tuyến",
    groupedTeachersTooltip: "Giáo viên theo nhóm - chọn một sẽ chọn tất cả",
    teachersAvailable: "giáo viên có sẵn",
    clickHere: "Nhấp vào đây",
    inCategory: "trong",
    noTeachersInCategory: "Không tìm thấy giáo viên trong",
    
    // Booking page
    registrationOpensSoon: "Đăng ký sắp mở",
    opensIn: "Mở trong:",
    unitDays: "Ngày",
    unitHours: "Giờ",
    unitMinutes: "Phút",
    unitSeconds: "Giây",
    pageUnlocksAutomatically: "Trang này sẽ tự động mở khóa khi đăng ký bắt đầu.",
    eventInformation: "Thông Tin Sự Kiện",
    dateTBA: "Ngày chưa xác định",
    virtualMeeting: "Họp Trực Tuyến",
    selectTimeSlot: "Chọn Khung Giờ",
    choosePreferredSlot: "Chọn khung giờ bạn muốn cho sự kiện này.",
    noSlotsAvailable: "Không có chỗ trống.",
    selectedTime: "Đã chọn:",
    waitlistOnly: "(Chỉ danh sách chờ)",
    completeFormBelow: "Hoàn thành biểu mẫu bên dưới.",
    slotJustTaken: "Rất tiếc! Khung giờ này vừa được đặt",
    slotTakenMessage: "Một phụ huynh khác vừa đặt khung giờ này trước bạn. Vui lòng làm mới để xem các khung giờ còn trống và chọn thời gian khác.",
    refreshAvailability: "Làm Mới Lịch Trống",
    registerForEvent: "Đăng Ký Sự Kiện Này",
    selectSlotFirst: "Vui lòng chọn khung giờ ở trên trước.",
    proceedToRegistration: "Nhấp tiếp tục để hoàn tất đăng ký.",
    changeTime: "Đổi Giờ",
    yourAppointment: "Cuộc Hẹn Của Bạn",
    completeFormToConfirm: "Hoàn thành biểu mẫu để xác nhận đặt chỗ.",
    fullName: "Họ và Tên",
    parentNamePlaceholder: "Tên Phụ Huynh",
    emailAddress: "Địa Chỉ Email",
    emailPlaceholder: "email@vidu.com",
    emailOptionalNote: "Tùy chọn - cung cấp để nhận nhắc nhở qua email",
    studentName: "Tên Học Sinh",
    studentNamePlaceholder: "Tên Học Sinh",
    phoneNumber: "Số Điện Thoại",
    phonePlaceholder: "0912 345 678",
    phoneOptionalNote: "Tùy chọn - cung cấp để nhận nhắc nhở qua SMS",
    commentsOptional: "Ghi Chú hoặc Yêu Cầu Đặc Biệt (Tùy chọn)",
    commentsPlaceholder: "Nhu cầu đặc biệt, câu hỏi hoặc thông tin bạn muốn chia sẻ với giáo viên...",
    needInterpreter: "Tôi cần phiên dịch",
    interpreterLanguagePlaceholder: "Ngôn ngữ (VD: Tiếng Anh, Ngôn ngữ ký hiệu)",
    reminderNotifications: "Thông Báo Nhắc Nhở (Tùy chọn)",
    reminderNotificationsDesc: "Chọn cách bạn muốn nhận nhắc nhở. Bạn phải cung cấp thông tin liên lạc cho phương thức đã chọn.",
    emailReminders: "Nhắc nhở qua email",
    emailRemindersDesc: "Nhận nhắc nhở về cuộc hẹn qua email. Yêu cầu địa chỉ email ở trên.",
    smsReminders: "Nhắc nhở qua SMS",
    smsRemindersDesc: "Nhận nhắc nhở qua tin nhắn. Yêu cầu số điện thoại ở trên. Có thể áp dụng phí tiêu chuẩn.",
    registering: "Đang đăng ký...",
    confirmRegistration: "Xác Nhận Đăng Ký",
    
    // Plan page additions
    scheduleYourConferences: "Lên Lịch Hội Nghị Của Bạn",
    youHaveMeetingsToSchedule: "Bạn có {count} {meeting} cần lên lịch.",
    meeting: "cuộc họp",
    meetings: "cuộc họp",
    continue: "Tiếp tục",
    continueButton: "tiếp tục →",
    yourTeachers: "Giáo Viên Của Bạn",
    selectTimesManually: "Chọn Thời Gian Thủ Công",
    suggestedSchedule: "Lịch Trình Đề Xuất",
    starts: "Bắt đầu:",
    totalTime: "Tổng thời gian:",
    tapContinue: "Nhấn \"Tiếp tục\"",
    howToUsePlanner: "Cách Sử Dụng Trình Lập Lịch",
    reviewAndConfirm: "Xem Lại và Xác Nhận",
    nextBest: "Tốt Nhất Tiếp Theo",
    
    poweredBy: "Cung cấp bởi",
    on: "vào",
    conferenceNotAvailable: "Hội Nghị Không Khả Dụng",
    startFresh: "Bắt Đầu Lại",
    yourPrivacyMatters: "Quyền Riêng Tư Của Bạn Rất Quan Trọng",
    privacyInfoUsage: "Chúng tôi chỉ sử dụng thông tin của bạn để lên lịch các cuộc hẹn hội nghị.",
    privacyNoSelling: "Chúng tôi không bao giờ bán dữ liệu của bạn.",
    viewPrivacyPolicy: "Xem Chính Sách Bảo Mật",
    autoSuggestWarning: "Tự động gợi ý sẽ thay thế các lựa chọn thời gian hiện tại của bạn. Bạn có muốn tiếp tục không? Điều này sẽ xóa các lựa chọn hiện tại.",
    interpreterNeeded: "Tôi cần phiên dịch viên",
    selectInterpreterLanguage: "Chọn ngôn ngữ",
    allSlotsFull: "Tất cả các khung giờ đã đầy",
    joinedWaitlistSuccess: "Bạn đã được thêm vào danh sách chờ! Chúng tôi sẽ thông báo nếu có chỗ trống.",
    stepYourInfo: "Thông tin<br />của bạn",
    stepSelectTeachers: "Chọn<br />Giáo viên",
    stepChooseTimes: "Chọn<br />Thời gian",
    stepConfirmMeetings: "Xác nhận<br />Cuộc họp",
    draftModeMessage: "Hội nghị này hiện đang ở chế độ nháp và chưa mở đăng ký công khai. Vui lòng quay lại sau hoặc liên hệ trường để biết thêm thông tin.",
    draftModeAdminNote: "Hội nghị sẽ hiển thị khi được quản trị viên trường xuất bản.",
    tryAutoSuggest: "Thử Gợi ý Tự động!",
    autoSuggestDescription: "Tìm lịch trình liên tiếp tốt nhất ngay lập tức.",
    dismiss: "Bỏ qua",
    manageSchedule: "Quản lý Lịch trình",
    findYourSchedule: "Tìm Lịch trình của Bạn",
    enterEmailToFindSchedule: "Nhập địa chỉ email bạn đã sử dụng để đặt lịch.",
    emailAddressLabel: "Địa chỉ Email",
    sendVerificationCode: "Gửi Mã Xác minh",
    checkYourEmail: "Kiểm tra Email của Bạn",
    weSentCodeTo: "Chúng tôi đã gửi mã 6 chữ số đến",
    verificationCode: "Mã Xác minh",
    verifyAndViewSchedule: "Xác minh và Xem Lịch trình",
    useDifferentEmail: "Sử dụng email khác",
    viewingScheduleFor: "Đang xem lịch trình cho",
    noUpcomingAppointments: "Không tìm thấy cuộc hẹn sắp tới.",
    bookAnAppointment: "Đặt Cuộc hẹn",
    studentLabel: "Học sinh:",
    rescheduleAppointment: "Đổi lịch Cuộc hẹn",
    cancelAppointment: "Hủy Cuộc hẹn",
    downloadCalendar: "Tải Lịch (.ics)",
    bookMoreAppointments: "+ Đặt Thêm Cuộc hẹn",
    reschedule: "Đổi lịch",
    currentAppointment: "Cuộc hẹn Hiện tại",
    selectNewTime: "Chọn Thời gian Mới",
    loadingAvailableTimes: "Đang tải thời gian có sẵn...",
    noOtherTimesAvailable: "Không có thời gian khác.",
    allSlotsFullOrPast: "Tất cả các khung giờ đã đầy hoặc đã qua.",
    confirmReschedule: "Xác nhận Đổi lịch",
    cancelConfirmation: "Bạn có chắc chắn muốn hủy cuộc hẹn này không?",
  },

  ko: {
    bookingConfirmedTitle: "예약이 확정되었습니다!",
    successfullyScheduled: "다음 선생님과의 상담을 예약했습니다",
    teachers: "명의 선생님",
    confirmationEmailSent: "확인 이메일을 보냈습니다:",
    addToCalendar: "캘린더에 추가",
    done: "완료",
    selectedTeachers: "선택한 선생님",
    dragToReorder: "끌어서 순서 변경",
    noTeachersSelected: "선택한 선생님이 없습니다.",
    goBackToAdd: "돌아가서 추가하기",
    youHaveTeachersToMeet: "상담할 선생님이 {count}명 있습니다.",
    desiredStartTime: "희망 시작 시간",
    autoSuggestSchedule: "일정 자동 추천",
    bestForBackToBack: "연속된 시간대에 적합",
    or: "또는",
    selectManually: "직접 선택",
    stepOf: "{total}단계 중 {current}단계",
    percentComplete: "{percent}% 완료",
    availableTimes: "예약 가능한 시간",
    noAvailableTimes: "이 선생님은 예약 가능한 시간이 없습니다.",
    time: "시간",
    howToUse: "이용 방법",
    tipClickSlots: "시간대를 클릭해 각 선생님과의 상담을 선택하세요. 선택한 시간대는 강조 표시됩니다.",
    tipAutoSuggest: "오른쪽 위의 자동 추천을 누르면 가장 좋은 연속 일정을 찾아줍니다.",
    tipDragTeachers: "사이드바에서 선생님을 끌어 순서를 바꿀 수 있으며, 추천 순서에 반영됩니다.",
    tipGrayedSlots: "회색으로 표시된 시간대는 다른 선생님에게 이미 선택한 시간이라 예약할 수 없습니다.",
    tipEnterStudentNames: "각 선생님 열 위에 학생 이름을 입력해 어떤 자녀의 상담인지 지정하세요.",
    foundOptimalSlots: "최적의 시간대 {count}개를 찾았습니다!",
    suggestionsOnly: "추천일 뿐입니다. 아래 일정을 확인한 뒤 준비되면 \"확인\"을 클릭하세요.",
    couldNotFindSchedule: "적합한 일정을 찾지 못했습니다. 시작 시간을 조정하거나 선생님 수를 줄여 보세요.",
    pleaseFillIn: "다음 항목을 입력해 주세요:",
    validEmailRequired: "유효한 이메일 주소를 입력해 주세요.",
    parentInformation: "학부모 정보",
    enterYourInfoAndChildren: "연락처를 입력하고 상담을 예약할 자녀를 추가하세요.",
    children: "자녀",
    addChild: "자녀 추가",
    addUpToTwoChildren: "한 번에 최대 2명의 자녀까지 예약할 수 있습니다.",
    addFirstChild: "첫 번째 자녀 추가",
    childName: "자녀 이름",
    continueToSelectTeachers: "선생님 선택으로 이동",
    forChild: "대상:",
    selectChild: "자녀 선택...",
    for: "대상",
    pleaseEnterName: "이름을 입력해 주세요",
    pleaseEnterValidEmail: "유효한 이메일 주소를 입력해 주세요",
    pleaseEnterValidPhone: "유효한 전화번호를 입력해 주세요",
    pleaseAddAtLeastOneChild: "자녀를 한 명 이상 추가해 주세요",
    pleaseEnterChildNames: "모든 자녀의 이름을 입력해 주세요",
    pleaseAssignChildToAllTeachers: "선택한 모든 선생님에게 자녀를 지정해 주세요",
    enterYourName: "성명을 입력하세요",
    enterYourEmail: "이메일 주소를 입력하세요",
    childOne: "자녀 1",
    childTwo: "자녀 2",
    // Common
    bookSlot: "예약하기",
    cancel: "취소",
    confirmBooking: "예약 확인",
    yourName: "이름",
    email: "이메일",
    phone: "전화번호",
    signUp: "신청",
    full: "마감",
    teacher: "선생님",
    location: "장소",
    backToSchool: "학교 회의로 돌아가기",
    required: "(필수)",
    optional: "(선택)",
    notifications: "알림",
    
    // School Page
    selectTeachers: "선생님 선택",
    searchTeachers: "선생님 검색...",
    sortBy: "정렬",
    lastName: "성",
    grade: "학년",
    ascending: "가-하",
    descending: "하-가",
    teacherSelected: "선생님 선택됨",
    teachersSelected: "선생님 선택됨",
    findTimes: "시간 찾기",
    clear: "지우기",
    select: "선택",
    selected: "선택됨",
    add: "추가",
    added: "추가됨",
    availableSlots: "가능한 시간",
    teamOf: "팀",
    selectAllTeam: "이 팀의 모든 선생님을 선택하시겠습니까?",
    yes: "예",
    no: "아니요",
    allTeachers: "모든 선생님",
    showAllTeachers: "모든 선생님 보기",
    chooseTimes: "시간 선택",
    pickAvailableSlots: "가능한 시간대 선택",
    confirm: "확인",
    confirmMeetings: "회의 확인",
    getConfirmation: "확인 받기",
    
    // Booking Limit
    bookingLimitTitle: "예약 제한",
    maxSlotsAllowed: "최대",
    slotsPerParent: "개의 학부모 면담을 예약할 수 있습니다.",
    enterEmailToSeeCount: "등록 시 이메일을 입력하면 현재 예약 현황을 확인할 수 있습니다.",
    youHaveBooked: "예약 완료",
    of: "/",
    allowedSlots: "개의 허용된 면담 예약",
    toBookMore: "추가 예약을 원하시면 기존 예약을 먼저 취소해 주세요.",
    youCanBook: "추가 예약 가능",
    moreSlots: "개",
    viewMySchedule: "내 일정 보기 & 관리 →",
    
    // Plan Page
    planYourSchedule: "일정 계획하기",
    selectTimeSlots: "각 선생님의 시간대를 선택하세요",
    autoSuggest: "자동 제안",
    autoSuggestDesc: "최적의 시간을 찾아드립니다",
    confirmAppointments: "예약 확인",
    appointment: "예약",
    appointments: "예약",
    noTimeSelected: "시간 미선택",
    childsName: "자녀 이름",
    enterChildName: "자녀 이름을 입력하세요",
    yourDetails: "연락처 정보",
    parentName: "학부모 이름",
    yourFullName: "이름 전체",
    yourEmail: "이메일@예시.com",
    yourPhone: "전화번호",
    review: "검토",
    back: "뒤로",
    nextTeacher: "다음 선생님",
    yourSchedule: "내 일정",
    tapToChange: "탭하여 변경",
    confirmNow: "지금 확인",
    confirming: "확인 중...",
    
    // Booking Form
    bookingFor: "예약 대상",
    selectSlot: "시간대 선택",
    joinWaitlist: "대기 명단 등록",
    booked: "예약됨",
    waitlisted: "대기 중",
    notifyMe: "알림 방법:",
    remindMeVia: "알림 수신",
    emailNotify: "이메일",
    smsNotify: "SMS",
    bothNotify: "둘 다",
    
    // Confirmation
    bookingConfirmed: "예약이 확인되었습니다!",
    addedToWaitlist: "대기 명단에 추가되었습니다",
    confirmationSent: "확인 이메일이 발송되었습니다.",
    waitlistInfo: "자리가 나면 알려드리겠습니다.",
    viewSchedule: "일정 보기",
    bookAnother: "추가 예약",
    
    // Schedule Lookup
    mySchedule: "내 일정",
    enterEmail: "이메일을 입력하여 예약 확인",
    lookupSchedule: "일정 조회",
    noAppointments: "예약 없음",
    tryDifferentEmail: "다른 이메일 주소로 시도해 보세요",
    
    // Dates & Times
    today: "오늘",
    tomorrow: "내일",
    am: "오전",
    pm: "오후",
    
    // Errors
    errorOccurred: "오류가 발생했습니다",
    pleaseRetry: "다시 시도해 주세요",
    requiredField: "필수 항목입니다",
    invalidEmail: "유효한 이메일을 입력해 주세요",
    
    // Registration locked
    registrationOpens: "등록 시작",
    registrationClosed: "등록이 마감되었습니다",
    comingSoon: "곧 출시",
    
    // Additional UI elements
    teacherPortal: "선생님 포털",
    schoolConferences: "학교 회의",
    needToMakeChanges: "일정을 변경해야 하나요?",
    nameAZ: "이름 (가-하)",
    nameZA: "이름 (하-가)",
    gradeAsc: "학년 (오름차순)",
    gradeDesc: "학년 (내림차순)",
    noConferencesFound: "회의를 찾을 수 없습니다",
    generalEvents: "일반 행사",
    generalEvent: "일반 행사",
    general: "일반",
    signup: "신청",
    appointmentsLabel: "예약",
    virtual: "온라인",
    groupedTeachersTooltip: "그룹 선생님 - 한 명을 선택하면 모두 선택됩니다",
    teachersAvailable: "명의 선생님 선택 가능",
    clickHere: "여기를 클릭하세요",
    inCategory: "의",
    noTeachersInCategory: "해당 카테고리에 선생님이 없습니다",
    
    // Booking page
    registrationOpensSoon: "곧 등록이 시작됩니다",
    opensIn: "시작까지:",
    unitDays: "일",
    unitHours: "시간",
    unitMinutes: "분",
    unitSeconds: "초",
    pageUnlocksAutomatically: "등록이 시작되면 이 페이지가 자동으로 잠금 해제됩니다.",
    eventInformation: "행사 정보",
    dateTBA: "날짜 미정",
    virtualMeeting: "온라인 미팅",
    selectTimeSlot: "시간대 선택",
    choosePreferredSlot: "원하시는 시간대를 선택해 주세요.",
    noSlotsAvailable: "가능한 시간이 없습니다.",
    selectedTime: "선택됨:",
    waitlistOnly: "(대기 명단만 가능)",
    completeFormBelow: "아래 양식을 작성해 주세요.",
    slotJustTaken: "죄송합니다! 해당 시간이 방금 예약되었습니다",
    slotTakenMessage: "다른 학부모님이 방금 이 시간대를 예약했습니다. 새로고침하여 가능한 시간을 확인하고 다른 시간을 선택해 주세요.",
    refreshAvailability: "예약 가능 시간 새로고침",
    registerForEvent: "이 행사에 등록",
    selectSlotFirst: "먼저 위에서 시간대를 선택해 주세요.",
    proceedToRegistration: "계속을 클릭하여 등록을 완료하세요.",
    changeTime: "시간 변경",
    yourAppointment: "귀하의 예약",
    completeFormToConfirm: "예약을 확정하려면 양식을 작성해 주세요.",
    fullName: "성명",
    parentNamePlaceholder: "학부모 성함",
    emailAddress: "이메일 주소",
    emailPlaceholder: "이메일@예시.com",
    emailOptionalNote: "선택 - 이메일 알림을 받으려면 입력",
    studentName: "학생 이름",
    studentNamePlaceholder: "학생 이름",
    phoneNumber: "전화번호",
    phonePlaceholder: "010-0000-0000",
    phoneOptionalNote: "선택 - SMS 알림을 받으려면 입력",
    commentsOptional: "메모 또는 특별 요청 (선택)",
    commentsPlaceholder: "특별한 요구사항, 질문 또는 선생님께 전달하고 싶은 정보...",
    needInterpreter: "통역이 필요합니다",
    interpreterLanguagePlaceholder: "언어 (예: 영어, 수화)",
    reminderNotifications: "알림 설정 (선택)",
    reminderNotificationsDesc: "알림 수신 방법을 선택하세요. 선택한 방법에 대한 연락처 정보를 제공해야 합니다.",
    emailReminders: "이메일 알림",
    emailRemindersDesc: "예약에 대한 이메일 알림을 받습니다. 위의 이메일 주소가 필요합니다.",
    smsReminders: "SMS 알림",
    smsRemindersDesc: "문자 메시지 알림을 받습니다. 위의 전화번호가 필요합니다. 표준 요금이 적용될 수 있습니다.",
    registering: "등록 중...",
    confirmRegistration: "등록 확정",
    
    // Plan page additions
    scheduleYourConferences: "회의 일정 잡기",
    youHaveMeetingsToSchedule: "{count}개의 {meeting}을 일정 잡아야 합니다.",
    meeting: "회의",
    meetings: "회의",
    continue: "계속",
    continueButton: "계속 →",
    yourTeachers: "선생님",
    selectTimesManually: "시간 수동 선택",
    suggestedSchedule: "권장 일정",
    starts: "시작:",
    totalTime: "총 시간:",
    tapContinue: "\"계속\" 탭하기",
    howToUsePlanner: "일정 계획기 사용 방법",
    reviewAndConfirm: "검토 및 확인",
    nextBest: "다음 최적",
    
    poweredBy: "제공",
    on: "",
    conferenceNotAvailable: "컨퍼런스를 사용할 수 없습니다",
    startFresh: "새로 시작",
    yourPrivacyMatters: "개인정보 보호가 중요합니다",
    privacyInfoUsage: "귀하의 정보는 컨퍼런스 일정을 잡는 데에만 사용됩니다.",
    privacyNoSelling: "귀하의 데이터를 절대 판매하지 않습니다.",
    viewPrivacyPolicy: "개인정보 처리방침 보기",
    autoSuggestWarning: "자동 제안은 현재 시간 선택을 대체합니다. 계속하시겠습니까? 현재 선택이 지워집니다.",
    interpreterNeeded: "통역사가 필요합니다",
    selectInterpreterLanguage: "언어 선택",
    allSlotsFull: "모든 시간대 마감",
    joinedWaitlistSuccess: "대기 명단에 추가되었습니다! 빈자리가 나면 알려드리겠습니다.",
    stepYourInfo: "귀하의<br />정보",
    stepSelectTeachers: "교사<br />선택",
    stepChooseTimes: "시간<br />선택",
    stepConfirmMeetings: "미팅<br />확인",
    draftModeMessage: "이 회의는 현재 초안 모드이며 아직 공개 등록이 불가능합니다. 나중에 다시 확인하시거나 학교에 문의하세요.",
    draftModeAdminNote: "회의는 학교 관리자가 게시하면 표시됩니다.",
    tryAutoSuggest: "자동 제안 사용해보기!",
    autoSuggestDescription: "최적의 연속 일정을 즉시 찾아줍니다.",
    dismiss: "닫기",
    manageSchedule: "일정 관리",
    findYourSchedule: "일정 찾기",
    enterEmailToFindSchedule: "예약 시 사용한 이메일 주소를 입력하세요.",
    emailAddressLabel: "이메일 주소",
    sendVerificationCode: "인증 코드 보내기",
    checkYourEmail: "이메일을 확인하세요",
    weSentCodeTo: "6자리 코드를 보냈습니다",
    verificationCode: "인증 코드",
    verifyAndViewSchedule: "인증 후 일정 보기",
    useDifferentEmail: "다른 이메일 사용",
    viewingScheduleFor: "일정 보기",
    noUpcomingAppointments: "예정된 예약이 없습니다.",
    bookAnAppointment: "예약하기",
    studentLabel: "학생:",
    rescheduleAppointment: "예약 변경",
    cancelAppointment: "예약 취소",
    downloadCalendar: "캘린더 다운로드 (.ics)",
    bookMoreAppointments: "+ 추가 예약",
    reschedule: "변경",
    currentAppointment: "현재 예약",
    selectNewTime: "새 시간 선택",
    loadingAvailableTimes: "가능한 시간 로딩 중...",
    noOtherTimesAvailable: "다른 시간이 없습니다.",
    allSlotsFullOrPast: "모든 시간이 마감되었거나 지났습니다.",
    confirmReschedule: "변경 확인",
    cancelConfirmation: "이 예약을 취소하시겠습니까?",
  },

  pt: {
    bookingConfirmedTitle: "Reserva confirmada!",
    successfullyScheduled: "Você agendou com sucesso reuniões com",
    teachers: "professores",
    confirmationEmailSent: "Um e-mail de confirmação foi enviado para",
    addToCalendar: "Adicionar ao calendário",
    done: "Concluído",
    selectedTeachers: "Professores selecionados",
    dragToReorder: "Arraste para reordenar",
    noTeachersSelected: "Nenhum professor selecionado.",
    goBackToAdd: "Volte para adicionar",
    youHaveTeachersToMeet: "Você tem {count} professores para encontrar.",
    desiredStartTime: "Horário de início desejado",
    autoSuggestSchedule: "Sugerir horário automaticamente",
    bestForBackToBack: "Ideal para horários consecutivos",
    or: "Ou",
    selectManually: "Selecionar manualmente",
    stepOf: "Etapa {current} de {total}",
    percentComplete: "{percent}% concluído",
    availableTimes: "Horários disponíveis",
    noAvailableTimes: "Não há horários disponíveis para este professor.",
    time: "Horário",
    howToUse: "Como usar",
    tipClickSlots: "Clique nos horários para escolher reuniões com cada professor. Os horários escolhidos ficam destacados.",
    tipAutoSuggest: "A sugestão automática (canto superior direito) encontra o melhor horário consecutivo.",
    tipDragTeachers: "Arraste os professores na barra lateral para reordená-los. Isso afeta a ordem sugerida.",
    tipGrayedSlots: "Horários em cinza estão bloqueados porque você já escolheu esse horário com outro professor.",
    tipEnterStudentNames: "Digite o nome do aluno acima da coluna de cada professor para indicar de qual filho é cada reunião.",
    foundOptimalSlots: "{count} horários ideais encontrados!",
    suggestionsOnly: "Estas são apenas sugestões. Revise o horário abaixo e clique em \"Confirmar\" quando estiver pronto para reservar.",
    couldNotFindSchedule: "Não foi possível encontrar um horário válido. Tente ajustar o horário de início ou selecionar menos professores.",
    pleaseFillIn: "Preencha o seguinte:",
    validEmailRequired: "Digite um endereço de e-mail válido.",
    parentInformation: "Informações do responsável",
    enterYourInfoAndChildren: "Informe seus dados de contato e adicione seus filhos para agendar as reuniões.",
    children: "Filhos",
    addChild: "Adicionar filho",
    addUpToTwoChildren: "Você pode agendar reuniões para até 2 filhos por vez.",
    addFirstChild: "Adicione seu primeiro filho",
    childName: "Nome do filho",
    continueToSelectTeachers: "Continuar para selecionar professores",
    forChild: "Para:",
    selectChild: "Selecione o filho...",
    for: "Para",
    pleaseEnterName: "Digite seu nome",
    pleaseEnterValidEmail: "Digite um endereço de e-mail válido",
    pleaseEnterValidPhone: "Digite um número de telefone válido",
    pleaseAddAtLeastOneChild: "Adicione pelo menos um filho",
    pleaseEnterChildNames: "Digite o nome de todos os filhos",
    pleaseAssignChildToAllTeachers: "Atribua um filho a todos os professores selecionados",
    enterYourName: "Digite seu nome completo",
    enterYourEmail: "Digite seu endereço de e-mail",
    childOne: "Filho 1",
    childTwo: "Filho 2",
    // Common
    bookSlot: "Reservar",
    cancel: "Cancelar",
    confirmBooking: "Confirmar Reserva",
    yourName: "Seu Nome",
    email: "E-mail",
    phone: "Telefone",
    signUp: "Inscrever-se",
    full: "Lotado",
    teacher: "Professor(a)",
    location: "Local",
    backToSchool: "Voltar às Conferências",
    required: "(Obrigatório)",
    optional: "(Opcional)",
    notifications: "Notificações",
    
    // School Page
    selectTeachers: "Selecionar Professores",
    searchTeachers: "Buscar professores...",
    sortBy: "Ordenar por",
    lastName: "Sobrenome",
    grade: "Série",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Professor Selecionado",
    teachersSelected: "Professores Selecionados",
    findTimes: "Encontrar Horários",
    clear: "Limpar",
    select: "Selecionar",
    selected: "Selecionado",
    add: "Adicionar",
    added: "Adicionado",
    availableSlots: "vagas disponíveis",
    teamOf: "Equipe de",
    selectAllTeam: "Selecionar todos os professores desta equipe?",
    yes: "Sim",
    no: "Não",
    allTeachers: "Todos os Professores",
    showAllTeachers: "Mostrar todos os professores",
    chooseTimes: "Escolher Horários",
    pickAvailableSlots: "Escolha os horários disponíveis",
    confirm: "Confirmar",
    confirmMeetings: "Confirmar Reuniões",
    getConfirmation: "Receber confirmação",
    
    // Booking Limit
    bookingLimitTitle: "Limite de Reservas",
    maxSlotsAllowed: "Máximo de",
    slotsPerParent: "conferências por responsável para este evento.",
    enterEmailToSeeCount: "Insira seu email durante o registro para ver suas reservas atuais.",
    youHaveBooked: "Você reservou",
    of: "de",
    allowedSlots: "vagas de conferência permitidas",
    toBookMore: "Para reservar mais, cancele primeiro um agendamento existente.",
    youCanBook: "Você pode reservar mais",
    moreSlots: "vaga(s)",
    viewMySchedule: "Ver e Gerenciar Minha Agenda →",
    
    // Plan Page
    planYourSchedule: "Planeje Sua Agenda",
    selectTimeSlots: "Selecione horários para cada professor",
    autoSuggest: "Sugestão Automática",
    autoSuggestDesc: "Deixe-nos encontrar os melhores horários",
    confirmAppointments: "Confirmar Agendamentos",
    appointment: "Agendamento",
    appointments: "Agendamentos",
    noTimeSelected: "Nenhum horário selecionado",
    childsName: "Nome da Criança",
    enterChildName: "Digite o nome do seu filho(a)",
    yourDetails: "Seus Dados",
    parentName: "Nome do Responsável",
    yourFullName: "Seu nome completo",
    yourEmail: "seu.email@exemplo.com",
    yourPhone: "Seu número de telefone",
    review: "Revisar",
    back: "Voltar",
    nextTeacher: "Próximo Professor",
    yourSchedule: "Sua Agenda",
    tapToChange: "Toque para alterar",
    confirmNow: "Confirmar Agora",
    confirming: "Confirmando...",
    
    // Booking Form
    bookingFor: "Reservando para",
    selectSlot: "Selecione um horário",
    joinWaitlist: "Entrar na Lista de Espera",
    booked: "Reservado",
    waitlisted: "Na Lista de Espera",
    notifyMe: "Notificar-me por:",
    remindMeVia: "Lembrar-me por",
    emailNotify: "E-mail",
    smsNotify: "SMS",
    bothNotify: "Ambos",
    
    // Confirmation
    bookingConfirmed: "Reserva Confirmada!",
    addedToWaitlist: "Adicionado à Lista de Espera",
    confirmationSent: "Uma confirmação foi enviada para seu e-mail.",
    waitlistInfo: "Notificaremos se uma vaga abrir.",
    viewSchedule: "Ver Minha Agenda",
    bookAnother: "Reservar Outro",
    
    // Schedule Lookup
    mySchedule: "Minha Agenda",
    enterEmail: "Digite seu e-mail para ver seus agendamentos",
    lookupSchedule: "Consultar Agenda",
    noAppointments: "Nenhum agendamento encontrado",
    tryDifferentEmail: "Tente outro endereço de e-mail",
    
    // Dates & Times
    today: "Hoje",
    tomorrow: "Amanhã",
    am: "h",
    pm: "h",
    
    // Errors
    errorOccurred: "Ocorreu um erro",
    pleaseRetry: "Por favor, tente novamente",
    requiredField: "Este campo é obrigatório",
    invalidEmail: "Digite um e-mail válido",
    
    // Registration locked
    registrationOpens: "Inscrições Abrem",
    registrationClosed: "As inscrições foram encerradas",
    comingSoon: "Em Breve",
    
    // Additional UI elements
    teacherPortal: "Portal do Professor",
    schoolConferences: "Conferências Escolares",
    needToMakeChanges: "Precisa fazer alterações na sua agenda?",
    nameAZ: "Nome (A-Z)",
    nameZA: "Nome (Z-A)",
    gradeAsc: "Série (Cresc.)",
    gradeDesc: "Série (Decresc.)",
    noConferencesFound: "Nenhuma conferência encontrada",
    generalEvents: "Eventos Gerais",
    generalEvent: "Evento Geral",
    general: "Geral",
    signup: "Inscrição",
    appointmentsLabel: "Agendamentos",
    virtual: "Virtual",
    groupedTeachersTooltip: "Professores agrupados - selecionar um seleciona todos",
    teachersAvailable: "professores disponíveis",
    clickHere: "Clique aqui",
    inCategory: "em",
    noTeachersInCategory: "Nenhum professor encontrado em",
    
    // Booking page
    registrationOpensSoon: "Inscrições abrem em breve",
    opensIn: "Abre em:",
    unitDays: "Dias",
    unitHours: "Horas",
    unitMinutes: "Minutos",
    unitSeconds: "Segundos",
    pageUnlocksAutomatically: "Esta página será desbloqueada automaticamente quando as inscrições abrirem.",
    eventInformation: "Informações do Evento",
    dateTBA: "Data a confirmar",
    virtualMeeting: "Reunião Virtual",
    selectTimeSlot: "Selecione um Horário",
    choosePreferredSlot: "Escolha seu horário preferido para este evento.",
    noSlotsAvailable: "Nenhum horário disponível.",
    selectedTime: "Selecionado:",
    waitlistOnly: "(Somente lista de espera)",
    completeFormBelow: "Complete o formulário abaixo.",
    slotJustTaken: "Ops! Este horário acabou de ser reservado",
    slotTakenMessage: "Outro responsável reservou este horário antes de você. Atualize para ver a disponibilidade e selecione outro horário.",
    refreshAvailability: "Atualizar Disponibilidade",
    registerForEvent: "Inscrever-se neste Evento",
    selectSlotFirst: "Por favor, selecione um horário acima primeiro.",
    proceedToRegistration: "Clique em continuar para completar seu registro.",
    changeTime: "Alterar Horário",
    yourAppointment: "Seu Agendamento",
    completeFormToConfirm: "Complete o formulário para confirmar sua reserva.",
    fullName: "Nome Completo",
    parentNamePlaceholder: "Nome do Responsável",
    emailAddress: "Endereço de E-mail",
    emailPlaceholder: "email@exemplo.com",
    emailOptionalNote: "Opcional - forneça para receber lembretes por e-mail",
    studentName: "Nome do Aluno",
    studentNamePlaceholder: "Nome do Aluno",
    phoneNumber: "Número de Telefone",
    phonePlaceholder: "(11) 99999-9999",
    phoneOptionalNote: "Opcional - forneça para receber lembretes por SMS",
    commentsOptional: "Comentários ou Pedidos Especiais (Opcional)",
    commentsPlaceholder: "Necessidades especiais, perguntas ou informações que gostaria de compartilhar com o professor...",
    needInterpreter: "Preciso de um intérprete",
    interpreterLanguagePlaceholder: "Idioma (ex., Inglês, Libras)",
    reminderNotifications: "Lembretes (Opcional)",
    reminderNotificationsDesc: "Escolha como deseja receber lembretes. Você deve fornecer as informações de contato para os métodos selecionados.",
    emailReminders: "Lembretes por e-mail",
    emailRemindersDesc: "Receba lembretes por e-mail sobre seu agendamento. Requer endereço de e-mail acima.",
    smsReminders: "Lembretes por SMS",
    smsRemindersDesc: "Receba lembretes por mensagem de texto. Requer número de telefone acima. Taxas padrão podem ser aplicadas.",
    registering: "Inscrevendo...",
    confirmRegistration: "Confirmar Inscrição",
    
    // Plan page additions
    scheduleYourConferences: "Agendar Suas Conferências",
    youHaveMeetingsToSchedule: "Você tem {count} {meeting} para agendar.",
    meeting: "reunião",
    meetings: "reuniões",
    continue: "Continuar",
    continueButton: "continuar →",
    yourTeachers: "Seus Professores",
    selectTimesManually: "Selecionar Horários Manualmente",
    suggestedSchedule: "Agenda Sugerida",
    starts: "Começa:",
    totalTime: "Tempo total:",
    tapContinue: "Toque em \"Continuar\"",
    howToUsePlanner: "Como Usar o Agendador",
    reviewAndConfirm: "Revisar e Confirmar",
    nextBest: "Próximo Melhor",
    
    poweredBy: "Desenvolvido por",
    on: "em",
    conferenceNotAvailable: "Conferência Não Disponível",
    startFresh: "Recomeçar",
    yourPrivacyMatters: "Sua Privacidade é Importante",
    privacyInfoUsage: "Usamos suas informações apenas para agendar seus compromissos de conferência.",
    privacyNoSelling: "Nunca vendemos seus dados.",
    viewPrivacyPolicy: "Ver Política de Privacidade",
    autoSuggestWarning: "A sugestão automática substituirá suas seleções de horários atuais. Deseja continuar? Isso limpará suas seleções atuais.",
    interpreterNeeded: "Preciso de um intérprete",
    selectInterpreterLanguage: "Selecionar idioma",
    allSlotsFull: "Todos os horários lotados",
    joinedWaitlistSuccess: "Você foi adicionado à lista de espera! Notificaremos se uma vaga abrir.",
    stepYourInfo: "Suas<br />Informações",
    stepSelectTeachers: "Selecionar<br />Professores",
    stepChooseTimes: "Escolher<br />Horários",
    stepConfirmMeetings: "Confirmar<br />Reuniões",
    draftModeMessage: "Esta conferência está atualmente em modo rascunho e não está disponível para registro público. Por favor, volte mais tarde ou entre em contato com a escola para mais informações.",
    draftModeAdminNote: "A conferência ficará visível assim que for publicada pelo administrador da escola.",
    tryAutoSuggest: "Experimente a Sugestão Automática!",
    autoSuggestDescription: "Encontra o melhor horário consecutivo instantaneamente.",
    dismiss: "Dispensar",
    manageSchedule: "Gerenciar Agenda",
    findYourSchedule: "Encontre Sua Agenda",
    enterEmailToFindSchedule: "Digite o e-mail que você usou para agendar seus compromissos.",
    emailAddressLabel: "Endereço de E-mail",
    sendVerificationCode: "Enviar Código de Verificação",
    checkYourEmail: "Verifique Seu E-mail",
    weSentCodeTo: "Enviamos um código de 6 dígitos para",
    verificationCode: "Código de Verificação",
    verifyAndViewSchedule: "Verificar e Ver Agenda",
    useDifferentEmail: "Usar outro e-mail",
    viewingScheduleFor: "Visualizando agenda de",
    noUpcomingAppointments: "Nenhum compromisso encontrado.",
    bookAnAppointment: "Agendar Compromisso",
    studentLabel: "Aluno:",
    rescheduleAppointment: "Reagendar Compromisso",
    cancelAppointment: "Cancelar Compromisso",
    downloadCalendar: "Baixar Calendário (.ics)",
    bookMoreAppointments: "+ Agendar Mais",
    reschedule: "Reagendar",
    currentAppointment: "Compromisso Atual",
    selectNewTime: "Selecionar Novo Horário",
    loadingAvailableTimes: "Carregando horários disponíveis...",
    noOtherTimesAvailable: "Nenhum outro horário disponível.",
    allSlotsFullOrPast: "Todos os horários estão lotados ou já passaram.",
    confirmReschedule: "Confirmar Reagendamento",
    cancelConfirmation: "Tem certeza de que deseja cancelar este compromisso?",
  },

  fa: {
    bookingConfirmedTitle: "رزرو تأیید شد!",
    successfullyScheduled: "شما با موفقیت قرار ملاقات‌هایی تنظیم کردید با",
    teachers: "معلم",
    confirmationEmailSent: "ایمیل تأیید ارسال شد به",
    addToCalendar: "افزودن به تقویم",
    done: "انجام شد",
    selectedTeachers: "معلمان انتخاب‌شده",
    dragToReorder: "برای تغییر ترتیب بکشید",
    noTeachersSelected: "هیچ معلمی انتخاب نشده است.",
    goBackToAdd: "برای افزودن بازگردید",
    youHaveTeachersToMeet: "شما {count} معلم برای ملاقات دارید.",
    desiredStartTime: "زمان شروع دلخواه",
    autoSuggestSchedule: "پیشنهاد خودکار برنامه",
    bestForBackToBack: "مناسب برای زمان‌های پشت‌سرهم",
    or: "یا",
    selectManually: "انتخاب دستی",
    stepOf: "مرحله {current} از {total}",
    percentComplete: "{percent}٪ تکمیل شد",
    availableTimes: "زمان‌های در دسترس",
    noAvailableTimes: "برای این معلم زمانی در دسترس نیست.",
    time: "زمان",
    howToUse: "نحوه استفاده",
    tipClickSlots: "روی زمان‌ها کلیک کنید تا برای هر معلم قرار ملاقات انتخاب کنید. زمان‌های انتخاب‌شده برجسته می‌شوند.",
    tipAutoSuggest: "پیشنهاد خودکار (بالا سمت راست) بهترین برنامه پشت‌سرهم را پیدا می‌کند.",
    tipDragTeachers: "معلمان را در نوار کناری بکشید تا ترتیبشان تغییر کند؛ این بر ترتیب پیشنهادی اثر می‌گذارد.",
    tipGrayedSlots: "زمان‌های خاکستری مسدود هستند، چون همان زمان را برای معلم دیگری انتخاب کرده‌اید.",
    tipEnterStudentNames: "نام دانش‌آموز را بالای ستون هر معلم بنویسید تا مشخص شود هر جلسه مربوط به کدام فرزند است.",
    foundOptimalSlots: "{count} زمان بهینه پیدا شد!",
    suggestionsOnly: "این‌ها فقط پیشنهاد هستند. برنامه زیر را بررسی کنید و هنگام آمادگی روی \"تأیید\" کلیک کنید.",
    couldNotFindSchedule: "برنامه معتبری پیدا نشد. زمان شروع را تغییر دهید یا معلمان کمتری انتخاب کنید.",
    pleaseFillIn: "لطفاً موارد زیر را تکمیل کنید:",
    validEmailRequired: "لطفاً یک ایمیل معتبر وارد کنید.",
    // Common - Farsi (Persian)
    bookSlot: "رزرو وقت",
    cancel: "لغو",
    confirmBooking: "تأیید رزرو",
    yourName: "نام شما",
    email: "ایمیل",
    phone: "تلفن",
    signUp: "ثبت‌نام",
    full: "پر شده",
    teacher: "معلم",
    location: "مکان",
    backToSchool: "بازگشت به کنفرانس‌ها",
    required: "(الزامی)",
    optional: "(اختیاری)",
    notifications: "اعلان‌ها",
    
    // School Page
    selectTeachers: "انتخاب معلمان",
    searchTeachers: "جستجوی معلمان...",
    sortBy: "مرتب‌سازی بر اساس",
    lastName: "نام خانوادگی",
    grade: "پایه",
    ascending: "الف-ی",
    descending: "ی-الف",
    teacherSelected: "معلم انتخاب شد",
    teachersSelected: "معلمان انتخاب شدند",
    findTimes: "یافتن زمان‌ها",
    clear: "پاک کردن",
    select: "انتخاب",
    selected: "انتخاب شده",
    add: "افزودن",
    added: "افزوده شد",
    availableSlots: "وقت‌های موجود",
    teamOf: "تیم",
    selectAllTeam: "انتخاب همه معلمان این تیم؟",
    yes: "بله",
    no: "خیر",
    allTeachers: "همه معلمان",
    showAllTeachers: "نمایش همه معلمان",
    chooseTimes: "انتخاب زمان‌ها",
    pickAvailableSlots: "وقت‌های موجود را انتخاب کنید",
    confirm: "تأیید",
    confirmMeetings: "تأیید جلسات",
    getConfirmation: "دریافت تأییدیه",
    
    // Booking Limit
    bookingLimitTitle: "محدودیت رزرو",
    maxSlotsAllowed: "حداکثر",
    slotsPerParent: "کنفرانس برای هر والد در این رویداد.",
    enterEmailToSeeCount: "ایمیل خود را هنگام ثبت‌نام وارد کنید تا رزروهای فعلی را ببینید.",
    youHaveBooked: "شما رزرو کرده‌اید",
    of: "از",
    allowedSlots: "وقت کنفرانس مجاز",
    toBookMore: "برای رزرو بیشتر، لطفاً ابتدا یک قرار موجود را لغو کنید.",
    youCanBook: "می‌توانید رزرو کنید",
    moreSlots: "وقت دیگر",
    viewMySchedule: "مشاهده و مدیریت برنامه من ←",
    
    // Plan Page
    planYourSchedule: "برنامه‌ریزی زمان‌بندی شما",
    selectTimeSlots: "زمان‌هایی برای هر معلم انتخاب کنید",
    autoSuggest: "پیشنهاد خودکار",
    autoSuggestDesc: "بگذارید بهترین زمان‌ها را برای شما پیدا کنیم",
    confirmAppointments: "تأیید قرارها",
    appointment: "قرار",
    appointments: "قرارها",
    noTimeSelected: "زمانی انتخاب نشده",
    childsName: "نام فرزند",
    enterChildName: "نام فرزند خود را وارد کنید",
    yourDetails: "اطلاعات شما",
    parentName: "نام والد",
    yourFullName: "نام کامل شما",
    yourEmail: "ایمیل@مثال.com",
    yourPhone: "شماره تلفن شما",
    review: "بررسی",
    back: "بازگشت",
    nextTeacher: "معلم بعدی",
    yourSchedule: "برنامه شما",
    tapToChange: "برای تغییر لمس کنید",
    confirmNow: "اکنون تأیید کنید",
    confirming: "در حال تأیید...",
    
    // Booking Form
    bookingFor: "رزرو برای",
    selectSlot: "یک زمان انتخاب کنید",
    joinWaitlist: "پیوستن به لیست انتظار",
    booked: "رزرو شده",
    waitlisted: "در لیست انتظار",
    notifyMe: "اطلاع‌رسانی از طریق:",
    remindMeVia: "یادآوری از طریق",
    emailNotify: "ایمیل",
    smsNotify: "پیامک",
    bothNotify: "هر دو",
    
    // Confirmation
    bookingConfirmed: "رزرو تأیید شد!",
    addedToWaitlist: "به لیست انتظار اضافه شد",
    confirmationSent: "تأییدیه به ایمیل شما ارسال شد.",
    waitlistInfo: "در صورت خالی شدن جا به شما اطلاع می‌دهیم.",
    viewSchedule: "مشاهده برنامه من",
    bookAnother: "رزرو دیگری",
    
    // Schedule Lookup
    mySchedule: "برنامه من",
    enterEmail: "ایمیل خود را برای مشاهده قرارها وارد کنید",
    lookupSchedule: "جستجوی برنامه",
    noAppointments: "قراری یافت نشد",
    tryDifferentEmail: "ایمیل دیگری امتحان کنید",
    
    // Dates & Times
    today: "امروز",
    tomorrow: "فردا",
    am: "ق.ظ",
    pm: "ب.ظ",
    
    // Errors
    errorOccurred: "خطایی رخ داد",
    pleaseRetry: "لطفاً دوباره تلاش کنید",
    requiredField: "این فیلد الزامی است",
    invalidEmail: "لطفاً یک ایمیل معتبر وارد کنید",
    
    // Registration locked
    registrationOpens: "ثبت‌نام باز می‌شود",
    registrationClosed: "ثبت‌نام بسته شده است",
    comingSoon: "به زودی",
    
    // Additional UI elements
    teacherPortal: "پورتال معلم",
    schoolConferences: "کنفرانس‌های مدرسه",
    needToMakeChanges: "نیاز به تغییر برنامه دارید؟",
    nameAZ: "نام (الف-ی)",
    nameZA: "نام (ی-الف)",
    gradeAsc: "پایه (صعودی)",
    gradeDesc: "پایه (نزولی)",
    noConferencesFound: "کنفرانسی یافت نشد",
    generalEvents: "رویدادهای عمومی",
    generalEvent: "رویداد عمومی",
    general: "عمومی",
    signup: "ثبت‌نام",
    appointmentsLabel: "قرارها",
    virtual: "مجازی",
    groupedTeachersTooltip: "معلمان گروه‌بندی شده - انتخاب یکی همه را انتخاب می‌کند",
    teachersAvailable: "معلم موجود",
    clickHere: "اینجا کلیک کنید",
    inCategory: "در",
    noTeachersInCategory: "معلمی در این دسته یافت نشد",
    
    // Booking page
    registrationOpensSoon: "ثبت‌نام به زودی باز می‌شود",
    opensIn: "باز می‌شود در:",
    unitDays: "روز",
    unitHours: "ساعت",
    unitMinutes: "دقیقه",
    unitSeconds: "ثانیه",
    pageUnlocksAutomatically: "این صفحه هنگام شروع ثبت‌نام به طور خودکار باز می‌شود.",
    eventInformation: "اطلاعات رویداد",
    dateTBA: "تاریخ اعلام می‌شود",
    virtualMeeting: "جلسه مجازی",
    selectTimeSlot: "انتخاب زمان",
    choosePreferredSlot: "زمان مورد نظر خود را برای این رویداد انتخاب کنید.",
    noSlotsAvailable: "زمانی موجود نیست.",
    selectedTime: "انتخاب شده:",
    waitlistOnly: "(فقط لیست انتظار)",
    completeFormBelow: "فرم زیر را تکمیل کنید.",
    slotJustTaken: "متأسفیم! این زمان همین الان رزرو شد",
    slotTakenMessage: "والد دیگری همین الان این زمان را رزرو کرد. لطفاً صفحه را تازه کنید تا زمان‌های موجود را ببینید و زمان دیگری انتخاب کنید.",
    refreshAvailability: "تازه‌سازی موجودی",
    registerForEvent: "ثبت‌نام در این رویداد",
    selectSlotFirst: "لطفاً ابتدا یک زمان در بالا انتخاب کنید.",
    proceedToRegistration: "برای تکمیل ثبت‌نام روی ادامه کلیک کنید.",
    changeTime: "تغییر زمان",
    yourAppointment: "وقت ملاقات شما",
    completeFormToConfirm: "فرم را برای تأیید رزرو تکمیل کنید.",
    fullName: "نام کامل",
    parentNamePlaceholder: "نام والد",
    emailAddress: "آدرس ایمیل",
    emailPlaceholder: "ایمیل@مثال.com",
    emailOptionalNote: "اختیاری - برای دریافت یادآوری ایمیلی وارد کنید",
    studentName: "نام دانش‌آموز",
    studentNamePlaceholder: "نام دانش‌آموز",
    phoneNumber: "شماره تلفن",
    phonePlaceholder: "09XX XXX XXXX",
    phoneOptionalNote: "اختیاری - برای دریافت یادآوری پیامکی وارد کنید",
    commentsOptional: "نظرات یا درخواست‌های ویژه (اختیاری)",
    commentsPlaceholder: "نیازهای خاص، سوالات یا اطلاعاتی که می‌خواهید با معلم به اشتراک بگذارید...",
    needInterpreter: "به مترجم نیاز دارم",
    interpreterLanguagePlaceholder: "زبان (مثلاً انگلیسی، زبان اشاره)",
    reminderNotifications: "اعلان‌های یادآوری (اختیاری)",
    reminderNotificationsDesc: "نحوه دریافت یادآوری را انتخاب کنید. باید اطلاعات تماس برای روش‌های انتخابی را ارائه دهید.",
    emailReminders: "یادآوری ایمیلی",
    emailRemindersDesc: "یادآوری‌های ایمیلی درباره قرار خود دریافت کنید. نیاز به آدرس ایمیل در بالا.",
    smsReminders: "یادآوری پیامکی",
    smsRemindersDesc: "یادآوری‌های پیامکی دریافت کنید. نیاز به شماره تلفن در بالا. هزینه‌های استاندارد ممکن است اعمال شود.",
    registering: "در حال ثبت‌نام...",
    confirmRegistration: "تأیید ثبت‌نام",
    
    // Plan page additions
    scheduleYourConferences: "برنامه‌ریزی کنفرانس‌های شما",
    youHaveMeetingsToSchedule: "شما {count} {meeting} برای برنامه‌ریزی دارید.",
    meeting: "جلسه",
    meetings: "جلسات",
    continue: "ادامه",
    continueButton: "ادامه ←",
    yourTeachers: "معلمان شما",
    selectTimesManually: "انتخاب زمان‌ها به صورت دستی",
    suggestedSchedule: "برنامه پیشنهادی",
    starts: "شروع:",
    totalTime: "زمان کل:",
    tapContinue: "روی \"ادامه\" بزنید",
    howToUsePlanner: "نحوه استفاده از برنامه‌ریز",
    reviewAndConfirm: "بررسی و تأیید",
    nextBest: "بهترین بعدی",
    
    poweredBy: "قدرت گرفته از",
    on: "در",
    
    // Multi-Child Support
    parentInformation: "اطلاعات والد",
    enterYourInfoAndChildren: "اطلاعات تماس خود را وارد کنید و فرزندان خود را برای برنامه‌ریزی کنفرانس‌ها اضافه کنید.",
    children: "فرزندان",
    addChild: "افزودن فرزند",
    addUpToTwoChildren: "می‌توانید همزمان برای حداکثر ۲ فرزند کنفرانس برنامه‌ریزی کنید.",
    addFirstChild: "اولین فرزند خود را اضافه کنید",
    childName: "نام فرزند",
    continueToSelectTeachers: "ادامه برای انتخاب معلمان",
    forChild: "برای:",
    selectChild: "فرزند را انتخاب کنید...",
    for: "برای",
    pleaseEnterName: "لطفاً نام خود را وارد کنید",
    pleaseEnterValidEmail: "لطفاً یک آدرس ایمیل معتبر وارد کنید",
    pleaseEnterValidPhone: "لطفاً یک شماره تلفن معتبر وارد کنید",
    pleaseAddAtLeastOneChild: "لطفاً حداقل یک فرزند اضافه کنید",
    pleaseEnterChildNames: "لطفاً نام همه فرزندان را وارد کنید",
    pleaseAssignChildToAllTeachers: "لطفاً یک فرزند به همه معلمان انتخاب شده اختصاص دهید",
    enterYourName: "نام کامل خود را وارد کنید",
    enterYourEmail: "آدرس ایمیل خود را وارد کنید",
    childOne: "فرزند ۱",
    childTwo: "فرزند ۲",
    conferenceNotAvailable: "کنفرانس در دسترس نیست",
    startFresh: "شروع مجدد",
    yourPrivacyMatters: "حریم خصوصی شما مهم است",
    privacyInfoUsage: "ما از اطلاعات شما فقط برای برنامه‌ریزی قرار ملاقات‌های کنفرانس استفاده می‌کنیم.",
    privacyNoSelling: "ما هرگز داده‌های شما را نمی‌فروشیم.",
    viewPrivacyPolicy: "مشاهده سیاست حفظ حریم خصوصی",
    autoSuggestWarning: "پیشنهاد خودکار انتخاب‌های زمانی فعلی شما را جایگزین خواهد کرد. آیا می‌خواهید ادامه دهید؟ این کار انتخاب‌های فعلی شما را پاک خواهد کرد.",
    interpreterNeeded: "من به مترجم نیاز دارم",
    selectInterpreterLanguage: "زبان را انتخاب کنید",
    allSlotsFull: "تمام وقت‌ها پر شده",
    joinedWaitlistSuccess: "شما به لیست انتظار اضافه شدید! در صورت آزاد شدن جا به شما اطلاع می‌دهیم.",
    stepYourInfo: "اطلاعات<br />شما",
    stepSelectTeachers: "انتخاب<br />معلم‌ها",
    stepChooseTimes: "انتخاب<br />زمان‌ها",
    stepConfirmMeetings: "تأیید<br />جلسات",
    draftModeMessage: "این کنفرانس در حال حاضر در حالت پیش‌نویس است و هنوز برای ثبت‌نام عمومی در دسترس نیست. لطفاً بعداً مراجعه کنید یا با مدرسه تماس بگیرید.",
    draftModeAdminNote: "کنفرانس پس از انتشار توسط مدیر مدرسه قابل مشاهده خواهد بود.",
    tryAutoSuggest: "پیشنهاد خودکار را امتحان کنید!",
    autoSuggestDescription: "بهترین برنامه پشت‌سرهم را فوراً پیدا می‌کند.",
    dismiss: "رد کردن",
    manageSchedule: "مدیریت برنامه",
    findYourSchedule: "برنامه خود را پیدا کنید",
    enterEmailToFindSchedule: "ایمیلی که برای رزرو استفاده کردید را وارد کنید.",
    emailAddressLabel: "آدرس ایمیل",
    sendVerificationCode: "ارسال کد تأیید",
    checkYourEmail: "ایمیل خود را بررسی کنید",
    weSentCodeTo: "یک کد ۶ رقمی ارسال شد به",
    verificationCode: "کد تأیید",
    verifyAndViewSchedule: "تأیید و مشاهده برنامه",
    useDifferentEmail: "استفاده از ایمیل دیگر",
    viewingScheduleFor: "مشاهده برنامه برای",
    noUpcomingAppointments: "هیچ قراری یافت نشد.",
    bookAnAppointment: "رزرو قرار",
    studentLabel: "دانش‌آموز:",
    rescheduleAppointment: "تغییر زمان قرار",
    cancelAppointment: "لغو قرار",
    downloadCalendar: "دانلود تقویم (.ics)",
    bookMoreAppointments: "+ رزرو بیشتر",
    reschedule: "تغییر زمان",
    currentAppointment: "قرار فعلی",
    selectNewTime: "انتخاب زمان جدید",
    loadingAvailableTimes: "در حال بارگذاری زمان‌های موجود...",
    noOtherTimesAvailable: "زمان دیگری موجود نیست.",
    allSlotsFullOrPast: "همه زمان‌ها پر یا گذشته هستند.",
    confirmReschedule: "تأیید تغییر زمان",
    cancelConfirmation: "آیا مطمئنید که می‌خواهید این قرار را لغو کنید؟",
  },

  tr: {
    bookingConfirmedTitle: "Randevu onaylandı!",
    successfullyScheduled: "Şu öğretmenlerle randevularınızı başarıyla oluşturdunuz",
    teachers: "öğretmen",
    confirmationEmailSent: "Onay e-postası gönderildi:",
    addToCalendar: "Takvime ekle",
    done: "Tamam",
    selectedTeachers: "Seçilen öğretmenler",
    dragToReorder: "Sıralamak için sürükleyin",
    noTeachersSelected: "Hiç öğretmen seçilmedi.",
    goBackToAdd: "Eklemek için geri dönün",
    youHaveTeachersToMeet: "Görüşmeniz gereken {count} öğretmen var.",
    desiredStartTime: "İstenen başlangıç saati",
    autoSuggestSchedule: "Programı otomatik öner",
    bestForBackToBack: "Arka arkaya randevular için ideal",
    or: "veya",
    selectManually: "Elle seç",
    stepOf: "Adım {current} / {total}",
    percentComplete: "%{percent} tamamlandı",
    availableTimes: "Uygun saatler",
    noAvailableTimes: "Bu öğretmen için uygun saat yok.",
    time: "Saat",
    howToUse: "Nasıl kullanılır",
    tipClickSlots: "Her öğretmenle randevu seçmek için saatlere tıklayın. Seçilen saatler vurgulanır.",
    tipAutoSuggest: "Sağ üstteki otomatik öneri, en iyi ardışık programı bulur.",
    tipDragTeachers: "Öğretmenleri kenar çubuğunda sürükleyerek sıralayın; bu, önerilen sırayı etkiler.",
    tipGrayedSlots: "Gri saatler, o saati başka bir öğretmen için seçtiğiniz için kapalıdır.",
    tipEnterStudentNames: "Her görüşmenin hangi çocuğunuza ait olduğunu belirtmek için öğrenci adını ilgili öğretmen sütununun üstüne yazın.",
    foundOptimalSlots: "{count} ideal saat bulundu!",
    suggestionsOnly: "Bunlar yalnızca öneridir. Aşağıdaki programı gözden geçirin ve hazır olduğunuzda \"Onayla\"ya tıklayın.",
    couldNotFindSchedule: "Uygun bir program bulunamadı. Başlangıç saatini değiştirmeyi veya daha az öğretmen seçmeyi deneyin.",
    pleaseFillIn: "Lütfen aşağıdakileri doldurun:",
    validEmailRequired: "Lütfen geçerli bir e-posta adresi girin.",
    // Common - Turkish
    bookSlot: "Randevu Al",
    cancel: "İptal",
    confirmBooking: "Rezervasyonu Onayla",
    yourName: "Adınız",
    email: "E-posta",
    phone: "Telefon",
    signUp: "Kayıt Ol",
    full: "Dolu",
    teacher: "Öğretmen",
    location: "Konum",
    backToSchool: "Konferanslara Dön",
    required: "(Zorunlu)",
    optional: "(İsteğe Bağlı)",
    notifications: "Bildirimler",
    
    // School Page
    selectTeachers: "Öğretmen Seç",
    searchTeachers: "Öğretmen ara...",
    sortBy: "Sırala",
    lastName: "Soyadı",
    grade: "Sınıf",
    ascending: "A-Z",
    descending: "Z-A",
    teacherSelected: "Öğretmen Seçildi",
    teachersSelected: "Öğretmen Seçildi",
    findTimes: "Zaman Bul",
    clear: "Temizle",
    select: "Seç",
    selected: "Seçildi",
    add: "Ekle",
    added: "Eklendi",
    availableSlots: "müsait zaman",
    teamOf: "Takım",
    selectAllTeam: "Bu takımdaki tüm öğretmenler seçilsin mi?",
    yes: "Evet",
    no: "Hayır",
    allTeachers: "Tüm Öğretmenler",
    showAllTeachers: "Tüm öğretmenleri göster",
    chooseTimes: "Zaman Seç",
    pickAvailableSlots: "Müsait zamanları seçin",
    confirm: "Onayla",
    confirmMeetings: "Toplantıları Onayla",
    getConfirmation: "Onay al",
    
    // Booking Limit
    bookingLimitTitle: "Rezervasyon Limiti",
    maxSlotsAllowed: "Maksimum",
    slotsPerParent: "konferans bu etkinlik için veli başına.",
    enterEmailToSeeCount: "Mevcut rezervasyonlarınızı görmek için kayıt sırasında e-postanızı girin.",
    youHaveBooked: "Rezerve ettiniz",
    of: "/",
    allowedSlots: "izin verilen konferans yeri",
    toBookMore: "Daha fazla rezervasyon için lütfen önce mevcut bir randevuyu iptal edin.",
    youCanBook: "Daha fazla rezervasyon yapabilirsiniz",
    moreSlots: "yer daha",
    viewMySchedule: "Programımı Görüntüle ve Yönet →",
    
    // Plan Page
    planYourSchedule: "Programınızı Planlayın",
    selectTimeSlots: "Her öğretmen için zaman dilimi seçin",
    autoSuggest: "Otomatik Öneri",
    autoSuggestDesc: "Sizin için en iyi zamanları bulalım",
    confirmAppointments: "Randevuları Onayla",
    appointment: "Randevu",
    appointments: "Randevular",
    noTimeSelected: "Zaman seçilmedi",
    childsName: "Çocuğun Adı",
    enterChildName: "Çocuğunuzun adını girin",
    yourDetails: "Bilgileriniz",
    parentName: "Veli Adı",
    yourFullName: "Tam adınız",
    yourEmail: "e-posta@ornek.com",
    yourPhone: "Telefon numaranız",
    review: "İncele",
    back: "Geri",
    nextTeacher: "Sonraki Öğretmen",
    yourSchedule: "Programınız",
    tapToChange: "Değiştirmek için dokunun",
    confirmNow: "Şimdi Onayla",
    confirming: "Onaylanıyor...",
    
    // Booking Form
    bookingFor: "Rezervasyon",
    selectSlot: "Bir zaman seçin",
    joinWaitlist: "Bekleme Listesine Katıl",
    booked: "Rezerve Edildi",
    waitlisted: "Bekleme Listesinde",
    notifyMe: "Şununla bildir:",
    remindMeVia: "Şununla hatırlat",
    emailNotify: "E-posta",
    smsNotify: "SMS",
    bothNotify: "Her İkisi",
    
    // Confirmation
    bookingConfirmed: "Rezervasyon Onaylandı!",
    addedToWaitlist: "Bekleme Listesine Eklendi",
    confirmationSent: "E-postanıza bir onay gönderildi.",
    waitlistInfo: "Yer açılırsa size bildireceğiz.",
    viewSchedule: "Programımı Gör",
    bookAnother: "Başka Rezervasyon",
    
    // Schedule Lookup
    mySchedule: "Programım",
    enterEmail: "Randevularınızı görmek için e-postanızı girin",
    lookupSchedule: "Program Ara",
    noAppointments: "Randevu bulunamadı",
    tryDifferentEmail: "Farklı bir e-posta adresi deneyin",
    
    // Dates & Times
    today: "Bugün",
    tomorrow: "Yarın",
    am: "ÖÖ",
    pm: "ÖS",
    
    // Errors
    errorOccurred: "Bir hata oluştu",
    pleaseRetry: "Lütfen tekrar deneyin",
    requiredField: "Bu alan zorunludur",
    invalidEmail: "Lütfen geçerli bir e-posta girin",
    
    // Registration locked
    registrationOpens: "Kayıt Açılıyor",
    registrationClosed: "Kayıt kapandı",
    comingSoon: "Yakında",
    
    // Additional UI elements
    teacherPortal: "Öğretmen Portalı",
    schoolConferences: "Okul Konferansları",
    needToMakeChanges: "Programınızda değişiklik yapmanız mı gerekiyor?",
    nameAZ: "Ad (A-Z)",
    nameZA: "Ad (Z-A)",
    gradeAsc: "Sınıf (Artan)",
    gradeDesc: "Sınıf (Azalan)",
    noConferencesFound: "Konferans bulunamadı",
    generalEvents: "Genel Etkinlikler",
    generalEvent: "Genel Etkinlik",
    general: "Genel",
    signup: "Kayıt",
    appointmentsLabel: "Randevular",
    virtual: "Sanal",
    groupedTeachersTooltip: "Gruplandırılmış öğretmenler - birini seçmek hepsini seçer",
    teachersAvailable: "öğretmen müsait",
    clickHere: "Buraya tıklayın",
    inCategory: "içinde",
    noTeachersInCategory: "Bu kategoride öğretmen bulunamadı",
    
    // Booking page
    registrationOpensSoon: "Kayıt yakında açılıyor",
    opensIn: "Açılış:",
    unitDays: "Gün",
    unitHours: "Saat",
    unitMinutes: "Dakika",
    unitSeconds: "Saniye",
    pageUnlocksAutomatically: "Kayıt başladığında bu sayfa otomatik olarak açılacaktır.",
    eventInformation: "Etkinlik Bilgisi",
    dateTBA: "Tarih açıklanacak",
    virtualMeeting: "Sanal Toplantı",
    selectTimeSlot: "Zaman Dilimi Seç",
    choosePreferredSlot: "Bu etkinlik için tercih ettiğiniz zaman dilimini seçin.",
    noSlotsAvailable: "Müsait zaman yok.",
    selectedTime: "Seçildi:",
    waitlistOnly: "(Sadece bekleme listesi)",
    completeFormBelow: "Aşağıdaki formu doldurun.",
    slotJustTaken: "Üzgünüz! Bu zaman az önce alındı",
    slotTakenMessage: "Başka bir veli bu zaman dilimini sizden hemen önce rezerve etti. Lütfen müsaitliği görmek için sayfayı yenileyin ve farklı bir zaman seçin.",
    refreshAvailability: "Müsaitliği Yenile",
    registerForEvent: "Bu Etkinliğe Kayıt Ol",
    selectSlotFirst: "Lütfen önce yukarıdan bir zaman dilimi seçin.",
    proceedToRegistration: "Kaydınızı tamamlamak için devam et'e tıklayın.",
    changeTime: "Saati Değiştir",
    yourAppointment: "Randevunuz",
    completeFormToConfirm: "Rezervasyonunuzu onaylamak için formu doldurun.",
    fullName: "Tam Ad",
    parentNamePlaceholder: "Veli Adı",
    emailAddress: "E-posta Adresi",
    emailPlaceholder: "e-posta@ornek.com",
    emailOptionalNote: "İsteğe bağlı - e-posta hatırlatıcıları almak için girin",
    studentName: "Öğrenci Adı",
    studentNamePlaceholder: "Öğrenci Adı",
    phoneNumber: "Telefon Numarası",
    phonePlaceholder: "05XX XXX XX XX",
    phoneOptionalNote: "İsteğe bağlı - SMS hatırlatıcıları almak için girin",
    commentsOptional: "Yorumlar veya Özel İstekler (İsteğe Bağlı)",
    commentsPlaceholder: "Öğretmenle paylaşmak istediğiniz özel ihtiyaçlar, sorular veya bilgiler...",
    needInterpreter: "Tercümana ihtiyacım var",
    interpreterLanguagePlaceholder: "Dil (örn., İngilizce, İşaret Dili)",
    reminderNotifications: "Hatırlatıcı Bildirimleri (İsteğe Bağlı)",
    reminderNotificationsDesc: "Hatırlatıcıları nasıl almak istediğinizi seçin. Seçtiğiniz yöntemler için iletişim bilgilerini sağlamalısınız.",
    emailReminders: "E-posta hatırlatıcıları",
    emailRemindersDesc: "Randevunuz hakkında e-posta hatırlatıcıları alın. Yukarıda e-posta adresi gerektirir.",
    smsReminders: "SMS hatırlatıcıları",
    smsRemindersDesc: "SMS hatırlatıcıları alın. Yukarıda telefon numarası gerektirir. Standart ücretler uygulanabilir.",
    registering: "Kaydediliyor...",
    confirmRegistration: "Kaydı Onayla",
    
    // Plan page additions
    scheduleYourConferences: "Konferanslarınızı Planlayın",
    youHaveMeetingsToSchedule: "Planlamanız gereken {count} {meeting} var.",
    meeting: "toplantı",
    meetings: "toplantı",
    continue: "Devam",
    continueButton: "devam →",
    yourTeachers: "Öğretmenleriniz",
    selectTimesManually: "Zamanları Manuel Seç",
    suggestedSchedule: "Önerilen Program",
    starts: "Başlangıç:",
    totalTime: "Toplam süre:",
    tapContinue: "\"Devam\"a dokunun",
    howToUsePlanner: "Program Planlayıcısı Nasıl Kullanılır",
    reviewAndConfirm: "İncele ve Onayla",
    nextBest: "Sonraki En İyi",
    
    poweredBy: "Tarafından desteklenmektedir",
    on: "tarihinde",
    
    // Multi-Child Support
    parentInformation: "Veli Bilgileri",
    enterYourInfoAndChildren: "Konferans planlamak için iletişim bilgilerinizi girin ve çocuklarınızı ekleyin.",
    children: "Çocuklar",
    addChild: "Çocuk Ekle",
    addUpToTwoChildren: "Aynı anda en fazla 2 çocuk için konferans planlayabilirsiniz.",
    addFirstChild: "İlk Çocuğunuzu Ekleyin",
    childName: "Çocuk Adı",
    continueToSelectTeachers: "Öğretmen Seçmeye Devam Et",
    forChild: "İçin:",
    selectChild: "Çocuk seçin...",
    for: "İçin",
    pleaseEnterName: "Lütfen adınızı girin",
    pleaseEnterValidEmail: "Lütfen geçerli bir e-posta adresi girin",
    pleaseEnterValidPhone: "Lütfen geçerli bir telefon numarası girin",
    pleaseAddAtLeastOneChild: "Lütfen en az bir çocuk ekleyin",
    pleaseEnterChildNames: "Lütfen tüm çocukların adlarını girin",
    pleaseAssignChildToAllTeachers: "Lütfen seçilen tüm öğretmenlere bir çocuk atayın",
    enterYourName: "Tam adınızı girin",
    enterYourEmail: "E-posta adresinizi girin",
    childOne: "Çocuk 1",
    childTwo: "Çocuk 2",
    conferenceNotAvailable: "Konferans Mevcut Değil",
    startFresh: "Yeniden Başla",
    yourPrivacyMatters: "Gizliliğiniz Önemlidir",
    privacyInfoUsage: "Bilgilerinizi yalnızca konferans randevularınızı planlamak için kullanıyoruz.",
    privacyNoSelling: "Verilerinizi asla satmıyoruz.",
    viewPrivacyPolicy: "Gizlilik Politikasını Görüntüle",
    autoSuggestWarning: "Otomatik öneri mevcut zaman seçimlerinizi değiştirecektir. Devam etmek istiyor musunuz? Bu, mevcut seçimlerinizi temizleyecektir.",
    interpreterNeeded: "Tercümana ihtiyacım var",
    selectInterpreterLanguage: "Dil seçin",
    allSlotsFull: "Tüm randevular dolu",
    joinedWaitlistSuccess: "Bekleme listesine eklendiniz! Bir yer açılırsa sizi bilgilendireceğiz.",
    stepYourInfo: "Sizin<br />Bilgileriniz",
    stepSelectTeachers: "Öğretmen<br />Seçin",
    stepChooseTimes: "Zaman<br />Seçin",
    stepConfirmMeetings: "Toplantıları<br />Onaylayın",
    draftModeMessage: "Bu konferans şu anda taslak modunda ve henüz herkese açık kayıt için mevcut değil. Lütfen daha sonra tekrar kontrol edin veya daha fazla bilgi için okulla iletişime geçin.",
    draftModeAdminNote: "Konferans, okul yöneticisi tarafından yayınlandığında görünür olacaktır.",
    tryAutoSuggest: "Otomatik Öneriyi Deneyin!",
    autoSuggestDescription: "En iyi ardışık programı anında bulur.",
    dismiss: "Kapat",
    manageSchedule: "Programı Yönet",
    findYourSchedule: "Programınızı Bulun",
    enterEmailToFindSchedule: "Randevularınızı ayarlarken kullandığınız e-posta adresini girin.",
    emailAddressLabel: "E-posta Adresi",
    sendVerificationCode: "Doğrulama Kodu Gönder",
    checkYourEmail: "E-postanızı Kontrol Edin",
    weSentCodeTo: "6 haneli bir kod gönderdik",
    verificationCode: "Doğrulama Kodu",
    verifyAndViewSchedule: "Doğrula ve Programı Gör",
    useDifferentEmail: "Farklı e-posta kullan",
    viewingScheduleFor: "Program görüntüleniyor",
    noUpcomingAppointments: "Yaklaşan randevu bulunamadı.",
    bookAnAppointment: "Randevu Al",
    studentLabel: "Öğrenci:",
    rescheduleAppointment: "Randevuyu Yeniden Planla",
    cancelAppointment: "Randevuyu İptal Et",
    downloadCalendar: "Takvimi İndir (.ics)",
    bookMoreAppointments: "+ Daha Fazla Randevu Al",
    reschedule: "Yeniden Planla",
    currentAppointment: "Mevcut Randevu",
    selectNewTime: "Yeni Saat Seçin",
    loadingAvailableTimes: "Uygun saatler yükleniyor...",
    noOtherTimesAvailable: "Başka uygun saat yok.",
    allSlotsFullOrPast: "Tüm saatler dolu veya geçmiş.",
    confirmReschedule: "Yeniden Planlamayı Onayla",
    cancelConfirmation: "Bu randevuyu iptal etmek istediğinizden emin misiniz?",
  },

  he: {
    bookingConfirmedTitle: "ההזמנה אושרה!",
    successfullyScheduled: "קבעת בהצלחה פגישות עם",
    teachers: "מורים",
    confirmationEmailSent: "נשלח אימייל אישור אל",
    addToCalendar: "הוספה ליומן",
    done: "סיום",
    selectedTeachers: "המורים שנבחרו",
    dragToReorder: "גררו לשינוי הסדר",
    noTeachersSelected: "לא נבחרו מורים.",
    goBackToAdd: "חזרו כדי להוסיף",
    youHaveTeachersToMeet: "יש לכם {count} מורים לפגוש.",
    desiredStartTime: "שעת התחלה מבוקשת",
    autoSuggestSchedule: "הצעת לוח זמנים אוטומטית",
    bestForBackToBack: "מתאים לפגישות רצופות",
    or: "או",
    selectManually: "בחירה ידנית",
    stepOf: "שלב {current} מתוך {total}",
    percentComplete: "{percent}% הושלמו",
    availableTimes: "שעות פנויות",
    noAvailableTimes: "אין שעות פנויות אצל מורה זה.",
    time: "שעה",
    howToUse: "איך משתמשים",
    tipClickSlots: "לחצו על השעות כדי לבחור פגישה עם כל מורה. השעות שנבחרו יסומנו.",
    tipAutoSuggest: "ההצעה האוטומטית (בפינה הימנית העליונה) מוצאת את לוח הזמנים הרצוף הטוב ביותר.",
    tipDragTeachers: "גררו את המורים בסרגל הצד כדי לשנות את סדרם. הדבר משפיע על הסדר המוצע.",
    tipGrayedSlots: "שעות באפור חסומות מפני שבחרתם את אותה שעה אצל מורה אחר.",
    tipEnterStudentNames: "הקלידו את שם התלמיד מעל עמודת כל מורה כדי לציין לאיזה ילד הפגישה מיועדת.",
    foundOptimalSlots: "נמצאו {count} שעות אופטימליות!",
    suggestionsOnly: "אלו הצעות בלבד. בדקו את לוח הזמנים למטה ולחצו על \"אישור\" כשתהיו מוכנים להזמין.",
    couldNotFindSchedule: "לא נמצא לוח זמנים מתאים. נסו לשנות את שעת ההתחלה או לבחור פחות מורים.",
    pleaseFillIn: "נא למלא את הפרטים הבאים:",
    validEmailRequired: "נא להזין כתובת אימייל תקינה.",
    // Common - Hebrew
    bookSlot: "הזמן תור",
    cancel: "ביטול",
    confirmBooking: "אשר הזמנה",
    yourName: "השם שלך",
    email: "אימייל",
    phone: "טלפון",
    signUp: "הרשמה",
    full: "מלא",
    teacher: "מורה",
    location: "מיקום",
    backToSchool: "חזרה לשיחות",
    required: "(חובה)",
    optional: "(אופציונלי)",
    notifications: "התראות",
    
    // School Page
    selectTeachers: "בחר מורים",
    searchTeachers: "חפש מורים...",
    sortBy: "מיין לפי",
    lastName: "שם משפחה",
    grade: "כיתה",
    ascending: "א-ת",
    descending: "ת-א",
    teacherSelected: "מורה נבחר",
    teachersSelected: "מורים נבחרו",
    findTimes: "מצא זמנים",
    clear: "נקה",
    select: "בחר",
    selected: "נבחר",
    add: "הוסף",
    added: "נוסף",
    availableSlots: "תורים פנויים",
    teamOf: "צוות של",
    selectAllTeam: "לבחור את כל המורים בצוות הזה?",
    yes: "כן",
    no: "לא",
    allTeachers: "כל המורים",
    showAllTeachers: "הצג את כל המורים",
    chooseTimes: "בחר זמנים",
    pickAvailableSlots: "בחר תורים פנויים",
    confirm: "אשר",
    confirmMeetings: "אשר פגישות",
    getConfirmation: "קבל אישור",
    
    // Booking Limit
    bookingLimitTitle: "מגבלת הזמנות",
    maxSlotsAllowed: "מקסימום",
    slotsPerParent: "שיחות להורה לאירוע זה.",
    enterEmailToSeeCount: "הזן את האימייל שלך בעת ההרשמה כדי לראות את ההזמנות הנוכחיות שלך.",
    youHaveBooked: "הזמנת",
    of: "מתוך",
    allowedSlots: "תורי שיחה מותרים",
    toBookMore: "כדי להזמין עוד, בטל תחילה פגישה קיימת.",
    youCanBook: "תוכל להזמין עוד",
    moreSlots: "תור/ים",
    viewMySchedule: "צפה ונהל את לוח הזמנים שלי ←",
    
    // Plan Page
    planYourSchedule: "תכנן את לוח הזמנים שלך",
    selectTimeSlots: "בחר זמנים לכל מורה",
    autoSuggest: "הצעה אוטומטית",
    autoSuggestDesc: "תן לנו למצוא את הזמנים הטובים ביותר עבורך",
    confirmAppointments: "אשר פגישות",
    appointment: "פגישה",
    appointments: "פגישות",
    noTimeSelected: "לא נבחר זמן",
    childsName: "שם הילד",
    enterChildName: "הזן את שם ילדך",
    yourDetails: "הפרטים שלך",
    parentName: "שם ההורה",
    yourFullName: "שמך המלא",
    yourEmail: "האימייל@דוגמה.com",
    yourPhone: "מספר הטלפון שלך",
    review: "סקור",
    back: "חזור",
    nextTeacher: "המורה הבא",
    yourSchedule: "לוח הזמנים שלך",
    tapToChange: "הקש לשינוי",
    confirmNow: "אשר עכשיו",
    confirming: "מאשר...",
    
    // Booking Form
    bookingFor: "הזמנה עבור",
    selectSlot: "בחר זמן",
    joinWaitlist: "הצטרף לרשימת המתנה",
    booked: "הוזמן",
    waitlisted: "ברשימת המתנה",
    notifyMe: "הודע לי דרך:",
    remindMeVia: "הזכר לי דרך",
    emailNotify: "אימייל",
    smsNotify: "SMS",
    bothNotify: "שניהם",
    
    // Confirmation
    bookingConfirmed: "ההזמנה אושרה!",
    addedToWaitlist: "נוסף לרשימת המתנה",
    confirmationSent: "אישור נשלח לאימייל שלך.",
    waitlistInfo: "נודיע לך אם יתפנה מקום.",
    viewSchedule: "צפה בלוח הזמנים שלי",
    bookAnother: "הזמן עוד",
    
    // Schedule Lookup
    mySchedule: "לוח הזמנים שלי",
    enterEmail: "הזן את האימייל שלך כדי לצפות בפגישות",
    lookupSchedule: "חפש לוח זמנים",
    noAppointments: "לא נמצאו פגישות",
    tryDifferentEmail: "נסה כתובת אימייל אחרת",
    
    // Dates & Times
    today: "היום",
    tomorrow: "מחר",
    am: "בוקר",
    pm: "אחה\"צ",
    
    // Errors
    errorOccurred: "אירעה שגיאה",
    pleaseRetry: "נא לנסות שוב",
    requiredField: "שדה זה הוא חובה",
    invalidEmail: "נא להזין אימייל תקין",
    
    // Registration locked
    registrationOpens: "ההרשמה נפתחת",
    registrationClosed: "ההרשמה נסגרה",
    comingSoon: "בקרוב",
    
    // Additional UI elements
    teacherPortal: "פורטל מורים",
    schoolConferences: "שיחות הורים",
    needToMakeChanges: "צריך לבצע שינויים בלוח הזמנים שלך?",
    nameAZ: "שם (א-ת)",
    nameZA: "שם (ת-א)",
    gradeAsc: "כיתה (עולה)",
    gradeDesc: "כיתה (יורד)",
    noConferencesFound: "לא נמצאו שיחות",
    generalEvents: "אירועים כלליים",
    generalEvent: "אירוע כללי",
    general: "כללי",
    signup: "הרשמה",
    appointmentsLabel: "פגישות",
    virtual: "וירטואלי",
    groupedTeachersTooltip: "מורים מקובצים - בחירת אחד בוחרת את כולם",
    teachersAvailable: "מורים זמינים",
    clickHere: "לחץ כאן",
    inCategory: "ב",
    noTeachersInCategory: "לא נמצאו מורים בקטגוריה",
    
    // Booking page
    registrationOpensSoon: "ההרשמה נפתחת בקרוב",
    opensIn: "נפתח בעוד:",
    unitDays: "ימים",
    unitHours: "שעות",
    unitMinutes: "דקות",
    unitSeconds: "שניות",
    pageUnlocksAutomatically: "דף זה ייפתח אוטומטית כאשר ההרשמה תתחיל.",
    eventInformation: "מידע על האירוע",
    dateTBA: "תאריך יימסר",
    virtualMeeting: "פגישה וירטואלית",
    selectTimeSlot: "בחר זמן",
    choosePreferredSlot: "בחר את הזמן המועדף עליך לאירוע זה.",
    noSlotsAvailable: "אין זמנים פנויים.",
    selectedTime: "נבחר:",
    waitlistOnly: "(רשימת המתנה בלבד)",
    completeFormBelow: "מלא את הטופס למטה.",
    slotJustTaken: "מצטערים! הזמן הזה נתפס עכשיו",
    slotTakenMessage: "הורה אחר הזמין את הזמן הזה ממש לפניך. רענן כדי לראות זמינות ובחר זמן אחר.",
    refreshAvailability: "רענן זמינות",
    registerForEvent: "הירשם לאירוע זה",
    selectSlotFirst: "אנא בחר תחילה זמן למעלה.",
    proceedToRegistration: "לחץ על המשך כדי להשלים את ההרשמה.",
    changeTime: "שנה זמן",
    yourAppointment: "התור שלך",
    completeFormToConfirm: "מלא את הטופס כדי לאשר את ההזמנה שלך.",
    fullName: "שם מלא",
    parentNamePlaceholder: "שם ההורה",
    emailAddress: "כתובת אימייל",
    emailPlaceholder: "אימייל@דוגמה.com",
    emailOptionalNote: "אופציונלי - הזן כדי לקבל תזכורות באימייל",
    studentName: "שם התלמיד",
    studentNamePlaceholder: "שם התלמיד",
    phoneNumber: "מספר טלפון",
    phonePlaceholder: "05X-XXX-XXXX",
    phoneOptionalNote: "אופציונלי - הזן כדי לקבל תזכורות ב-SMS",
    commentsOptional: "הערות או בקשות מיוחדות (אופציונלי)",
    commentsPlaceholder: "צרכים מיוחדים, שאלות או מידע שתרצה לשתף עם המורה...",
    needInterpreter: "אני צריך מתרגם",
    interpreterLanguagePlaceholder: "שפה (למשל, אנגלית, שפת סימנים)",
    reminderNotifications: "התראות תזכורת (אופציונלי)",
    reminderNotificationsDesc: "בחר כיצד תרצה לקבל תזכורות. עליך לספק פרטי קשר לשיטות שבחרת.",
    emailReminders: "תזכורות באימייל",
    emailRemindersDesc: "קבל תזכורות באימייל על הפגישה שלך. דורש כתובת אימייל למעלה.",
    smsReminders: "תזכורות ב-SMS",
    smsRemindersDesc: "קבל תזכורות בהודעות טקסט. דורש מספר טלפון למעלה. עלולים לחול תעריפים רגילים.",
    registering: "נרשם...",
    confirmRegistration: "אשר הרשמה",
    
    // Plan page additions
    scheduleYourConferences: "תזמן את השיחות שלך",
    youHaveMeetingsToSchedule: "יש לך {count} {meeting} לתזמן.",
    meeting: "פגישה",
    meetings: "פגישות",
    continue: "המשך",
    continueButton: "המשך ←",
    yourTeachers: "המורים שלך",
    selectTimesManually: "בחר זמנים ידנית",
    suggestedSchedule: "לוח זמנים מוצע",
    starts: "מתחיל:",
    totalTime: "זמן כולל:",
    tapContinue: "הקש על \"המשך\"",
    howToUsePlanner: "איך להשתמש במתכנן לוח הזמנים",
    reviewAndConfirm: "סקור ואשר",
    nextBest: "הטוב הבא",
    
    poweredBy: "מופעל על ידי",
    on: "ב",
    
    // Multi-Child Support
    parentInformation: "פרטי ההורה",
    enterYourInfoAndChildren: "הזן את פרטי הקשר שלך והוסף את ילדיך כדי לתזמן שיחות.",
    children: "ילדים",
    addChild: "הוסף ילד",
    addUpToTwoChildren: "תוכל לתזמן שיחות לעד 2 ילדים בו-זמנית.",
    addFirstChild: "הוסף את הילד הראשון שלך",
    childName: "שם הילד",
    continueToSelectTeachers: "המשך לבחירת מורים",
    forChild: "עבור:",
    selectChild: "בחר ילד...",
    for: "עבור",
    pleaseEnterName: "נא להזין את שמך",
    pleaseEnterValidEmail: "נא להזין כתובת אימייל תקינה",
    pleaseEnterValidPhone: "נא להזין מספר טלפון תקין",
    pleaseAddAtLeastOneChild: "נא להוסיף לפחות ילד אחד",
    pleaseEnterChildNames: "נא להזין שמות לכל הילדים",
    pleaseAssignChildToAllTeachers: "נא להקצות ילד לכל המורים שנבחרו",
    enterYourName: "הזן את שמך המלא",
    enterYourEmail: "הזן את כתובת האימייל שלך",
    childOne: "ילד 1",
    childTwo: "ילד 2",
    conferenceNotAvailable: "הכנס אינו זמין",
    startFresh: "התחל מחדש",
    yourPrivacyMatters: "הפרטיות שלך חשובה",
    privacyInfoUsage: "אנו משתמשים במידע שלך אך ורק לתזמון פגישות הכנס שלך.",
    privacyNoSelling: "אנחנו לעולם לא מוכרים את הנתונים שלך.",
    viewPrivacyPolicy: "צפה במדיניות הפרטיות",
    autoSuggestWarning: "ההצעה האוטומטית תחליף את בחירות הזמן הנוכחיות שלך. האם ברצונך להמשיך? פעולה זו תמחק את הבחירות הנוכחיות שלך.",
    interpreterNeeded: "אני צריך/ה מתורגמן",
    selectInterpreterLanguage: "בחר שפה",
    allSlotsFull: "כל המשבצות מלאות",
    joinedWaitlistSuccess: "נוספת לרשימת ההמתנה! נודיע לך אם יתפנה מקום.",
    stepYourInfo: "המידע<br />שלך",
    stepSelectTeachers: "בחירת<br />מורים",
    stepChooseTimes: "בחירת<br />זמנים",
    stepConfirmMeetings: "אישור<br />פגישות",
    draftModeMessage: "כנס זה נמצא כעת במצב טיוטה ואינו זמין עדיין לרישום ציבורי. אנא בדקו מאוחר יותר או צרו קשר עם בית הספר למידע נוסף.",
    draftModeAdminNote: "הכנס יהיה גלוי לאחר פרסומו על ידי מנהל בית הספר.",
    tryAutoSuggest: "נסו הצעה אוטומטית!",
    autoSuggestDescription: "מוצא את לוח הזמנים הרצוף הטוב ביותר באופן מיידי.",
    dismiss: "סגור",
    manageSchedule: "ניהול לוח זמנים",
    findYourSchedule: "מצא את לוח הזמנים שלך",
    enterEmailToFindSchedule: "הזן את כתובת האימייל שבה השתמשת לקביעת הפגישות.",
    emailAddressLabel: "כתובת אימייל",
    sendVerificationCode: "שלח קוד אימות",
    checkYourEmail: "בדוק את האימייל שלך",
    weSentCodeTo: "שלחנו קוד בן 6 ספרות אל",
    verificationCode: "קוד אימות",
    verifyAndViewSchedule: "אמת וצפה בלוח הזמנים",
    useDifferentEmail: "השתמש באימייל אחר",
    viewingScheduleFor: "צפייה בלוח זמנים של",
    noUpcomingAppointments: "לא נמצאו פגישות קרובות.",
    bookAnAppointment: "קבע פגישה",
    studentLabel: "תלמיד:",
    rescheduleAppointment: "שנה מועד פגישה",
    cancelAppointment: "בטל פגישה",
    downloadCalendar: "הורד יומן (.ics)",
    bookMoreAppointments: "+ קבע עוד פגישות",
    reschedule: "שנה מועד",
    currentAppointment: "פגישה נוכחית",
    selectNewTime: "בחר זמן חדש",
    loadingAvailableTimes: "טוען זמנים זמינים...",
    noOtherTimesAvailable: "אין זמנים אחרים זמינים.",
    allSlotsFullOrPast: "כל המשבצות מלאות או עברו.",
    confirmReschedule: "אשר שינוי מועד",
    cancelConfirmation: "האם אתה בטוח שברצונך לבטל פגישה זו?",
  },
};

const LanguageContext = createContext<{
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => translations['en'][key] || key
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // Always initialize with 'en' to ensure server and client match
  // We'll update from localStorage after hydration
  const [language, setLanguage] = useState<Language>('en');

  // RTL languages list
  const rtlLanguages: Language[] = ['ar', 'fa', 'he'];
  
  // Load language preference after hydration to avoid mismatch
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('preferredLanguage') as Language;
      if (saved && translations[saved]) {
        setLanguage(saved);
        if (typeof document !== 'undefined') {
          document.documentElement.dir = rtlLanguages.includes(saved) ? 'rtl' : 'ltr';
          document.documentElement.lang = saved;
        }
      } else if (typeof document !== 'undefined') {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = 'en';
      }
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferredLanguage', lang);
      if (typeof document !== 'undefined') {
        document.documentElement.dir = rtlLanguages.includes(lang) ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
      }
    }
  };

  const t = (key: string): string => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useTranslation = () => useContext(LanguageContext);
